import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { askAboutCopy, fittedAskAboutCopy, placeholderFits, pageTopic } from "./page-topic.ts";
import { debugLog } from "./debug.ts";
import { adoptsPendingRow, placeholderIdFor } from "./pending-answer.ts";
import { createRoot } from "react-dom/client";
import Markdown from "markdown-to-jsx";
import styles from "@widget-ui/styles/chat.css";
import type { ProductCard } from "@bluehat/shared/events/schema";
import { stockLabel, priceLabel, discountNote } from "./product-card-format";
import { partitionDualLevels } from "./clinical-glass.ts";
import { nudgeFloatCount, nudgeMobileCount, phoneChipBudget } from "./nudge-pool.ts";
import { fetchRankedPageQuestions } from "./rank-page-questions.ts";
import {
  isPathJourneySerious,
  pathStackForRank,
  recordPathLand,
  PATH_JOURNEY_LLM_THRESHOLD,
} from "./path-journey.ts";
import type { ChatMsg, LeadForm, BannerConfig, HeaderBookingCta } from "./messages.ts";
import type { PlacementRuntime } from "@bluehat/shared/widget-design/runtime";
import type { VoiceMode } from "@bluehat/shared/voice/mode";
import {
  DEFAULT_LIVING_BAR_SKIN,
  glassCapsuleCtaLabel,
  glassCapsulePalette,
  isAiStyledSkin,
  isGlassCapsuleSkin,
  isSideLivingBarSkin,
  nudgesSurfaceLook,
  type LivingBarSkin,
} from "@bluehat/shared/widget-design/living-bar";
import { NUDGES_INLINE_MAX, inlineSurfaceSkin, type InlineMode } from "@bluehat/shared/widget-design/inline";
import { inlineRenderPhase, inlineExpandedHostHeight, agentChipDwellMs } from "./inline-mode";
import { createTypewriter, type TypewriterHandle, type TypewriterLine } from "./typewriter.ts";
import { buildBarQueue } from "./bar-queue.ts";
import type { BarLineKind } from "@bluehat/shared/events/schema";
import {
  pageQuestionsForPills,
  pillLabel,
  questionHash,
  normalizeQuestion,
  shouldSuppressPagePills,
  getAskedPageQuestions,
  markPageQuestionAsked,
  filterUnaskedChipOptions,
  filterUnaskedPageQuestions,
  pickDiversePageQuestions,
  buildSharedNextAsks,
  MAX_PAGE_PILLS,
  type PanelOpenSource,
} from "./page-pills.ts";
import { productIdFromStampedUrl } from "@bluehat/shared/events/cta-attribution";
import {
  buildEngagementBarLead,
  focusMatchesInventory,
  focusMatchesPageContext,
  isSpeakableContinuityTopic,
  rankQuestionsByFocus,
  readConversationFocus,
  topicMatchesInventory,
  writeConversationFocus,
} from "./conversation-focus.ts";
import {
  buildSectionBarView,
  type SectionBarView,
} from "./section-voice.ts";
import { setAiVoiceSessionUi } from "./placements.ts";
import {
  isJunkSectionHeading,
  resolveTextLandPresentation,
  titleishHeading,
} from "./text-continuity.ts";
import {
  createSttVoiceTalk,
  isSttSupported,
  sttNeedsWarm,
  sttReady,
  warmStt,
} from "./stt/adapter";
import {
  isLikelyVoiceGarbageTranscript,
  isBookDemoCommand,
  isChipStyleVoiceAnswer,
  isNavNoCommand,
  isNavYesCommand,
  isReadThemCommand,
  isStopTalkingCommand,
  isAgentSpeechActive,
  isSessionSpeechActive,
  isWhisperMuted,
  NAV_HOST_LISTEN_MS,
  pickNavHostPill,
  POST_ANSWER_LISTEN_MS,
  POST_ANSWER_SILENCE_MS,
  PUSH_TO_TALK_LISTEN_MS,
  setOnAgentSpeechIdle,
  setWhisperMuted,
  abortOpenAiVoiceNetwork,
  prefetchSpeakText,
  getTtsApiOrigin,
  shouldIncludeEngagedNavMeta,
  speakAgentText,
  speakAgentVoiceUrl,
  speakNavVoiceUrl,
  stopAgentSpeech,
  textForSpeech,
  unlockAudioPlayback,
  warmOpenAiTts,
  warmSpeechVoices,
  type AgentSpeechRole,
  type VoiceTalkHandle,
  type VoiceTalkState,
} from "./voice-talk.ts";

/** Whether the panel carries the "Powered by BlueHatAI.ai" line at its bottom.
 *
 *  TEMPORARILY false (Brian, 2026-09-29) — set it back to true to restore the
 *  attribution. Nothing else needs touching: the markup and the `.chat-powered`
 *  CSS are both still in place. Deliberately NOT a config field: it is a
 *  hard-coded temporary state, and making it configurable would turn "hide this
 *  for now" into a per-tenant product decision nobody has made. */
const SHOW_POWERED_BY = false;

/** How long the chip's exit animation runs — must match the CSS duration, or
 *  the swap lands mid-flight and the words change while the old chip is still
 *  sliding. */
const AGENT_CHIP_EXIT_MS = 220;

/** CSS modifier suffix for section wrap / dock chrome. */
function livingBarSkinMod(skin: LivingBarSkin): string {
  switch (skin) {
    case "glass":
      return "glass";
    case "constellation":
      return "constellation";
    case "radar":
      return "radar";
    case "command_line":
      return "cli";
    case "split_theater":
      return "theater";
    case "on_this_page":
      return "otp";
    case "side_rail":
      return "rail";
    case "side_theater":
      return "side-theater";
    case "clinical_glass":
    case "graphite":
    case "ember":
    case "deep_field":
      // One geometry for the whole family — the palette rides a second
      // class so the shape CSS is written once.
      return "cglass";
    default:
      return "notes";
  }
}

function livingBarLead(
  skin: LivingBarSkin,
  opts: {
    hasNext: boolean;
    currentHeading?: string;
    nextHeading?: string | null;
    /** Current + next section questions for continuity match. */
    inventory?: readonly string[];
    /**
     * LLM / grounded continuity lead (same as hero + in-content).
     * When set, dock shows this instead of weak "You asked about yes" templates.
     */
    continuityLead?: string | null;
  },
): string {
  const { currentHeading, nextHeading, inventory, continuityLead } = opts;
  const llm = (continuityLead ?? "").replace(/\s+/g, " ").trim();
  // Prefer the same LLM line as hero/in-content (phase 2 nudge).
  if (
    llm.length >= 12 &&
    !/want to continue with:|you asked about (yes|no|that|ok)\b/i.test(llm)
  ) {
    const clipped =
      llm.length > 140
        ? `${llm.slice(0, 137).replace(/\s+\S*$/, "")}…`
        : llm;
    if (skin === "command_line") return `// ${clipped}`;
    return clipped;
  }
  // Lexical fallback only (never "You asked about yes").
  const core = buildEngagementBarLead({
    currentHeading,
    nextHeading,
    focus: readConversationFocus(),
    inventory: inventory ?? [],
  });
  if (skin === "command_line") {
    return `// ${core}`;
  }
  return core;
}

/** Display section label for rail/tags — blank if layout junk. */
function displaySectionHeading(raw: string | null | undefined): string {
  const t = titleishHeading(raw ?? "");
  if (!t || isJunkSectionHeading(t)) return "";
  return t;
}

/**
 * UI-only continuity / section lead for living-bar surfaces.
 * Does not change inventory, ranking, or send paths — label text only.
 */
function surfaceLeadLabel(input: {
  continuityLead?: string | null;
  currentHeading?: string | null;
  nextHeading?: string | null;
  currentQuestions?: readonly string[];
  nextQuestions?: readonly string[];
  pagePool?: readonly string[];
  monologue?: readonly string[];
  inviteLines?: readonly string[];
  sectionHeadings?: readonly string[];
}): string {
  const llm = (input.continuityLead ?? "").replace(/\s+/g, " ").trim();
  if (llm) return llm;
  try {
    const p = resolveTextLandPresentation({
      focus: readConversationFocus(),
      path: typeof window !== "undefined" ? window.location.pathname : "",
      pageTopic: pageTopic(),
      monologue: input.monologue,
      inviteLines: input.inviteLines,
      sectionHeadings: input.sectionHeadings,
      currentHeading: input.currentHeading,
      nextHeading: input.nextHeading,
      pagePool: input.pagePool ?? [],
      currentQuestions: input.currentQuestions ?? [],
      nextQuestions: input.nextQuestions ?? [],
    });
    if (p.railLead) return p.railLead;
  } catch {
    /* presentation only */
  }
  return displaySectionHeading(input.currentHeading) || "On this page";
}

/** Phone breakpoint for living-bar surface chrome (not open-panel layout). */
const MOBILE_SURFACE_MQ = "(max-width: 640px)";
/**
 * On mobile: 1 this-section + 2 coming-up, compact chrome (more asks, less cover).
 */
const MOBILE_SURFACE_CURRENT = 1;
const MOBILE_SURFACE_NEXT = 2;

/** UI-only: track phone width so skins can cap visible surface pills. */
function useMobileSurface(): boolean {
  const [mobile, setMobile] = useState(() =>
    typeof window !== "undefined" && window.matchMedia(MOBILE_SURFACE_MQ).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_SURFACE_MQ);
    const onChange = () => setMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return mobile;
}

/**
 * UI-only slice of section surface inventory for painting skins.
 * Desktop keeps full dual chrome; mobile paints 1 current + 2 coming-up
 * with compact bar/pills so page content stays visible.
 * Does not change ranking, send path, or sectionBarView inventory.
 */
function surfacePaintQuestions(
  view: SectionBarView,
  mobile: boolean,
): { current: string[]; next: string[] } {
  // The customer float count caps the TOTAL chips painted across both
  // groups on every skin — it was only wired into the glass-capsule
  // partition, so the other skins painted the full 2+2 dual chrome
  // regardless of the setting (Brian bench, 2026-08-14: 4 shown where the
  // count said 2). Current fills first; next gets the remainder.
  // Phones: the tenant's phone count when set (2026-10-14) replaces the
  // 1 + 2 built-in layout cap; unset keeps it exactly.
  const phoneCount = mobile ? nudgeMobileCount() : null;
  const total = phoneCount ?? nudgeFloatCount();
  const currentCap = mobile ? Math.min(MOBILE_SURFACE_CURRENT, total) : total;
  const current = view.currentQuestions.slice(0, currentCap);
  const remaining = Math.max(0, total - current.length);
  const nextCap = mobile && phoneCount == null ? Math.min(MOBILE_SURFACE_NEXT, remaining) : remaining;
  return { current, next: view.nextQuestions.slice(0, nextCap) };
}

/** Pill-shaped continuity kicker above section surface chrome (UI only). */
function SurfaceLeadKicker({
  lead,
  skin,
  className,
}: {
  lead: string;
  /** Tints kicker to match the living-bar skin palette (uniform with cards/bar). */
  skin?: LivingBarSkin;
  className?: string;
}) {
  const t = lead.replace(/\s+/g, " ").trim();
  if (!t) return null;
  const continuity = t.toLowerCase().startsWith("related to");
  const mod = skin ? livingBarSkinMod(skin) : "";
  const onDark = skin === "constellation" || skin === "command_line";
  const classes = [
    "bhai-surface-kicker",
    mod ? `bhai-surface-kicker--${mod}` : "",
    onDark ? "bhai-surface-kicker--on-dark" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div
      className={
        continuity ? `${classes} bhai-surface-kicker--continuity` : classes
      }
    >
      {t}
    </div>
  );
}

/** CTA label on the dock for a skin (pill inventory still uses openWithQuestions). */
function livingBarCtaLabel(skin: LivingBarSkin, fallback: string): string {
  if (skin === "on_this_page" || isSideLivingBarSkin(skin)) {
    return "Answer me now";
  }
  // Per-member capsule copy: privacy reassurance belongs to Clinical Glass
  // alone; the brand palettes are ordinary commercial surfaces.
  if (isGlassCapsuleSkin(skin)) {
    return glassCapsuleCtaLabel(skin);
  }
  return fallback || "Get the answers now";
}

/**
 * Clinical Glass capsule pill row — the fit-measured half of the dual level.
 * Renders every candidate invisibly first, measures REAL widths, then keeps
 * the highest-ranked pills that fit the row WHOLE (greedy in rank order —
 * a too-long #1 is skipped, not clipped, and #2 gets its seat). Re-measures
 * when the row resizes or the candidate set changes. Pills send their full
 * verbatim question — display and payload are never decoupled here.
 */
/** Sticky-note stagger (equal left lean in CSS; delay only). */
function stickyNoteVars(index: number): CSSProperties {
  return {
    ["--note-delay" as string]: `${50 + index * 75}ms`,
    ["--note-z" as string]: String(10 + index),
  };
}

/** Glass thought-card stagger (equal cards; delay + depth only). */
function glassCardVars(index: number, kind: "now" | "next"): CSSProperties {
  const depth = kind === "now" ? 0 : 1;
  return {
    ["--glass-delay" as string]: `${40 + index * 70}ms`,
    ["--glass-z" as string]: String(20 - index + (kind === "now" ? 4 : 0)),
    ["--glass-depth" as string]: String(depth),
  };
}

/** Constellation node stagger + slight vertical orbit. */
function constellationVars(index: number, kind: "now" | "next"): CSSProperties {
  const lifts = [0, -10, -4, -14];
  return {
    ["--star-delay" as string]: `${30 + index * 65}ms`,
    ["--star-z" as string]: String(12 + index),
    ["--star-lift" as string]: `${lifts[index % lifts.length]! + (kind === "next" ? -2 : 0)}px`,
  };
}

/** Radar blip stagger around the sweep. */
function radarBlipVars(index: number): CSSProperties {
  return {
    ["--radar-delay" as string]: `${50 + index * 80}ms`,
    ["--radar-z" as string]: String(10 + index),
  };
}

/** CLI line boot delay — remount on section change restarts the sequence. */
function cliLineVars(index: number): CSSProperties {
  return {
    ["--cli-delay" as string]: `${120 + index * 140}ms`,
  };
}

const MARKDOWN_OPTS = {
  overrides: {
    a: {
      props: { target: "_blank", rel: "noopener noreferrer" },
    },
  },
  // Render inline-level in chat bubbles to avoid stray <p> margins,
  // but still allow block elements like lists and code blocks.
  forceBlock: true,
};

type VoiceHostKind =
  | "cold_open"
  | "revisit"
  | "engaged_nav"
  | "sb_answer"
  | "follow_up"
  | "bridge"
  | "invite"
  | "read_pills"
  | "other";

type SurfacePillKind = "now" | "next" | "other";

type BarEvent =
  // Retained though nothing emits it any more: widget bundles are versioned and
  // cached, so OLDER deployed bundles keep posting this until tenants pick up a
  // new one. The server must go on parsing it — the score processor's allowlist is
  // what makes it harmless meanwhile.
  | { event: "bar_line_shown"; kind: BarLineKind }
  | { event: "bar_line_clicked"; kind: BarLineKind; submitted: boolean }
  | { event: "bar_expanded" }
  | { event: "bar_dismissed" }
  | { event: "sticky_bar_shown" }
  | { event: "sticky_bar_hidden" }
  | { event: "panel_opened" }
  | {
      event: "surface_pill_clicked";
      skin: LivingBarSkin;
      kind: SurfacePillKind;
      question: string;
    }
  | { event: "voice_session_started" }
  | { event: "voice_session_ended"; reason?: string }
  | {
      event: "voice_intent";
      trigger:
        | "cold_open"
        | "engaged_nav"
        | "panel_invite"
        | "bridge"
        | "follow_up"
        | "read_them"
        | "cta_handoff";
      intentId: string;
      sectionIndex?: number;
      panelOpen?: boolean;
      transcript?: string;
      pill?: string | null;
      /** follow_up: the pills visible on screen — voice reads exactly these. */
      visibleAsks?: string[];
    }
  | {
      event: "voice_host_said";
      kind: VoiceHostKind;
      said: string;
      pill?: string | null;
      rankSource?: string;
      matchesFocus?: boolean;
      navIndex?: number;
      includeMeta?: boolean;
    }
  | {
      event: "voice_nav_listen";
      outcome: "yes" | "no" | "timeout" | "question" | "abort";
      pill?: string | null;
    };

type BubbleCallbacks = {
  onOpen: () => void;
  onClose: () => void;
  onSend: (text: string) => void;
  onSubmitLead: (values: Record<string, string>) => void;
  /** A presented CTA was clicked (#156): record the click-through, then nav.
   *  `source` distinguishes FM-presented buttons from panel-header chrome. */
  onClickCta: (
    url: string,
    slug: string,
    openInNewTab: boolean,
    source?: "presented" | "panel_header",
  ) => void;
  /** A recommended PRODUCT link was clicked inside the assistant's prose.
   *  Separate from onClickCta because a product id is not a CTA slug, and
   *  crediting one as a cta_click would feed an unresolvable value into
   *  "All Conversions". Optional so an older host wrapper still builds. */
  onClickProduct?: (productId: string, url: string) => void;
  /** The rich-lead offer chip was clicked — post the opt-in event (the accept
   *  text itself goes through onSend like a typed message). */
  onChipClick: (sendText: string) => void;
  /** Living-bar analytics events → /api/events via snippet. */
  onBarEvent?: (ev: BarEvent) => void;
  /** Answer-choice chips rendered — measure lift (labels + turnId). */
  onAnswerChipsShown?: (
    labels: string[],
    turnId: string,
    chipKind?: "answer" | "context_next_ask",
  ) => void;
  /** Answer-choice chip tapped — measure lift; sendText goes through onSend. */
  onAnswerChipClick?: (label: string, turnId: string) => void;
  /** Living-bar page-question pills shown in the open panel. */
  onPageQuestionPillsShown?: (questionHashes: string[], source: PanelOpenSource) => void;
  /** Living-bar page-question pill tapped. */
  onPageQuestionPillClicked?: (
    questionHash: string,
    question: string,
    source: PanelOpenSource,
  ) => void;
};

type PagePillsState = {
  turnId: string;
  source: PanelOpenSource;
  options: Array<{ label: string; sendText: string }>;
};

const BAR_DISMISS_KEY = "bhai_bar_dismissed";
/** Inline unit closed by the visitor (tenant opted in to the X) — hidden for
 *  the rest of the browser session. Separate from BAR_DISMISS_KEY: closing an
 *  in-page container must not also shrink the floating bar on other pages. */
const INLINE_DISMISS_KEY = "bhai_inline_dismissed";
const BAR_PULSE_KEY = "bhai_bar_pulsed";

export type MountOptions =
  | { mode: "floating" }
  | {
      mode: "inline";
      target: HTMLElement;
      /** Resolved inline mode when known at mount (div attribute, or config
       *  that landed pre-mount). null = fail-closed until config (#592). */
      inlineMode?: InlineMode | null;
      /** `data-bhai-chips="off"` on the div — hide the rotating question chip
       *  above an agent field. Opt-out: absent means show it. */
      chipsAllowed?: boolean;
      /** Skin that landed pre-mount. Without this seed, a post-config mount
       *  painted frame 1 with the STOCK look (mode known via options, skin
       *  only via a post-bind setter) and restyled on frame 2 — the
       *  unstyled-flash bench finding, 2026-08-17. */
      initialLivingBarSkin?: LivingBarSkin | null;
      /** True when the tenant config already landed pre-mount. A div
       *  attribute makes the MODE known before config, but the LOOK (skin)
       *  is only known once config lands — painting before then showed the
       *  plain capsule first (the 118ms plain paint, bench 2026-08-17). */
      initialConfigLanded?: boolean;
      /** Tenant's inline close-button setting when config landed pre-mount. */
      initialInlineClose?: boolean;
    };

export type BubbleHandle = {
  host: HTMLElement;
  shadow: ShadowRoot;
  setMessages: (msgs: ChatMsg[]) => void;
  addMessage: (msg: ChatMsg) => void;
  updateMessage: (msg: ChatMsg) => void;
  setThinking: (thinking: boolean) => void;
  /** Append a presented CTA button to the chat (the "present a URL" path). */
  addCta: (url: string, label?: string, slug?: string, openInNewTab?: boolean) => void;
  /** Append a presented lead-capture form to the chat (mode="form"). */
  addForm: (form: LeadForm) => void;
  /** Append a rich-lead offer chip (quick_reply command). */
  addChip: (label: string, sendText: string) => void;
  /** Append product cards (products command). The runtime built them from the
   *  store's records and the answer's prose states no figures, so these are the
   *  only price/stock/link the shopper gets. */
  addProducts: (products: ProductCard[]) => void;
  /** Append answer-choice chips (answer_chips command) — 2–4 short answers
   *  or context_next_ask visitor questions. Returns whether they attached:
   *  false = discarded (a newer visitor message is in flight — decoupled
   *  chips arriving late — or every option was already asked). Callers must
   *  not open the panel or emit answer_chips_shown on false. */
  addAnswerChips: (
    chips: Array<{ label: string; sendText: string }>,
    turnId: string,
    kind?: "answer" | "context_next_ask",
  ) => boolean;
  open: () => void;
  /** Open panel and optionally submit a full question (living-bar click). */
  openWithText?: (text?: string) => void;
  /**
   * Experiment: open panel and seed suggested pills from section Qs
   * ("Get the answers now") without auto-submitting.
   */
  openWithSectionQuestions?: (questions: string[]) => void;
  /** Feed page-voice / invite lines into the living-bar typewriter queue. */
  setBarLines?: (lines: TypewriterLine[]) => void;
  /**
   * Full page embedding pool for this URL (from page-voice).
   * Display shows max 3 unasked; pool refills as questions are asked.
   */
  setPageQuestionPool?: (questions: string[]) => void;
  /**
   * Shared next-ask list (server bar or voice rank top-3). Voice speaks #1;
   * dead-chrome Suggested paints the same list — text and voice stay in step.
   */
  setRankedNextAsks?: (questions: string[]) => void;
  /**
   * Grounded page-voice meta for cold Click-to-talk summary (scenario 1).
   * Monologue / invites / section headings only — never freestyle claims.
   */
  setPageVoiceMeta?: (meta: {
    heroMonologue?: string[];
    inviteLines?: string[];
    sectionHeadings?: string[];
  }) => void;
  /**
   * Side rail + bottom dock continuity lead (same LLM line as hero/in-content).
   * Null/empty → lexical section template.
   */
  setRailContinuityLead?: (lead: string | null) => void;
  /** Alias: dock bar lead (bottom living bar). Same storage as rail continuity. */
  setDockContinuityLead?: (lead: string | null) => void;
  /** Experiment: scroll-aware dual chrome for the sticky living bar. */
  setSectionBarView?: (view: SectionBarView | null) => void;
  /** Dashboard-chosen section living-bar skin (sticky notes | glass). */
  setLivingBarSkin?: (skin: LivingBarSkin) => void;
  /** Inline placement mode (#592) — open | expand; pre-bind writes buffer. */
  setInlineMode?: (mode: InlineMode) => void;
  /** Config landed — the inline collapsed surface may paint (skin known). */
  setInlineConfigLanded?: () => void;
  /** Inline close button allowed (tenant setting, default off). */
  setInlineCloseAllowed?: (allowed: boolean) => void;
  /** Page-voice payload landed (even empty) — the capsule's row layout is
   *  final; the collapsed surface may paint content-complete. */
  setSectionDataLanded?: () => void;
  /**
   * THE voice input — the page-gated mode from snippet.applyScope. Everything
   * else (voiceAssist chrome, classicVoice behavior) derives from it inside;
   * there is deliberately no setVoiceAssist. Two writable copies of one fact,
   * with only one wired to the scope gate, is how a denied page kept a live
   * classic-voice UI (the #527 mic-leak class, iframe side).
   */
  setVoiceMode?: (mode: VoiceMode) => void;
  /** Toggle AI Voice session (hero placement + external controls). */
  toggleAiVoice?: () => void;
  /**
   * SPA nav while voice session is live: pause mic/speech (keep session on).
   */
  pauseVoiceForNavigation?: () => void;
  /**
   * After new page voice/sections load: announce page + current/next sections,
   * then resume listening (same dual-chrome awareness as the living bar).
   */
  announcePageForVoice?: (view: SectionBarView | null) => void;
  /** Play the SERVER-decided nav voice mp3 (cold monologue / warm reservation)
   *  for this navigation. No-ops unless a voice session is active. */
  playNavVoice?: (mp3Url: string) => void;
  /** Play a server-assembled HOST-line mp3 for the intent that requested it,
   *  running that trigger's stashed continuation (listen window) when done. */
  playHostVoice?: (intentId: string, mp3Url: string) => void;
  /** Scroll-dock: show/hide sticky living bar. */
  setStickyBarVisible?: (visible: boolean) => void;
  /**
   * Experiment: in-page AI section stage owns section pills/monologue.
   * When true, hide dual-chrome skins on the bottom bar (slim prompt only).
   */
  setVisible: (visible: boolean) => void;
  /** Push the per-tenant banner config (or null to hide it). */
  setBanner: (banner: BannerConfig | null) => void;
  /** Set the display mode via a host attribute; CSS docks the side panel. */
  setDisplay: (display: "bubble" | "side_panel") => void;
  /** Re-derive the page-aware topic copy (call on SPA navigation). */
  refreshTopic: () => void;
  /** Push the designed bottom-bar placement (copy + enabled) from the tenant's
   *  generated design; null = no design → built-in default bar. */
  setBottomBar: (bar: PlacementRuntime | null) => void;
  /**
   * Widget-scope verdict for the bottom bar — INDEPENDENT of the design.
   *
   * Cannot ride setBottomBar: `null` there means "no generated design, use
   * defaults", which the render gate reads as `?? true`. A tenant who has
   * never run Widget Design has design=null, so a scope rule had no way to
   * express "hidden" through that channel at all — it silently did nothing on
   * exactly the tenants most likely to be new. This is the third state.
   */
  setBarSuppressed?: (suppressed: boolean) => void;
  /** The tenant's lower-bar MODE verdict (always / unless_inline /
   *  inline_only) — the FLOATING bar only. Deliberately separate from
   *  setBarSuppressed, which means "no chrome on this page at all": merged,
   *  `unless_inline` suppressed the inline container's own collapsed bar,
   *  which is the surface that mode exists to defer TO. */
  setLowerBarModeAllows?: (allowed: boolean) => void;
  /** The tenant's lower-bar CONTENT: true shows the bar's question pills
   *  alone, with no capsule (BAR_CONTENTS "nudges"). Floating bar only. */
  setBarNudgesOnly?: (nudgesOnly: boolean) => void;
  /** Panel-header booking CTA chrome from preload; null hides the button. */
  setPrimaryCta: (cta: HeaderBookingCta | null) => void;
  unmount: () => void;
};

// External handles to push state into the React tree
let _setMessages: ((msgs: ChatMsg[]) => void) | null = null;
let _addMessage: ((msg: ChatMsg) => void) | null = null;
let _updateMessage: ((msg: ChatMsg) => void) | null = null;
let _setThinking: ((thinking: boolean) => void) | null = null;
let _addCta: ((url: string, label?: string, slug?: string, openInNewTab?: boolean) => void) | null = null;
let _addForm: ((form: LeadForm) => void) | null = null;
let _addChip: ((label: string, sendText: string) => void) | null = null;
let _addProducts: ((products: ProductCard[]) => void) | null = null;
let _addAnswerChips:
  | ((
      chips: Array<{ label: string; sendText: string }>,
      turnId: string,
      kind?: "answer" | "context_next_ask",
    ) => boolean)
  | null = null;
let _openChat: (() => void) | null = null;
let _openWithText: ((text?: string) => void) | null = null;
let _openWithSectionQuestions: ((questions: string[]) => void) | null = null;
let _setBarLines: ((lines: TypewriterLine[]) => void) | null = null;
let _setPageQuestionPool: ((questions: string[]) => void) | null = null;
let _setRankedNextAsks: ((questions: string[]) => void) | null = null;
let _setPageVoiceMeta:
  | ((meta: {
      heroMonologue?: string[];
      inviteLines?: string[];
      sectionHeadings?: string[];
    }) => void)
  | null = null;
let _setRailContinuityLead: ((lead: string | null) => void) | null = null;
let _setSectionBarView: ((view: SectionBarView | null) => void) | null = null;
/** Latest section view if set before React binds the handler (mount race). */
let _pendingSectionBarView: SectionBarView | null | undefined = undefined;
let _setLivingBarSkin: ((skin: LivingBarSkin) => void) | null = null;
/** Skin write before React binds the handler. */
let _pendingLivingBarSkin: LivingBarSkin | undefined = undefined;
let _setInlineMode: ((mode: InlineMode) => void) | null = null;
let _pendingInlineMode: InlineMode | undefined = undefined;
let _setInlineCfgLanded: (() => void) | null = null;
let _pendingInlineCfgLanded = false;
let _setInlineCloseAllowed: ((allowed: boolean) => void) | null = null;
let _pendingInlineCloseAllowed: boolean | undefined = undefined;
let _setSectionDataLanded: (() => void) | null = null;
let _pendingSectionDataLanded = false;
let _setVoiceMode: ((mode: VoiceMode) => void) | null = null;
let _pendingVoiceMode: VoiceMode | undefined;
let _toggleAiVoice: (() => void) | null = null;
let _pauseVoiceForNavigation: (() => void) | null = null;
let _announcePageForVoice: ((view: SectionBarView | null) => void) | null = null;
let _playNavVoice: ((mp3Url: string) => void) | null = null;
let _playHostVoice: ((intentId: string, mp3Url: string) => void) | null = null;
let _setStickyBarVisible: ((v: boolean) => void) | null = null;
let _setVisible: ((v: boolean) => void) | null = null;
let _setBanner: ((b: BannerConfig | null) => void) | null = null;
let _setDisplay: ((d: "bubble" | "side_panel") => void) | null = null;
let _refreshTopic: (() => void) | null = null;
let _setBottomBar: ((b: PlacementRuntime | null) => void) | null = null;
let _setBarSuppressed: ((v: boolean) => void) | null = null;
/** Verdict that arrived before React bound the setter (mount race). */
let _pendingBarSuppressed: boolean | undefined;
let _setLowerBarModeAllows: ((v: boolean) => void) | null = null;
/** Same mount race, same buffer: a lost pre-bind write would strand the
 *  floating bar on its default (allowed) and show a bar `inline_only` says
 *  should not exist. */
let _pendingLowerBarModeAllows: boolean | undefined;
let _setBarNudgesOnly: ((v: boolean) => void) | null = null;
/** Same mount race: a lost pre-bind write would paint the full bar for a
 *  tenant who chose questions only. */
let _pendingBarNudgesOnly: boolean | undefined;
/**
 * Sticky verdict that arrived before React bound the setter.
 *
 * THE MOUNT RACE, and this setter is the one that was missing a buffer.
 * `initialMount` calls `createBubble` and then `wireLivingBar` synchronously,
 * `wireLivingBar` builds the scroll-dock, and `setupScrollDock` runs
 * `attach()` immediately — which on a page with no hero/in-content anchor
 * calls `setSticky(true)` right there, before React has run its effects.
 *
 * Without a buffer that call vanished, AND scroll-dock's own `lastSticky`
 * dedup had already recorded `true`, so it never fired again: the collapsed
 * bar — the ONLY persistent launcher in side-panel mode — stayed hidden for
 * the life of the page. Intermittent, because whether the later
 * page-voice `refresh()` landed first was a race.
 */
let _pendingStickyBarVisible: boolean | undefined;
let _setPrimaryCta: ((c: HeaderBookingCta | null) => void) | null = null;
// Light-DOM host element (set by createBubble) — the open/keyboard effect marks
// it with .bhai-chat-open and carries --bhai-kb so host pages + chat.css can react.
let _hostEl: HTMLElement | null = null;

// Host-line voice intents in flight: intentId → the trigger's continuation
// (listen window) + a safety timer. When the server mp3 arrives via voice:play
// we run the onDone; if it never arrives we run onDone anyway (timer) so the
// voice conversation never stalls waiting on audio that isn't coming.
const _hostIntents = new Map<
  string,
  { onDone: () => void; timer: ReturnType<typeof setTimeout>; role: AgentSpeechRole }
>();
let _hostIntentSeq = 0;
/** Max wait for a host-line mp3 before continuing the conversation silently. */
const HOST_VOICE_INTENT_TIMEOUT_MS = 6000;
/** After a listen window expires, how long a flushed (async whisper)
 *  transcription may still deliver its late onFinal before the window truly
 *  closes. Whisper transcribes ~1-3s AFTER speech ends; abort() at the hard
 *  timeout discarded that in-flight speech (#397 regression). */
const STT_FLUSH_GRACE_MS = 4_000;
/**
 * A listen window that expires while the engine is still turning captured
 * audio into text must WAIT, not abort — those words were already spoken.
 * Polled rather than awaited so a wedged worker cannot hold the mic forever.
 */
const STT_TRANSCRIBE_POLL_MS = 400;
const STT_TRANSCRIBE_MAX_WAIT_MS = 8_000;

/** Host triggers that are page/nav monologues — interruptible on SPA route change. */
function hostTriggerSpeechRole(
  trigger:
    | "cold_open"
    | "engaged_nav"
    | "panel_invite"
    | "bridge"
    | "follow_up"
    | "read_them"
    | "cta_handoff",
): AgentSpeechRole {
  // Turn continuity + CTA handoff stay with the conversation; page intros do not.
  if (
    trigger === "follow_up" ||
    trigger === "read_them" ||
    trigger === "cta_handoff"
  ) {
    return "session";
  }
  return "nav";
}

function _resolveHostIntent(
  intentId: string,
): { onDone: () => void; role: AgentSpeechRole } | null {
  const entry = _hostIntents.get(intentId);
  if (!entry) return null;
  clearTimeout(entry.timer);
  _hostIntents.delete(intentId);
  return { onDone: entry.onDone, role: entry.role };
}

// sessionStorage key so a dismissed banner stays dismissed for the
// browsing session (and across SPA re-renders), but returns next visit.
const BANNER_DISMISS_KEY = "bhai-banner-dismissed";

function bannerDismissedThisSession(): boolean {
  try {
    return sessionStorage.getItem(BANNER_DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

// sessionStorage flag: keep the chat OPEN as the visitor moves through the site.
// A full page load re-mounts the widget fresh (React state resets to closed), so
// we persist the open intent for the browsing session and re-open on the next
// page — history comes back via the server's chat:init on (re)connect, so no
// client-side message cache is needed.
const CHAT_OPEN_KEY = "bhai-chat-open";

function chatOpenThisSession(): boolean {
  try {
    return sessionStorage.getItem(CHAT_OPEN_KEY) === "1";
  } catch {
    return false;
  }
}

function setChatOpenFlag(open: boolean): void {
  try {
    if (open) sessionStorage.setItem(CHAT_OPEN_KEY, "1");
    else sessionStorage.removeItem(CHAT_OPEN_KEY);
  } catch {
    // storage blocked (private mode / sandbox) — in-memory state still holds.
  }
}

/**
 * localStorage: the session that has an actual CONVERSATION in it.
 *
 * Distinct from CHAT_OPEN_KEY above, and the distinction is the whole point:
 * that one is sessionStorage, so it is per TAB. Open the site in a new tab and
 * the intent is gone while the conversation — the sid, in localStorage — is
 * still there, so the visitor met an empty field and their history only
 * reappeared once they typed. "I had old messages, it needs to be open."
 *
 * Stores the SID rather than a boolean, and is honoured only when it matches
 * the sid the host is about to use. A stale flag from a superseded session
 * would otherwise open an empty panel — worse than the collapsed field it
 * replaced. `bhai-` prefixed, so the new-session wipe clears it with
 * everything else.
 */
const CHAT_ENGAGED_KEY = "bhai-chat-engaged";

function markConversationEngaged(): void {
  try {
    const sid = localStorage.getItem("bh_sid");
    if (sid) localStorage.setItem(CHAT_ENGAGED_KEY, sid);
  } catch {
    /* storage blocked — in-memory state still holds for this page */
  }
}

function ChatIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.2L4 17.2V4h16v12z" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
    </svg>
  );
}

function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" aria-hidden="true">
      <rect x="9" y="3" width="6" height="11" rx="3" stroke="currentColor" strokeWidth="2" />
      <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M12 18v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
    </svg>
  );
}

// The BlueHat constellation — one central 4-point star + three satellites,
// each dimming and flaring on its own clock (animation lives in chat.css).
// The single identity mark across the system: collapsed bar, panel header,
// thinking state, composer. Reads as "alive / AI", never a chatbot icon.
function ConstellationIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="bhai-star" aria-hidden="true">
      <path className="bhai-star-s0" d="M12 3.6C12.9 8.4 15.6 11.1 20.4 12 15.6 12.9 12.9 15.6 12 20.4 11.1 15.6 8.4 12.9 3.6 12 8.4 11.1 11.1 8.4 12 3.6Z" />
      <path className="bhai-star-s1" d="M19 3.2C19.35 5 20.4 6.05 22.2 6.4 20.4 6.75 19.35 7.8 19 9.6 18.65 7.8 17.6 6.75 15.8 6.4 17.6 6.05 18.65 5 19 3.2Z" />
      <path className="bhai-star-s2" d="M5.4 14.8C5.68 16.2 6.5 17.02 7.9 17.3 6.5 17.58 5.68 18.4 5.4 19.8 5.12 18.4 4.3 17.58 2.9 17.3 4.3 17.02 5.12 16.2 5.4 14.8Z" />
      <path className="bhai-star-s3" d="M6.2 4.4C6.4 5.4 7 6 8 6.2 7 6.4 6.4 7 6.2 8 6 7 5.4 6.4 4.4 6.2 5.4 6 6 5.4 6.2 4.4Z" />
    </svg>
  );
}

// The single canned default — used for any CONTENT field the page's banner div
// omits. Styling is NOT here: colors come from the --bhjs-banner-* CSS custom
// properties (resolved in chat.css :host), the same mechanism the chat uses.
const CANNED_BANNER = {
  headline: "This is a new generation website.",
  subtext: "Ask a question and get a direct answer.",
  buttonLabel: "Give it a shot",
};

/** Sticky top-of-viewport banner — the website-native entry point. Clicking
 *  the banner (or its button) reveals the floating chat. CONTENT comes from the
 *  div's data-* (headline/subtext/button); STYLING comes from --bhjs-banner-*
 *  CSS variables, consistent with the chat's --bhjs-* theming. */
function Banner({
  config,
  onOpen,
  onDismiss,
}: {
  config: BannerConfig;
  onOpen: () => void;
  onDismiss: () => void;
}) {
  const subtext = config.subtext ?? CANNED_BANNER.subtext;

  return (
    <div className="banner" role="region" aria-label="Site assistant">
      <button className="banner-main" onClick={onOpen}>
        <span className="banner-inner">
          <span className="banner-text">
            <span className="banner-headline">{config.headline || CANNED_BANNER.headline}</span>
            {subtext && <span className="banner-subtext">{subtext}</span>}
          </span>
          <span className="banner-cta">{config.buttonLabel || CANNED_BANNER.buttonLabel}</span>
        </span>
      </button>
      <button
        className="banner-dismiss"
        aria-label="Dismiss"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss();
        }}
      >
        <CloseIcon />
      </button>
    </div>
  );
}

/** Thinking state — the constellation "reading the page", not queue-position
 *  dots. Grounded phrasing: it says retrieval, which is what's happening. */
function ThinkingIndicator() {
  return (
    <div className="thinking">
      <ConstellationIcon />
      <span className="thinking-label">Thinking…</span>
    </div>
  );
}

/** Inline lead-capture form (mode="form"). Collects the configured fields,
 *  validates required ones, and hands the values up on submit. */
function LeadFormView({
  form,
  onSubmit,
}: {
  form: LeadForm;
  onSubmit: (values: Record<string, string>) => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (submitted) {
    return <div className="chat-lead-done">Thanks — we'll be in touch shortly.</div>;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    for (const f of form.fields) {
      if (f.required && !(values[f.key] ?? "").trim()) {
        setError(`${f.label} is required.`);
        return;
      }
    }
    setError(null);
    setSubmitted(true);
    onSubmit(values);
  };

  return (
    <form className="chat-lead-form" onSubmit={handleSubmit}>
      {form.title && <div className="chat-lead-title">{form.title}</div>}
      {form.fields.map((f) => (
        <label key={f.key} className="chat-lead-field">
          <span>{f.label}{f.required ? " *" : ""}</span>
          <input
            type={f.type}
            value={values[f.key] ?? ""}
            onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
          />
        </label>
      ))}
      {error && <div className="chat-lead-error">{error}</div>}
      <button type="submit" className="chat-lead-submit">{form.submitLabel || "Submit"}</button>
    </form>
  );
}

/** Human label for a source page derived from its URL path — "/pricing" →
 *  "Pricing", "/" → "Home". Used when the server didn't send a label. */
function sourceLabel(u: string): string {
  try {
    const path = new URL(u, window.location.origin).pathname.replace(/\/+$/, "");
    const seg = path.split("/").filter(Boolean).pop();
    if (!seg) return "Home";
    return seg.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  } catch {
    return u;
  }
}

function ChatWindow({
  messages,
  thinking,
  onSend,
  onSubmitLead,
  onClose,
  onClickCta,
  onClickProduct,
  onClickChip,
  onClickAnswerChip,
  pagePills,
  onClickPagePill,
  primaryCta,
  voiceAssist = false,
  voiceSessionOn = false,
  voiceTalkState = "idle",
  voiceHint = null,
  voiceDraft = null,
  onEditVoiceDraft,
  onToggleVoice,
  onSpeak,
  onStopListening,
  classicVoice = false,
  initialDraft,
  autoFocusComposer = false,
  windowClassName,
}: {
  messages: ChatMsg[];
  thinking: boolean;
  onSend: (text: string) => void;
  onSubmitLead: (values: Record<string, string>) => void;
  /** When set, render an in-panel close control (side-panel mode — there is no
   *  floating circle; closing returns to the in-page text bar). */
  onClose?: () => void;
  onClickCta: (
    url: string,
    slug: string,
    openInNewTab: boolean,
    source?: "presented" | "panel_header",
  ) => void;
  /** A recommended product link was clicked in the prose. Optional: the design
   *  preview and any older host wrapper supply no product handler. */
  onClickProduct?: (productId: string, url: string) => void;
  onClickChip: (sendText: string) => void;
  onClickAnswerChip: (label: string, sendText: string, turnId: string) => void;
  /** Living-bar page questions as pills (under thinking, not after the answer). */
  pagePills?: PagePillsState | null;
  onClickPagePill?: (sendText: string) => void;
  /** Persistent tenant booking CTA in the panel header (chrome, not FM). */
  primaryCta?: HeaderBookingCta | null;
  /** Dashboard voice assist — show the composer mic. */
  voiceAssist?: boolean;
  voiceSessionOn?: boolean;
  voiceTalkState?: VoiceTalkState;
  voiceHint?: string | null;
  /** Live STT interim — mirrors what the visitor is saying into the prompt bar. */
  voiceDraft?: string | null;
  /** Visitor typed while a voice draft was showing — parent clears the draft. */
  onEditVoiceDraft?: () => void;
  onToggleVoice?: () => void;
  /** Re-arm listening inside an active voice session (classic Speak button). */
  onSpeak?: () => void;
  /** Finish the current listen (push-to-talk stop — commits pending speech). */
  onStopListening?: () => void;
  /** Classic AI Voice mode: original header cluster + text controls. */
  classicVoice?: boolean;
  /** #592 inline expand: keystrokes captured by the collapsed bar's input
   *  before the swap — seeds the composer once, on mount. */
  initialDraft?: string;
  /** #592 inline expand: focus the composer so typing continues seamlessly. */
  autoFocusComposer?: boolean;
  /** #592 inline: skin classes for the window (bhai-skwin--* + palette). */
  windowClassName?: string;
}) {
  const [input, setInput] = useState(initialDraft ?? "");
  const messagesRef = useRef<HTMLDivElement>(null);
  const inputElRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (autoFocusComposer) inputElRef.current?.focus();
    // Mount-time only — a later parent re-render must not steal focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll the messages container to its bottom on new messages AND on
  // streamed growth of the newest one (chat:update replaces the array).
  // Also when suggested pills paint — so dead-chrome chips are not hidden
  // under a long answer (voice/text march must be visible without hunting).
  // Set scrollTop directly (not scrollIntoView) so only this container
  // scrolls — the host page never scrolls.
  useEffect(() => {
    const el = messagesRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, thinking, pagePills]);

  // While the runtime is working, the newest agent TEXT message is the one
  // streaming — the caret rides it. If the newest message is still the
  // visitor's (no partial yet), show the "Thinking…" state instead.
  const last = messages.length > 0 ? messages[messages.length - 1] : undefined;
  const streamingId =
    thinking &&
    last &&
    last.sender === "agent" &&
    !last.cta &&
    !last.form &&
    !last.chip &&
    !last.answerChips
      ? last.id
      : null;

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const text = (voiceDraft ?? input).trim();
      if (!text) return;
      onSend(text);
      setInput("");
    },
    [input, voiceDraft, onSend],
  );

  // Page name for the header — shown BARE (no "Reading:" prefix): the AI must
  // never read as scoped to the current page; it speaks for the whole site.
  // No clean topic → show nothing (never "this page").
  const topic = pageTopic();

  return (
    // `part` is the ONLY way a host page can reach inside the shadow tree.
    // Four names, deliberately few: a designer can retheme the panel, its
    // composer and the bar without us exposing internals we then cannot move.
    <div
      part="panel"
      className={windowClassName ? `chat-window ${windowClassName}` : "chat-window"}
    >
      <div className="chat-head">
        <ConstellationIcon />
        <span className="chat-head-title">BlueHat AI</span>
        {topic && (
          <span className="chat-head-ctx">
            <i className="chat-head-dot" />
            {topic}
          </span>
        )}
        {/* Header actions pin right: [Voice] [Book] [X].
            Voice stays usable while typing — not only the outer page FAB. */}
        <div className="chat-head-actions">
          {classicVoice && onToggleVoice && (
            <div className="chat-head-voice">
              {voiceSessionOn && voiceHint && (
                <span className="chat-head-voice-hint" role="status" title={voiceHint}>
                  {voiceTalkState === "listening"
                    ? "Listening…"
                    : voiceHint.length > 28
                      ? `${voiceHint.slice(0, 26).trim()}…`
                      : voiceHint}
                </span>
              )}
              {voiceSessionOn && onSpeak && (
                <button
                  type="button"
                  className={`bhai-voice-talk chat-head-voice-btn bhai-voice-speak${
                    voiceTalkState === "listening" ? " bhai-voice-speak--listening" : ""
                  }`}
                  aria-label={
                    voiceTalkState === "listening" ? "Stop listening" : "Speak — start listening"
                  }
                  title={voiceTalkState === "listening" ? "Stop listening" : "Speak"}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (voiceTalkState === "listening") onStopListening?.();
                    else onSpeak();
                  }}
                >
                  {voiceTalkState === "listening" ? "Stop" : "Speak"}
                </button>
              )}
              <button
                type="button"
                className={`bhai-voice-talk chat-head-voice-btn${
                  voiceSessionOn ? " bhai-voice-talk--session" : ""
                }${
                  voiceSessionOn && voiceTalkState === "listening"
                    ? " bhai-voice-talk--listening"
                    : ""
                }`}
                aria-label={
                  voiceSessionOn
                    ? "AI Voice is on. Tap to turn off"
                    : "Turn AI Voice on"
                }
                aria-pressed={voiceSessionOn}
                title={
                  voiceSessionOn
                    ? "AI Voice is on — tap to turn off"
                    : "Turn AI Voice on"
                }
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleVoice();
                }}
              >
                {voiceSessionOn ? "AI Voice On" : "AI Voice"}
              </button>
            </div>
          )}
          {primaryCta && (
            <a
              className="chat-head-cta"
              href={primaryCta.url}
              rel="noopener noreferrer"
              data-bh-cta={primaryCta.slug}
              data-bh-cta-source="panel_header"
              onClick={(e) => {
                // Always host-execute (same path as voice) — no native tab/nav.
                e.preventDefault();
                onClickCta(
                  primaryCta.url,
                  primaryCta.slug,
                  primaryCta.openInNewTab,
                  "panel_header",
                );
              }}
            >
              {primaryCta.label}
            </a>
          )}
          {onClose && (
            <button type="button" className="chat-close" aria-label="Minimize chat" onClick={onClose}>
              <CloseIcon />
            </button>
          )}
        </div>
      </div>
      <div className="chat-messages" ref={messagesRef}>
        {/* Empty state — the page-aware invitation IS the canvas: the widget
            already knows the page, so an empty panel says "Ask me about
            <topic>" front and center. Gone the moment the visitor types. */}
        {messages.length === 0 && !thinking && input === "" && !pagePills && (
          <div className="chat-empty">
            <ConstellationIcon />
            <div className="chat-empty-title">{askAboutCopy("Ask me anything")}</div>
          </div>
        )}
        {messages.map((msg) =>
          msg.products && msg.products.length > 0 ? (
            // Product cards — the runtime's, built from the store's records.
            // The answer's prose states no price, stock or link, so this row is
            // the only place those reach the shopper. Each card is one click
            // target; the url arrives already stamped, so the click is
            // attributed on ARRIVAL and needs no event from here.
            <div key={msg.id} className="chat-products">
              {msg.products.map((p) => {
                const price = priceLabel(p.price, p.currency);
                const stock = stockLabel(p.availability);
                return (
                  <a
                    key={p.id}
                    className="chat-product"
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-bh-product={p.id}
                  >
                    {p.image ? (
                      <img className="chat-product-img" src={p.image} alt="" loading="lazy" />
                    ) : (
                      <div className="chat-product-img" />
                    )}
                    <div className="chat-product-body">
                      <div className="chat-product-title">{p.title}</div>
                      <div className="chat-product-meta">
                        {price ? <span className="chat-product-price">{price}</span> : null}
                        {price && p.discountCode ? (
                          <span className="chat-product-discount">({discountNote(p.discountCode)})</span>
                        ) : null}
                        {stock ? <span className="chat-product-stock">{stock}</span> : null}
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          ) : msg.cta ? (
            // Presented CTA — a standalone clickable button (no bubble).
            // Tagged with data-bh-cta=<slug>; clicking records a cta_click
            // (#156) then navigates (the host defers nav so the event posts).
            <a
              key={msg.id}
              className="chat-cta"
              href={msg.cta.url}
              rel="noopener noreferrer"
              data-bh-cta={msg.cta.slug}
              onClick={(e) => {
                // Always host-execute (same path as voice / header Book a Demo).
                e.preventDefault();
                onClickCta(
                  msg.cta!.url,
                  msg.cta!.slug ?? "",
                  msg.cta!.openInNewTab ?? true,
                  "presented",
                );
              }}
            >
              {msg.cta.label}
            </a>
          ) : msg.chip ? (
            // Rich-lead offer chip — one-tap accept. Clicking sends the accept
            // text into the chat (like typing it) and posts rich_lead_opt_in.
            <button
              key={msg.id}
              type="button"
              className="chat-chip"
              onClick={() => onClickChip(msg.chip!.sendText)}
            >
              {msg.chip.label}
            </button>
          ) : msg.answerChips && msg.answerChips.kind !== "page_question_pill" ? (
            // Enumerable answer choices OR context next-asks (visitor questions).
            // Tap submits sendText as the visitor message — NOT rich_lead_opt_in.
            // (page_question_pill rows are rendered under the thinking indicator.)
            msg.answerChips.kind === "context_next_ask" ? (
              <div key={msg.id} className="chat-suggested">
                <div className="chat-suggested-label">Suggested questions</div>
                <div
                  className="chat-answer-chips chat-answer-chips--page-pills"
                  role="group"
                  aria-label="Suggested questions"
                >
                  {msg.answerChips.options.map((opt) => (
                    <button
                      key={opt.sendText}
                      type="button"
                      className="chat-chip chat-chip--answer chat-chip--page-question-pill"
                      title={opt.sendText}
                      onClick={() =>
                        onClickAnswerChip(opt.label, opt.sendText, msg.answerChips!.turnId)
                      }
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div key={msg.id} className="chat-answer-chips" role="group" aria-label="Suggested answers">
                {msg.answerChips.options.map((opt) => (
                  <button
                    key={opt.label}
                    type="button"
                    className="chat-chip chat-chip--answer"
                    onClick={() =>
                      onClickAnswerChip(opt.label, opt.sendText, msg.answerChips!.turnId)
                    }
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )
          ) : msg.form ? (
            <LeadFormView key={msg.id} form={msg.form} onSubmit={onSubmitLead} />
          ) : msg.sender === "agent" ? (
            // The AI's answer — typeset prose on the canvas, no bubble. The
            // caret rides the newest answer while it streams; source chips
            // (the pages that grounded it) follow once present.
            <div key={msg.id} className="chat-turn chat-turn--agent">
              <div
                className="chat-answer"
                // Delegated ON PURPOSE. A recommended product reaches the visitor as
                // a markdown link in the prose, not as a component we render, so
                // there is no anchor of ours to attach a handler to. One listener on
                // the answer catches every product link the model emits, including
                // ones in lists and tables.
                onClick={(e) => {
                  if (!onClickProduct) return;
                  const anchor = (e.target as HTMLElement | null)?.closest?.("a");
                  const href = anchor?.getAttribute("href");
                  if (!href) return;
                  const productId = productIdFromStampedUrl(href);
                  if (!productId) return;
                  // Record BEFORE navigating. The arrival credit is the fallback for
                  // exactly the race this loses when the page unloads first.
                  e.preventDefault();
                  onClickProduct(productId, href);
                }}
              >
                <Markdown options={MARKDOWN_OPTS}>{msg.text}</Markdown>
                {streamingId === msg.id && <span className="chat-caret" />}
              </div>
              {(msg.sources?.length ?? 0) > 0 && (
                <div className="chat-sources">
                  {msg.sources!.map((s) => (
                    <a key={s.url} className="chat-src" href={s.url} target="_blank" rel="noopener noreferrer">
                      {s.label || sourceLabel(s.url)}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ) : (
            // The visitor's turn — a small, quiet block; their words are the
            // question, the AI's words are the hero.
            <div key={msg.id} className="chat-turn chat-turn--client">
              <div className="chat-turn-who">You</div>
              <div className="chat-turn-said">{msg.text}</div>
            </div>
          ),
        )}
        {thinking && !streamingId && <ThinkingIndicator />}
        {/* Embedding pills only when NOT thinking. Hidden while answer chips /
            CTA / RLH own the footer. Answer OR pills — never both. */}
        {!thinking &&
          pagePills &&
          pagePills.options.length > 0 &&
          onClickPagePill &&
          !shouldSuppressPagePills(messages) && (
          <div className="chat-suggested">
            <div className="chat-suggested-label">Suggested questions</div>
            <div
              className="chat-answer-chips chat-answer-chips--page-pills"
              role="group"
              aria-label="Suggested questions"
            >
              {pagePills.options.map((opt) => (
                <button
                  key={opt.sendText}
                  type="button"
                  className="chat-chip chat-chip--answer chat-chip--page-question-pill"
                  title={opt.sendText}
                  onClick={() => onClickPagePill(opt.sendText)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <form part="composer" className="chat-input" onSubmit={handleSubmit}>
        <ConstellationIcon />
        <input
          ref={inputElRef}
          type="text"
          value={voiceDraft ?? input}
          onChange={(e) => {
            if (voiceDraft != null) onEditVoiceDraft?.();
            setInput(e.target.value);
          }}
          placeholder="Type your own questions here"
        />
        {/* Voice on the prompt bar so typing + stop/listen stay available. */}
        {classicVoice && onToggleVoice && (
          <button
            type="button"
            className={`bhai-voice-talk chat-input-voice${
              voiceSessionOn ? " bhai-voice-talk--session" : ""
            }${
              voiceSessionOn && voiceTalkState === "listening"
                ? " bhai-voice-talk--listening"
                : ""
            }`}
            aria-label={
              voiceSessionOn
                ? "AI Voice is on. Tap to turn off"
                : "Turn AI Voice on"
            }
            aria-pressed={voiceSessionOn}
            title={
              voiceSessionOn
                ? "AI Voice is on — tap to turn off"
                : "Turn AI Voice on"
            }
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleVoice();
            }}
          >
            {voiceSessionOn ? "AI Voice On" : "AI Voice"}
          </button>
        )}
        {/* Voice on the prompt bar so typing + stop/listen stay available. */}
        {!classicVoice && voiceAssist && onToggleVoice && (
          <button
            type="button"
            className={`bhai-voice-talk chat-input-voice chat-input-voice--mic${
              voiceSessionOn ? " bhai-voice-talk--session" : ""
            }${
              voiceSessionOn && voiceTalkState === "listening"
                ? " bhai-voice-talk--listening"
                : ""
            }`}
            aria-label={
              voiceSessionOn
                ? "Voice is on. Tap to turn off"
                : "Ask by voice"
            }
            aria-pressed={voiceSessionOn}
            title={
              voiceSessionOn
                ? "Voice is on — tap to turn off"
                : "Ask by voice — tap, then speak"
            }
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleVoice();
            }}
          >
            <MicIcon />
          </button>
        )}
        <button type="submit" aria-label="Send">
          <SendIcon />
        </button>
      </form>
      {/* Full-width brand line across the panel bottom — every tenant's widget
          carries the BlueHatAI.ai attribution.

          TEMPORARILY HIDDEN (Brian, 2026-09-29). Flip SHOW_POWERED_BY back to
          true to restore it; the markup and CSS are untouched.

          Gated on the RENDER rather than hidden with CSS on purpose: the widget's
          styles live in a low-priority @layer and deliberately carry no
          !important so a tenant can restyle them, which means a host page could
          override `display: none` and put the attribution back. A render gate
          cannot be overridden from the host, and the node never enters the DOM
          rather than sitting there invisible. (The string itself does still ship
          in bh.js — the minifier keeps the branch — so grepping the bundle is
          not a way to check whether this is on.) */}
      {SHOW_POWERED_BY && (
        <div className="chat-powered">
          Powered by{" "}
          <a href="https://bluehatai.ai" target="_blank" rel="noopener noreferrer">
            BlueHatAI.ai
          </a>
        </div>
      )}
    </div>
  );
}

function Bubble({
  callbacks,
  mode,
  initialBanner = null,
  initialVisible = false,
  initialInlineMode = null,
  initialLivingBarSkin = null,
  initialConfigLanded = false,
  initialInlineClose = false,
  agentChipsAllowed = true,
}: {
  callbacks: BubbleCallbacks;
  mode: "floating" | "inline";
  initialBanner?: BannerConfig | null;
  initialVisible?: boolean;
  /** #592: inline placement mode when resolvable at mount; null until config. */
  initialInlineMode?: InlineMode | null;
  /** Skin that landed pre-mount — seeds state so frame 1 paints styled. */
  initialLivingBarSkin?: LivingBarSkin | null;
  /** Config landed pre-mount — the inline surface's paint permission. */
  initialConfigLanded?: boolean;
  /** Tenant's inline close-button setting, when config landed pre-mount. */
  initialInlineClose?: boolean;
  /** `data-bhai-chips="off"` on the blessed div. Opt-out, so the default is on. */
  agentChipsAllowed?: boolean;
}) {
  const inline = mode === "inline";
  // Restore an open chat across a full page load: a hard navigation re-mounts
  // the widget fresh, so we re-open from the persisted session flag (computed
  // once). INLINE participates too (2026-08-24): a visitor who has already
  // asked something should not be handed an empty field again on the next
  // page — the conversation is the state, and re-collapsing it reads as the
  // widget having forgotten them.
  //
  // Read synchronously at mount on purpose. Waiting for the restored messages
  // to arrive would paint the collapsed field first and swap a beat later,
  // which is precisely the flicker class this file spent 2026-08-17 removing.
  const [reopen] = useState(() => !inline && chatOpenThisSession());
  // Floating starts hidden until config says chatEnabled — UNLESS config already
  // arrived before mount (initialVisible) or we're reopening a persisted chat.
  // Seeding here paints on the first render, avoiding the race where
  // handle.setVisible()/open() right after createBubble() no-ops (the refs are
  // wired in a later useEffect).
  const [visible, setVisibleState] = useState(inline || initialVisible || reopen);
  // #592: inline no longer force-opens — "expand" starts collapsed; "open"
  // (and a late-resolving "open") opens via the effect below.
  // NO inline surface resumes a past conversation OPEN (Fadi, 2026-08-26).
  // Agent used to — and a returning visitor got the full panel ambushing
  // mid-page (ndx, 2026-08-25). Inline now always mounts collapsed; an
  // engaged session used to get a resume chip on the agent surface instead
  // (removed 2026-10-13 — the chip slot is for nudges; the field below still
  // replays the transcript on the visitor's first message).
  // The floating bar keeps its own per-tab intent flag as it always did.
  const [open, setOpen] = useState(
    inline ? initialInlineMode === "open" : reopen,
  );
  const [inlineMode, setInlineModeState] = useState<InlineMode | null>(initialInlineMode);
  /** Pre-expansion keystrokes from the collapsed bar's real input (#592). */
  const [inlineDraft, setInlineDraft] = useState("");
  /** Site agent's in-place draft. Unlike inlineDraft it does NOT open the
   *  chat as it changes — the visitor types, then commits with Enter. */
  const [agentDraft, setAgentDraft] = useState("");
  /** Pending chip swap, cleared on unmount so a rotation cannot outlive the
   *  surface it belongs to. */
  const agentChipSwapRef = useRef<number | null>(null);
  /** Height to apply on expand when the blessed div was sized for a bar,
   *  not a panel — measured in openChat BEFORE the chat commits. */
  const inlineExpandHeightRef = useRef<string | null>(null);
  const inlineOpenedRef = useRef(false);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [thinking, setThinkingState] = useState(false);
  // Seeded synchronously from the page's banner div so it paints on first
  // render; later add/remove (SPA nav) flows through _setBanner.
  const [banner, setBannerState] = useState<BannerConfig | null>(initialBanner);
  const [bannerDismissed, setBannerDismissed] = useState(bannerDismissedThisSession);
  // Side-panel is the ONLY mode globally — the bubble is ripped out, so display
  // is pinned to side_panel and never reads "bubble" from config.
  const [display, setDisplayState] = useState<"bubble" | "side_panel">("side_panel");
  // The page-aware bottom bar is the SOLE persistent launcher now (no bubble), so
  // it shows from first paint — `minimized` starts true, unless we're reopening a
  // persisted chat (then it starts open).
  const [minimized, setMinimized] = useState(!reopen);
  // Bumped on SPA navigation to re-derive the page-aware bar copy.
  const [, setTopicTick] = useState(0);
  // The designed bottom-bar placement (copy + enabled) from the tenant's
  // generated design; null = no design yet → built-in default bar.
  const [bottomBar, setBottomBarState] = useState<PlacementRuntime | null>(null);
  /** Per-URL scope said hide the bar. Separate from the design's own enabled
   *  flag, and from the session dismiss. */
  const [barSuppressed, setBarSuppressedState] = useState(false);
  /**
   * The tenant's lower-bar MODE verdict, kept apart from `barSuppressed`
   * because the two answer different questions:
   *
   *  - `barSuppressed` = "no widget chrome on this page" (scope deny, design
   *    disable). It applies to every surface, inline included.
   *  - this = "where does the LOWER BAR live" (always / unless_inline /
   *    inline_only). It is a FLOATING-bar question. An inline container is
   *    not the lower bar — it is the thing `unless_inline` defers TO — so
   *    suppressing the inline surface with it kills the exact surface the
   *    mode was protecting (bench 2026-08-24: expand-mode containers painted
   *    nothing at all under the default mode).
   */
  const [lowerBarModeAllows, setLowerBarModeAllowsState] = useState(true);
  /** Lower bar shows its question pills alone (BAR_CONTENTS "nudges"). */
  const [barNudgesOnly, setBarNudgesOnlyState] = useState(false);
  // Panel-header booking CTA (preload primaryCta). null = no enabled booking CTA.
  const [primaryCta, setPrimaryCtaState] = useState<HeaderBookingCta | null>(null);
  // Living bar: dismissed → compact pill; sticky dock visibility; typewriter queue.
  const [barDismissed, setBarDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(BAR_DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });
  // false while in-page hero/line is on screen; true after scroll past hero
  // (scroll-dock). Nav-only installs show immediately.
  const [stickyBarVisible, setStickyBarVisibleState] = useState(false);
  const [barLines, setBarLinesState] = useState<TypewriterLine[]>(() =>
    buildBarQueue({ fallbackPlaceholder: "Ask me anything" }),
  );
  const [barTyping, setBarTyping] = useState(false);
  const [markPulse, setMarkPulse] = useState(false);
  /** Suggested-next / page-question pill bucket (client-only, not FM). */
  const [pagePills, setPagePills] = useState<PagePillsState | null>(null);
  /** Experiment: scroll-aware dual chrome for sticky living bar. */
  const [sectionBarView, setSectionBarViewState] = useState<SectionBarView | null>(null);
  /**
   * Shared LLM/lexical continuity lead for side rail + bottom dock.
   * Kept in React state so the collapsed bar re-renders when snippet applies LLM.
   */
  const [railContinuityLead, setRailContinuityLeadState] = useState<string | null>(null);
  /**
   * When the in-page AI section stage is showing, bottom bar stays slim —
   * no glass/notes dual chrome (stage owns section questions).
   */
  /**
   * Dashboard-chosen section living-bar skin. null until preload/config —
   * do not default to sticky_notes (that flashed the wrong skin on load).
   */
  const [livingBarSkin, setLivingBarSkinState] =
    useState<LivingBarSkin | null>(initialLivingBarSkin);
  /**
   * Collapsed-surface paint gate, both mounts. Two independent facts:
   *  - THEME is never a wait: skin classes derive from the skin alone
   *    (preload-known), so any paint is dressed. Inline additionally waits
   *    for config (#592 — the div attribute reveals the mode early, not the
   *    look; skin=null pre-config means UNKNOWN).
   *  - CONTENT completeness: the capsule's row layout differs with vs
   *    without section data (sparse "let's chat" row vs kicker + Ask
   *    privately + chips). Painting the sparse variant while the page-voice
   *    payload is in flight showed a 1-frame layout flip on every sectioned
   *    page (screencast, bench 2026-08-17). So the surface holds the ~50ms
   *    until the payload resolves — an EMPTY payload paints the sparse row
   *    immediately (it is that page's final look), and a 2s failsafe covers
   *    a lost payload (still dressed — the theme fact above).
   */
  const [inlineCfgLanded, setInlineCfgLandedState] = useState(inline ? initialConfigLanded : true);
  /** Tenant setting: an X on the inline collapsed surfaces. Default off. */
  const [inlineCloseAllowed, setInlineCloseAllowedState] = useState(inline && initialInlineClose);
  /** The visitor closed the inline unit this session (only reachable when the
   *  tenant allows the X). */
  const [inlineDismissed, setInlineDismissed] = useState(() => {
    if (!inline) return false;
    try {
      return sessionStorage.getItem(INLINE_DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [sectionDataLanded, setSectionDataLandedState] = useState(false);
  const [surfaceForced, setSurfaceForced] = useState(false);
  const surfaceReady = surfaceForced || (inlineCfgLanded && sectionDataLanded);
  useEffect(() => {
    if (surfaceReady) return;
    const t = setTimeout(() => setSurfaceForced(true), 2000);
    return () => clearTimeout(t);
  }, [surfaceReady]);
  /** Phone surface chrome: 1 this-section + 2 coming-up, compact cover. */
  const mobileSurface = useMobileSurface();
  /**
   * Page-gated voice mode from snippet.applyScope — THE only voice state.
   * "classic" = the original AI Voice feature (monologues, nav narration, its
   * own controls — THE SAME OLD FEATURE); "tap_to_talk" = voice IN, text out:
   * the mic listens, the answer renders in the chat, the AI never speaks.
   *
   * `voiceAssist` and `classicVoice` are DERIVED — never independent state.
   * When a page's scope rules deny voice, the snippet pushes "off" and both
   * flags go false together; there is no second variable a gate can miss.
   */
  const [voiceMode, setVoiceModeState] = useState<VoiceMode>("off");
  const voiceAssist = voiceMode !== "off";
  const voiceAssistRef = useRef(false);
  voiceAssistRef.current = voiceAssist;
  const classicVoice = voiceMode === "classic";
  const classicVoiceRef = useRef(false);
  classicVoiceRef.current = classicVoice;
  const [voiceTalkState, setVoiceTalkState] = useState<VoiceTalkState>("idle");
  /**
   * True while AI Voice session is active (TTS + listen + nav host).
   * Drives the mic's on state — not only the brief listening window.
   */
  const [voiceSessionOn, setVoiceSessionOn] = useState(false);
  const [voiceHint, setVoiceHint] = useState<string | null>(null);
  /**
   * A hint for a state the visitor is no longer IN — "didn't catch that",
   * "mic issue", "voice unavailable". These used to be set with no clear
   * timer, so the bar kept announcing a dead-end long after it had passed
   * (live 2026-08-05: a first-use voice failure left the message parked in
   * the bar for the rest of the session, over the bar's own lead line).
   * Announce it, then hand the bar back.
   */
  const setTerminalVoiceHint = useCallback((msg: string, ms = 4000) => {
    setVoiceHint(msg);
    window.setTimeout(() => {
      setVoiceHint((prev) => (prev === msg ? null : prev));
    }, ms);
  }, []);
  const voiceTalkRef = useRef<VoiceTalkHandle | null>(null);
  /**
   * The engine is holding captured audio it has not turned into text yet.
   * The listen-window timeouts must NEVER abort while this is true: those
   * words were already spoken, and aborting deletes them silently (live
   * 2026-08-05 — the visitor's question vanished with no log and no reply).
   */
  const sttTranscribingRef = useRef(false);
  /** Bound on how long a timeout may defer for one in-flight transcription. */
  const sttTranscribeWaitRef = useRef(0);
  /** After click-to-talk, speak the next completed agent reply (TTS of SB text). */
  const speakNextAgentReplyRef = useRef(false);
  const lastSpokenAgentIdRef = useRef<string | null>(null);
  /**
   * Voice conversation session: stays on after first AI Voice on until mute /
   * stop talking / endVoiceSession. Mirrored by voiceSessionOn for React UI.
   */
  const voiceConversationRef = useRef(false);
  /** Speak full pill bridge only once per voice session (not every re-listen). */
  const voiceBridgeSpokenRef = useRef(false);
  /**
   * Cold-open page summary spoken at most once per page visit (not every
   * AI Voice re-tap after auto-off).
   */
  const coldOpenSpokenRef = useRef(false);
  /**
   * Paths that already got a monologue intro this browser tab session.
   * Cleared when AI Voice turns fully off so a re-test / re-open can hear
   * the page summary again (fixes: history present → cold skipped forever).
   */
  const spokenPageIntroPathsRef = useRef<Set<string>>(new Set());
  /**
   * Bumps when a new user turn starts or voice stops — cancels pending
   * answer → re-listen chain so two speeches never overlap.
   */
  const voiceChainRef = useRef(0);
  /** Last STT warm-up hint decile — dedupes progress re-renders. */
  const sttWarmPctRef = useRef(0);
  /** Bumps each push-to-talk listen window so stale 10s timers no-op. */
  const pushToTalkListenGenRef = useRef(0);
  /**
   * Next mic open is after host speech (monologue / bridge / FM / follow-up):
   * longer window, faster silence commit, hard timer starts only when STT
   * reports listening. Fixes "pick or ask me anything" → spoken Q ignored.
   */
  const postAnswerListenRef = useRef(false);
  /** One free re-arm if that invite listen expires with no transcript. */
  const postAnswerRetryRef = useRef(false);
  /** Pending re-listen after answer — must clear when a new question is asked. */
  const reListenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * Normalized visitor text we armed voice for. TTS only speaks the agent
   * reply that follows THIS ask (Sales Brain text for that turn — never an
   * older answer still on screen from history).
   */
  const pendingVoiceUserKeyRef = useRef<string | null>(null);
  /** Watchdog: an armed voice must never dead-end on a lost answer. */
  const armWatchdogRef = useRef<number | null>(null);
  /** Pill offered by nav host (visitor can say yes / tap / ignore). */
  const pendingNavPillRef = useRef<string | null>(null);
  /** Canned page-monologue URL delivered on a pageview BEFORE any voice
   *  session exists. Unengaged sessions get monologue URLs (never minted
   *  reservations) — stash it so the first AI-Voice tap plays the
   *  pre-generated mp3 instantly: no reservation, no LLM, no synth wait. */
  const pendingNavVoiceUrlRef = useRef<{ path: string; url: string } | null>(null);
  /** One quiet cross-page re-listen per path while the conversation is active. */
  const convoListenPathRef = useRef<string | null>(null);
  /** Live STT interim mirrored into the prompt bar ("write what I'm saying"). */
  const [voiceDraft, setVoiceDraft] = useState<string | null>(null);
  /** Mirror of voiceDraft for hard-timer / flush paths (state alone is stale in timers). */
  const voiceDraftRef = useRef<string | null>(null);
  voiceDraftRef.current = voiceDraft;
  /** Avoid re-announcing the same page+section in one session. */
  const lastNavAnnounceKeyRef = useRef<string | null>(null);
  /**
   * Count of engaged SPA page-lands this voice session (1-based when speaking).
   * Meta pick/ask/stop instructions only on odd lands (1, 3, 5…).
   */
  const engagedNavCountRef = useRef(0);
  /**
   * Latest section view to announce after current speech/listen finishes.
   * Scroll must never cut mid-sentence — queue only.
   */
  const pendingScrollAnnounceRef = useRef<SectionBarView | null>(null);
  /** Hard stop for push-to-talk / nav listen window (10s → session off). */
  const navListenHardTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const startClickToTalkRef = useRef<(opts?: { skipBridge?: boolean }) => void>(
    () => {},
  );
  const flushOrSendRef = useRef<(text: string) => void>(() => {});
  /** Voice "book demo" → open presented CTA or panel-header booking CTA. */
  const launchBookDemoRef = useRef<() => boolean>(() => false);
  const announcePageForVoiceRef = useRef<
    ((view: SectionBarView | null) => void) | null
  >(null);

  const clearReListenTimer = useCallback(() => {
    if (reListenTimerRef.current != null) {
      clearTimeout(reListenTimerRef.current);
      reListenTimerRef.current = null;
    }
    if (navListenHardTimerRef.current != null) {
      clearTimeout(navListenHardTimerRef.current);
      navListenHardTimerRef.current = null;
    }
  }, []);

  /** Session-scoped voice telemetry → /api/events (via snippet onBarEvent). */
  /** Render-free mic glow: write the live input level to a CSS var on the
   *  light-DOM host — no React state, no re-renders (the progress-storm
   *  lesson). chat.css scales the listening ring with it. */
  const setMicLevel = useCallback((level: number) => {
    try {
      _hostEl?.style.setProperty("--bhai-mic-level", level.toFixed(2));
    } catch {
      /* ignore */
    }
  }, []);

  const emitVoiceEvent = useCallback((ev: BarEvent) => {
    try {
      callbacksRef.current.onBarEvent?.(ev);
    } catch {
      /* ignore */
    }
  }, []);

  /**
   * Request a server-assembled host line. The client signals the trigger +
   * minimal context ONLY — the server assembles every spoken word and returns
   * the mp3 URL on the events response, relayed back as voice:play with our
   * intentId. `onDone` (the trigger's listen-window continuation) runs when the
   * mp3 finishes, or after a safety timeout if no audio arrives. Zero client
   * text assembly.
   */
  const requestHostVoice = useCallback(
    (
      descriptor: {
        trigger:
          | "cold_open"
          | "engaged_nav"
          | "panel_invite"
          | "bridge"
          | "follow_up"
          | "read_them"
          | "cta_handoff";
        sectionIndex?: number;
        panelOpen?: boolean;
        transcript?: string;
        pill?: string | null;
        /** follow_up: on-screen pills — the spoken line reads exactly these. */
        visibleAsks?: string[];
      },
      onDone: () => void,
    ) => {
      // Host lines are CLASSIC-ONLY. Tap-to-talk is voice in / text out — the
      // AI never speaks, so no intent is emitted (the server then never
      // mints or synthesizes a line: no reservation, no TTS spend). The
      // continuation still runs — callers chain listen windows off it, and a
      // swallowed onDone would leave that machinery hung, not silent.
      if (!classicVoiceRef.current) {
        setTimeout(onDone, 0);
        return;
      }
      const intentId = `hi_${++_hostIntentSeq}`;
      const role = hostTriggerSpeechRole(descriptor.trigger);
      const timer = setTimeout(() => {
        _hostIntents.delete(intentId);
        try {
          onDone();
        } catch {
          /* ignore */
        }
      }, HOST_VOICE_INTENT_TIMEOUT_MS);
      _hostIntents.set(intentId, { onDone, timer, role });
      emitVoiceEvent({
        event: "voice_intent",
        trigger: descriptor.trigger,
        intentId,
        ...(descriptor.sectionIndex != null ? { sectionIndex: descriptor.sectionIndex } : {}),
        ...(descriptor.panelOpen != null ? { panelOpen: descriptor.panelOpen } : {}),
        ...(descriptor.transcript != null ? { transcript: descriptor.transcript } : {}),
        ...(descriptor.pill != null ? { pill: descriptor.pill } : {}),
        ...(descriptor.visibleAsks?.length ? { visibleAsks: descriptor.visibleAsks } : {}),
      });
    },
    [emitVoiceEvent],
  );

  /**
   * Hard kill: no mic, no re-listen timers, no nav offers, no OpenAI voice HTTP.
   * Used by "stop talking" / mute. Does NOT reopen listen on any path.
   */
  const endVoiceSession = useCallback(
    (hint?: string) => {
      const wasOn = voiceConversationRef.current;
      setVoiceDraft(null);
      voiceConversationRef.current = false;
      setVoiceSessionOn(false);
      voiceBridgeSpokenRef.current = false;
      speakNextAgentReplyRef.current = false;
      pendingVoiceUserKeyRef.current = null;
      if (armWatchdogRef.current != null) {
        window.clearTimeout(armWatchdogRef.current);
        armWatchdogRef.current = null;
      }
      pendingNavPillRef.current = null;
      pendingScrollAnnounceRef.current = null;
      // Reset so a later AI Voice can re-offer this page on nav when re-armed.
      lastNavAnnounceKeyRef.current = null;
      engagedNavCountRef.current = 0;
      coldOpenSpokenRef.current = false;
      spokenPageIntroPathsRef.current = new Set();
      clearReListenTimer();
      pushToTalkListenGenRef.current += 1;
      voiceChainRef.current += 1;
      voiceTalkRef.current?.abort();
      voiceTalkRef.current = null;
      setVoiceTalkState("idle");
      setWhisperMuted(true);
      stopAgentSpeech();
      abortOpenAiVoiceNetwork();
      // Brief confirmation, then clear so UI doesn't stay stuck on a hint.
      const msg = hint ?? "AI Voice off.";
      setVoiceHint(msg);
      window.setTimeout(() => {
        setVoiceHint((prev) => (prev === msg ? null : prev));
      }, 2200);
      if (wasOn) {
        emitVoiceEvent({
          event: "voice_session_ended",
          reason: (hint ?? "off").slice(0, 80),
        });
      }
      try {
        console.log("[BlueHat voice] session ended — mic + OpenAI voice stopped");
      } catch {
        /* ignore */
      }
    },
    [clearReListenTimer, emitVoiceEvent],
  );
  const endVoiceSessionRef = useRef(endVoiceSession);
  endVoiceSessionRef.current = endVoiceSession;

  const openRef = useRef(open);
  openRef.current = open;
  const pagePillsRef = useRef(pagePills);
  pagePillsRef.current = pagePills;

  /**
   * Post-answer voice inventory — LLM dual chrome ONLY (in tune with chat):
   * - CTA / form after this turn's prose → mode "cta" (no pills text or voice)
   * - FM poles (Yes/Not yet, Paid/Both, …) → mode "fm" (mic first)
   * - context_next_ask from the engine → mode "next" (speak those only)
   * Never page/bar/section inventory — that was "voice read pills not on chat".
   */
  const collectVoiceFollowUp = useCallback((): {
    mode: "fm" | "next" | "cta" | "none";
    items: string[];
  } => {
    const msgs = messagesRef.current;
    // Current-turn chrome ONLY: chips/CTA rows are appended AFTER their turn's
    // prose. Anything at/before the newest agent text belongs to a finished turn.
    let lastProseIdx = -1;
    for (let i = msgs.length - 1; i >= 0; i--) {
      const m = msgs[i]!;
      if (m.sender === "agent" && (m.text?.trim() ?? "").length > 0) {
        lastProseIdx = i;
        break;
      }
    }
    // CTA / lead form owns the turn — no FM or next-ask voice.
    for (let i = msgs.length - 1; i > lastProseIdx; i--) {
      const m = msgs[i]!;
      if (m.cta || m.form) return { mode: "cta", items: [] };
    }

    const fmItems: string[] = [];
    const nextItems: string[] = [];
    const fmSeen = new Set<string>();
    const nextSeen = new Set<string>();
    const pushInto = (
      bag: string[],
      bagSeen: Set<string>,
      q: string | undefined | null,
    ) => {
      const t = (q ?? "").replace(/\s+/g, " ").trim();
      if (!t || t.length < 4) return;
      const k = normalizeQuestion(t);
      if (bagSeen.has(k)) return;
      bagSeen.add(k);
      bag.push(t);
    };
    for (let i = msgs.length - 1; i > lastProseIdx; i--) {
      const m = msgs[i]!;
      if (m.sender !== "agent" || !m.answerChips?.options?.length) continue;
      const kind = m.answerChips.kind;
      if (kind === "context_next_ask") {
        for (const o of m.answerChips.options) {
          pushInto(nextItems, nextSeen, o.sendText || o.label);
        }
      } else if (kind === "answer" || kind == null) {
        for (const o of m.answerChips.options) {
          pushInto(fmItems, fmSeen, o.label || o.sendText);
        }
      }
    }
    // Dual chrome: FM poles first (answer the fork); next-asks on screen for tap.
    if (fmItems.length > 0) {
      return { mode: "fm", items: fmItems.slice(0, 4) };
    }
    if (nextItems.length > 0) {
      return { mode: "next", items: nextItems.slice(0, 6) };
    }
    return { mode: "none", items: [] };
  }, []);

  /**
   * After an answer:
   * - CTA → mic only (say "book demo" / fill form); never read off-screen pills
   * - FM poles → mic first (poles already in the SB answer)
   * - LLM context_next_ask only → speak those visibleAsks, then mic
   * - else → mic only
   */
  const speakFollowUpThenListen = useCallback(
    (chain: number) => {
      if (chain !== voiceChainRef.current) return;
      if (!voiceConversationRef.current || isWhisperMuted()) return;
      if (speakNextAgentReplyRef.current || thinkingRef.current) return;

      const panelOpen = openRef.current;
      const openMic = (hint: string) => {
        setVoiceHint(hint);
        clearReListenTimer();
        // Mark post-answer listen BEFORE open so startListeningOnly uses the
        // longer FM window (whisper cold-start was eating the old 5s).
        postAnswerListenRef.current = true;
        postAnswerRetryRef.current = true;
        reListenTimerRef.current = setTimeout(() => {
          reListenTimerRef.current = null;
          if (chain !== voiceChainRef.current) {
            try {
              console.log("[BlueHat voice] openMic DEAD: chain moved", {
                chain,
                now: voiceChainRef.current,
              });
            } catch {
              /* ignore */
            }
            return;
          }
          if (!voiceConversationRef.current || isWhisperMuted()) {
            try {
              console.log("[BlueHat voice] openMic DEAD: conv/mute");
            } catch {
              /* ignore */
            }
            return;
          }
          if (speakNextAgentReplyRef.current || thinkingRef.current) {
            try {
              console.log("[BlueHat voice] openMic DEAD: armed/thinking");
            } catch {
              /* ignore */
            }
            return;
          }
          startClickToTalkRef.current({ skipBridge: true });
        }, 60);
      };

      const { mode, items } = collectVoiceFollowUp();

      // CTA turn: no pill voice (text already suppressed). Mic for "book demo".
      if (mode === "cta") {
        openMic(
          panelOpen
            ? "Demo form is ready — say book demo, or fill it out…"
            : "Listening — say book demo, or ask anything…",
        );
        return;
      }

      // Panel closed → mic still opens (voice is the interface); never invent pills.
      if (!panelOpen) {
        openMic("Listening — ask me anything…");
        return;
      }

      // FM fork already spoken in the SB answer — open mic, do not re-read poles.
      if (mode === "fm") {
        const poles = items.slice(0, 3).filter(Boolean);
        openMic(
          poles.length > 0
            ? `Your turn — ${poles.join(", ")}, or ask something else.`
            : "Your turn — pick a choice on screen, or ask.",
        );
        return;
      }

      // LLM context_next_ask on this turn only — classic speaks exactly
      // those, then mic; tap-to-talk keeps them ON SCREEN only.
      if (mode === "next" && items.length > 0) {
        if (!classicVoiceRef.current) {
          openMic("Your turn — ask, or tap a question on screen.");
          return;
        }
        setVoiceHint("Next questions on screen…");
        requestHostVoice(
          {
            trigger: "follow_up",
            panelOpen: true,
            visibleAsks: items.slice(0, 3),
          },
          () => {
            if (chain !== voiceChainRef.current) return;
            if (!voiceConversationRef.current || isWhisperMuted()) return;
            if (speakNextAgentReplyRef.current || thinkingRef.current) return;
            openMic("Your turn — ask, or tap a question on screen.");
          },
        );
        return;
      }

      // Dead chrome / nothing: mic only — never page-bar inventory aloud.
      openMic("Your turn — ask me anything…");
    },
    [collectVoiceFollowUp, clearReListenTimer, requestHostVoice],
  );

  /**
   * Latest visitor message + its following agent reply (Sales Brain only).
   * Skips CTA/chip chrome rows.
   */
  const findLatestTurnPair = useCallback(
    (
      msgs: ChatMsg[],
    ): { client: ChatMsg; agent: ChatMsg } | null => {
      let lastClientIdx = -1;
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i]!.sender === "client") {
          lastClientIdx = i;
          break;
        }
      }
      if (lastClientIdx < 0) return null;
      const client = msgs[lastClientIdx]!;
      // Agent must follow THIS client — first agent after client, not a later turn.
      for (let i = lastClientIdx + 1; i < msgs.length; i++) {
        const m = msgs[i]!;
        if (m.sender === "client") break; // next user turn — no agent for this pair yet
        if (
          m.sender === "agent" &&
          m.text.trim().length > 0 &&
          !m.cta &&
          !m.form &&
          !m.chip &&
          !m.answerChips
        ) {
          return { client, agent: m };
        }
      }
      return null;
    },
    [],
  );

  /**
   * Pair for a specific armed visitor text (normalized). Prevents late voice
   * from speaking turn N while turn N+1 is already on screen.
   */
  const findTurnPairForUserKey = useCallback(
    (
      msgs: ChatMsg[],
      userKey: string,
    ): { client: ChatMsg; agent: ChatMsg } | null => {
      if (!userKey || userKey === "__pending__") return null;
      // Prefer the latest matching client (re-asks).
      for (let i = msgs.length - 1; i >= 0; i--) {
        const m = msgs[i]!;
        if (m.sender !== "client") continue;
        if (normalizeQuestion(m.text) !== userKey) continue;
        for (let j = i + 1; j < msgs.length; j++) {
          const a = msgs[j]!;
          if (a.sender === "client") break;
          if (
            a.sender === "agent" &&
            a.text.trim().length > 0 &&
            !a.cta &&
            !a.form &&
            !a.chip &&
            !a.answerChips
          ) {
            return { client: m, agent: a };
          }
        }
        return null;
      }
      return null;
    },
    [],
  );

  /**
   * Arm voice for the next Sales Brain reply to `userText`.
   * Only that turn's agent text will be spoken (same string as chat).
   * Always cancels in-flight TTS so a new ask never plays a previous answer.
   */
  const armVoiceForNextAnswer = useCallback(
    (userText?: string) => {
      // CLASSIC-ONLY: tap-to-talk answers render as text, so nothing arms.
      // Gating here (not just at the speak site) matters — the arm starts a
      // 45s watchdog whose miss path re-opens the mic, which in a text-out
      // mode would pop a listen window out of nowhere long after the answer.
      if (!classicVoice || !voiceConversationRef.current) return;
      clearReListenTimer();
      // Bump chain first so in-flight speak onDone cannot re-open the mic.
      voiceChainRef.current += 1;
      stopAgentSpeech();
      voiceTalkRef.current?.abort();
      setVoiceTalkState("idle");
      speakNextAgentReplyRef.current = true;
      // Watchdog (Fadi's rule: whatever the click order, it must never get
      // stuck). If no answer pairs up (turn failed / connection dropped /
      // commands lost), release the arm and hand the visitor the mic instead
      // of an eternal "Waiting for the answer…". Re-checks while a long turn
      // is genuinely still thinking.
      if (armWatchdogRef.current != null) window.clearTimeout(armWatchdogRef.current);
      const armWatch = (delayMs: number) => {
        armWatchdogRef.current = window.setTimeout(() => {
          armWatchdogRef.current = null;
          if (!speakNextAgentReplyRef.current) return; // resolved
          if (thinkingRef.current) {
            armWatch(20_000); // still working — keep watching
            return;
          }
          speakNextAgentReplyRef.current = false;
          pendingVoiceUserKeyRef.current = null;
          if (!voiceConversationRef.current || isWhisperMuted()) return;
          setVoiceHint("Your turn — ask me anything…");
          startClickToTalkRef.current({ skipBridge: true });
        }, delayMs);
      };
      armWatch(45_000);
      const k = userText?.trim() ? normalizeQuestion(userText) : null;
      // Prefer a real user key — __pending__ only when mid-join with no text yet.
      pendingVoiceUserKeyRef.current = k && k.length > 0 ? k : "__pending__";
    },
    [classicVoice, clearReListenTimer],
  );
  /** Stable for openWithText / handlers bound in mount effect. */
  const armVoiceForNextAnswerRef = useRef(armVoiceForNextAnswer);
  armVoiceForNextAnswerRef.current = armVoiceForNextAnswer;
  const sectionBarViewRef = useRef(sectionBarView);
  sectionBarViewRef.current = sectionBarView;
  const textRef = useRef<HTMLSpanElement | null>(null);
  const typewriterRef = useRef<TypewriterHandle | null>(null);
  const barLinesRef = useRef(barLines);
  barLinesRef.current = barLines;
  const thinkingRef = useRef(thinking);
  thinkingRef.current = thinking;
  const pagePillsSourceRef = useRef<PanelOpenSource>("bar_rest");
  /**
   * In-memory mirror of session-wide asked page questions (sessionStorage).
   * Seeded on mount; markAsked keeps store + ref in sync.
   */
  const askedQuestionsRef = useRef<Set<string>>(getAskedPageQuestions());
  /**
   * Full page embedding pool for this URL (set by snippet from page-voice).
   * showPagePills takes the next unasked from here when not in section mode.
   */
  const pageQuestionPoolRef = useRef<string[]>([]);
  /**
   * Grounded page-voice meta for cold open summary (hero monologue / invites /
   * section headings). Cleared on SPA nav before new page-voice loads.
   */
  const pageVoiceMetaRef = useRef<{
    heroMonologue: string[];
    inviteLines: string[];
    sectionHeadings: string[];
  }>({ heroMonologue: [], inviteLines: [], sectionHeadings: [] });
  /**
   * When the panel was opened from the section living bar, refill suggested
   * pills from this list (current + coming) until exhausted — never jump to
   * the flat page pool while these remain.
   */
  const sectionPillPoolRef = useRef<string[]>([]);
  /**
   * Shared next-ask list (server bar compose and/or voice rank top-3).
   * Voice speaks #1; Suggested paints the same strings — text/voice march.
   */
  const rankedNextAsksRef = useRef<string[]>([]);
  /** Latest messages for “has answer chips?” checks without stale closures. */
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  const markAsked = useCallback((text: string) => {
    const k = normalizeQuestion(text);
    if (!k) return;
    askedQuestionsRef.current.add(k);
    markPageQuestionAsked(text);
  }, []);

  /**
   * Paint dead-chrome Suggested from an explicit ranked list (shared with voice).
   * Does not re-rank — preserves the order voice used / server bar composed.
   */
  const paintRankedNextAsks = useCallback(
    (ranked: readonly string[], source: PanelOpenSource) => {
      if (thinkingRef.current) {
        setPagePills(null);
        return;
      }
      for (const k of getAskedPageQuestions()) askedQuestionsRef.current.add(k);
      const qs = filterUnaskedPageQuestions(
        ranked.map((q) => q.replace(/\s+/g, " ").trim()).filter(Boolean),
        MAX_PAGE_PILLS,
      );
      rankedNextAsksRef.current = [...ranked];
      if (qs.length === 0) {
        setPagePills(null);
        return;
      }
      const turnId = `pq-ranked-${Date.now()}`;
      const options = qs.map((q) => ({ label: pillLabel(q), sendText: q }));
      pagePillsSourceRef.current = source;
      setPagePills({ turnId, source, options });
      callbacksRef.current.onPageQuestionPillsShown?.(
        qs.map(questionHash),
        source,
      );
    },
    [],
  );

  /**
   * Rebuild suggested questions (dead chrome only — caller must gate FM chips).
   * Priority:
   *   1. Shared ranked next-ask list (server bar / voice rank) — same as voice
   *   2. Section coming-up pool
   *   3. Page embedding pool (focus + diversify)
   * Never show while thinking — answer OR pills, not both.
   */
  const showPagePills = useCallback((source: PanelOpenSource, exclude?: string) => {
    if (thinkingRef.current) {
      setPagePills(null);
      return;
    }
    if (exclude) markAsked(exclude);
    // Re-read session store (host sends / SPA / multi-tab same origin).
    for (const k of getAskedPageQuestions()) askedQuestionsRef.current.add(k);

    let qs: string[] = [];
    const focus = readConversationFocus();
    const lastUser = focus?.lastUser ?? null;

    // 1) Shared ranked list — text/voice single source of truth.
    if (rankedNextAsksRef.current.length > 0) {
      qs = filterUnaskedPageQuestions(rankedNextAsksRef.current, MAX_PAGE_PILLS);
      if (qs.length === 0) rankedNextAsksRef.current = [];
    }

    if (qs.length === 0 && sectionPillPoolRef.current.length > 0) {
      // Coming-up (or CTA-seeded) inventory only — diversify twins, no focus re-rank.
      qs = pickDiversePageQuestions(
        filterUnaskedPageQuestions(sectionPillPoolRef.current, MAX_PAGE_PILLS + 4),
        MAX_PAGE_PILLS,
        { againstLastUser: lastUser },
      );
      if (qs.length === 0) {
        // Coming-up set exhausted this session — fall through to page pool.
        sectionPillPoolRef.current = [];
      }
    }
    if (qs.length === 0) {
      const pool =
        pageQuestionPoolRef.current.length > 0
          ? pageQuestionPoolRef.current
          : pageQuestionsForPills(barLinesRef.current, askedQuestionsRef.current);
      // Page pool: light focus rank is OK for discoverability when not section-seeded.
      qs = pickDiversePageQuestions(
        rankQuestionsByFocus(
          filterUnaskedPageQuestions(pool, MAX_PAGE_PILLS + 6),
          focus,
        ),
        MAX_PAGE_PILLS,
        { againstLastUser: lastUser },
      );
    }
    if (qs.length === 0) {
      setPagePills(null);
      return;
    }
    const turnId = `pq-pills-${Date.now()}`;
    const options = qs.map((q) => ({ label: pillLabel(q), sendText: q }));
    pagePillsSourceRef.current = source;
    setPagePills({ turnId, source, options });
    callbacksRef.current.onPageQuestionPillsShown?.(
      qs.map(questionHash),
      source,
    );
  }, [markAsked]);

  const flushOrSend = useCallback((text: string) => {
    const payload = text.trim();
    if (!payload) return;
    // One turn at a time: ignore pill/type spam while the model is working.
    if (thinkingRef.current) return;
    // Book demo is a CTA execute, not another SB turn (telemetry: "so book the
    // demo" re-pitched the same CTA answer instead of launching).
    if (
      voiceAssist &&
      voiceConversationRef.current &&
      isBookDemoCommand(payload)
    ) {
      if (launchBookDemoRef.current?.()) {
        setVoiceDraft(null);
        setVoiceHint("Opening demo…");
        return;
      }
    }
    setVoiceDraft(null);
    markAsked(payload);
    // ANY send retires a pending voice offer: even when the visitor answers
    // with a DIFFERENT ask (spoken or tapped), the offered question was heard
    // aloud — it must never re-surface as a pill. QA receipt: voice offered
    // "How do I launch…", visitor tapped a different pill, and the engine
    // re-served the offered question as suggestion #1 next turn.
    const offeredPill = pendingNavPillRef.current;
    pendingNavPillRef.current = null;
    if (offeredPill && normalizeQuestion(offeredPill) !== normalizeQuestion(payload)) {
      markAsked(offeredPill);
    }
    setThinkingState(true);
    setPagePills(null); // hide bucket while the turn is in flight
    // Chips / page pills must arm voice the same way as spoken asks.
    // (armVoiceForNextAnswer itself is classic-only.)
    armVoiceForNextAnswer(payload);
    if (classicVoiceRef.current && voiceConversationRef.current) {
      setVoiceHint("Answering…");
    } else if (voiceConversationRef.current) {
      // Tap-to-talk is ONE-SHOT: the utterance committed, so the mic's job is
      // done — end the session here rather than leaving a live-looking mic
      // with nothing listening (whose next tap would read as "turn off", the
      // double-tap trap). The answer arrives as text like any typed turn; the
      // visitor taps again to speak again.
      endVoiceSessionRef.current?.();
    }
    callbacksRef.current.onSend(payload);
  }, [markAsked, armVoiceForNextAnswer]);
  flushOrSendRef.current = flushOrSend;

  // When a turn finishes (thinking true→false): restore remaining unasked
  // embedding pills unless answer chips / CTA / RLH own the footer (dead-chrome fix).
  const wasThinkingRef = useRef(false);
  useEffect(() => {
    const ended = wasThinkingRef.current && !thinking;
    wasThinkingRef.current = thinking;
    if (thinking) {
      // Never leave pills visible under Thinking… (answer OR pills).
      setPagePills(null);
      return;
    }
    if (ended) {
      // Answer-chips / CTA often arrive just after thinking clears — wait a
      // beat so we don't flash page FAQ under FM chips or a demo link. Retry
      // so true answer-only turns still get a suggested-next path.
      const restoreIfNoChips = () => {
        if (thinkingRef.current) return;
        const msgs = messagesRef.current;
        if (shouldSuppressPagePills(msgs)) return;
        showPagePills(pagePillsSourceRef.current);
      };
      window.setTimeout(restoreIfNoChips, 400);
      window.setTimeout(restoreIfNoChips, 900);
      window.setTimeout(restoreIfNoChips, 1600);
    }
  }, [thinking, showPagePills]);

  useEffect(() => {
    _setMessages = (msgs) => setMessages(msgs);
    // De-dup by id: a reconnect race (chat:init replay overlapping an in-flight
    // chat:new) must never render the same message twice.
    _addMessage = (msg) =>
      setMessages((prev) => {
        if (prev.some((m) => m.id === msg.id)) return prev;
        // The client paints a local placeholder for a question the server has
        // not persisted yet (#514, chat:init pendingQuestion). The server then
        // echoes that same message back with its OWN id — and an id-only
        // de-dup let both render, so the visitor saw their question twice
        // (reported live on 0.1.3-beta4). The authoritative row replaces the
        // placeholder in place, keeping its position in the transcript.
        //
        // Identity first: the echo carries the send's own clientMsgId and the
        // placeholder's id embeds the same value — exact, survives any server
        // text normalisation. The text comparison below is ONLY the
        // mixed-version fallback (older iframe bundle or older server that
        // doesn't echo the id yet) and can be retired once the fleet is past
        // both.
        const adoptIdx = prev.findIndex((m) =>
          msg.clientMsgId
            ? m.id === placeholderIdFor(msg.clientMsgId)
            : adoptsPendingRow(m, msg),
        );
        if (adoptIdx >= 0) {
          const adopted = [...prev];
          adopted[adoptIdx] = msg;
          return adopted;
        }
        const next = [...prev, msg];
        // Conversation focus for rail/bar continuity (presentation only).
        if (
          msg.sender === "agent" &&
          (msg.text?.trim() ?? "").length > 20 &&
          !msg.answerChips &&
          !msg.cta &&
          !msg.chip &&
          !msg.form
        ) {
          let lastUser = "";
          for (let i = next.length - 2; i >= 0; i--) {
            const m = next[i]!;
            if (m.sender === "client" && m.text?.trim()) {
              lastUser = m.text;
              break;
            }
          }
          if (lastUser) {
            writeConversationFocus({
              lastUser,
              lastAgent: msg.text,
            });
          }
        }
        return next;
      });
    _updateMessage = (msg) =>
      setMessages((prev) =>
        prev.map((m) => (m.id === msg.id ? msg : m)),
      );
    _setThinking = (t) => setThinkingState(t);
    _addCta = (url, label, slug, openInNewTab) => {
      // CTA owns the footer — no FM / context chips / page FAQs in text or voice.
      setPagePills(null);
      rankedNextAsksRef.current = [];
      setMessages((prev) => [
        ...prev.filter((m) => !m.answerChips && !m.chip),
        {
          id: `cta-${prev.length}-${url}`,
          sender: "agent",
          text: "",
          createdAt: Date.now(),
          cta: { url, label: label && label.trim() ? label : "Open link", slug, openInNewTab },
        },
      ]);
    };
    _addForm = (form) => {
      setPagePills(null);
      rankedNextAsksRef.current = [];
      setMessages((prev) => [
        ...prev.filter((m) => !m.answerChips && !m.chip),
        { id: `form-${prev.length}`, sender: "agent", text: "", createdAt: Date.now(), form },
      ]);
    };
    _addChip = (label, sendText) =>
      setMessages((prev) => [
        // One live offer chip at a time — a re-offer replaces the older chip
        // instead of stacking duplicates. Does not touch answerChips rows.
        ...prev.filter((m) => !m.chip),
        { id: `chip-${prev.length}`, sender: "agent", text: "", createdAt: Date.now(), chip: { label, sendText } },
      ]);
    _addProducts = (products) => {
      // Its own row, appended after the answer it belongs to. Chip/answerChip
      // rows are NOT filtered out the way a CTA or form filters them: cards are
      // the answer's evidence, not a competing call to action, so follow-up
      // chips under them remain useful.
      setMessages((prev) => [
        ...prev,
        {
          id: `products-${prev.length}-${products[0]?.id ?? ""}`,
          sender: "agent",
          text: "",
          createdAt: Date.now(),
          products,
        },
      ]);
    };
    _addAnswerChips = (chips, turnId, kind) => {
      // Chips decouple: server chips may now arrive seconds AFTER their
      // answer. If the visitor has already sent the NEXT message (thinking =
      // a send is in flight — handleSend set it), these chips answer a
      // question that is no longer the tail of the conversation: appending
      // them would seat stale poles under the new pending turn, and tapping
      // one would send a stale reply. Discard; the return value tells the
      // snippet to skip open/telemetry too. Normal flow is unaffected — the
      // answer frame clears thinking BEFORE its chips frame arrives.
      if (thinkingRef.current) return false;
      // Dual chrome: FM poles (kind answer) AND logical next (context_next_ask)
      // may both show as separate rows. Replace only the SAME kind so the other
      // layer survives. Always kill client page-pool FAQs — logical next is
      // engine context_next_ask only, never diversify "What is BlueHatAI?".
      const chipKind = kind === "context_next_ask" ? "context_next_ask" : "answer";
      // Never leave client page-pool FAQs under engine chips.
      setPagePills(null);
      // Context next-asks: drop already asked this session. Empty → free-type.
      let options = chips;
      if (chipKind === "context_next_ask") {
        for (const k of getAskedPageQuestions()) askedQuestionsRef.current.add(k);
        options = filterUnaskedChipOptions(chips, MAX_PAGE_PILLS);
        // Everything already asked → nothing renders; report NOT accepted so
        // the snippet skips the forced open and the answer_chips_shown event
        // (which used to fire even for this silent no-op).
        if (options.length === 0) return false;
        // Shared next-ask list (voice #1 / paint) — same strings as Suggested.
        const ranked = options.map((o) => o.sendText || o.label);
        rankedNextAsksRef.current = ranked;
        // Persist SB-influenced next asks into conversation focus (for rail rank).
        try {
          const msgs = messagesRef.current;
          let lastUser = "";
          let lastAgent = "";
          for (let i = msgs.length - 1; i >= 0; i--) {
            const m = msgs[i]!;
            if (!lastAgent && m.sender === "agent" && m.text?.trim()) {
              lastAgent = m.text;
            }
            if (m.sender === "client" && m.text?.trim()) {
              lastUser = m.text;
              break;
            }
          }
          if (lastUser) {
            writeConversationFocus({
              lastUser,
              lastAgent,
              nextAskHints: ranked,
            });
          }
        } catch {
          /* ignore */
        }
        // Host side rail: show logical next pills now (not only after SPA nav).
        // forceEngaged: focus write just ran; don't wait for storage races.
        try {
          const idx = sectionBarViewRef.current?.sectionIndex ?? 0;
          const rail = buildSectionBarView([], idx, {
            engagedRanked: ranked,
            forceEngaged: true,
          });
          if (rail.active) setSectionBarViewState(rail);
        } catch {
          /* ignore */
        }
      }
      setMessages((prev) => [
        // Keep the other chrome layer (FM vs logical next); replace this kind only.
        ...prev.filter((m) => {
          if (!m.answerChips) return true;
          const k = m.answerChips.kind ?? "answer";
          return k !== chipKind;
        }),
        {
          id: `answer-chips-${chipKind}-${prev.length}`,
          sender: "agent",
          text: "",
          createdAt: Date.now(),
          answerChips: {
            turnId,
            options,
            kind: chipKind,
          },
        },
      ]);
      return true;
    };
    _openChat = () => {
      // Opening always reveals the chat — a placement/launcher or a backend
      // open command should show it even if it was hidden (chatEnabled=false).
      setVisibleState(true);
      setMinimized(false);
      setOpen((prev) => {
        if (!prev) {
          callbacks.onOpen();
          callbacks.onBarEvent?.({ event: "panel_opened" });
        }
        return true;
      });
      // Current-page grounded pills (discoverability). With session continuity,
      // history may already be in the panel; pills still reflect THIS page.
      showPagePills("bar_rest");
    };
    _openWithText = (text?: string) => {
      setVisibleState(true);
      setMinimized(false);
      setOpen((prev) => {
        if (!prev) {
          callbacks.onOpen();
          callbacks.onBarEvent?.({ event: "panel_opened" });
        }
        return true;
      });
      if (text && text.trim()) {
        // Intentional submit of a full grounded question (hero / placement /
        // living-bar typewriter). Same arm path as askFromSurface / typed send
        // — without arm, AI Voice stays silent on the SB reply (telemetry:
        // response_rendered + no sb_answer after panel_opened from bar).
        const payload = text.trim();
        markAsked(payload);
        setPagePills(null);
        setThinkingState(true);
        armVoiceForNextAnswerRef.current(payload);
        if (voiceConversationRef.current) {
          setVoiceHint("Answering…");
        }
        // Open posts chat:open → iframe connectWs (async). Two rAFs + short
        // timeout keeps order: open handled, then message queued.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.setTimeout(() => callbacks.onSend(payload), 50);
          });
        });
      } else {
        // Open without a pre-selected Q — show embedding pills to pick.
        showPagePills("bar_rest");
      }
    };
    /** "Get the answers now" — open + seed pills from CTA list; no auto-send. */
    _openWithSectionQuestions = (questions: string[]) => {
      const raw = (questions ?? []).filter(
        (q): q is string => typeof q === "string" && q.trim().length > 0,
      );
      // Bar CTA: seed the list provided (usually this+next display cards).
      // After a later Answer-now from rail, askFromSurface re-seeds to coming-up only.
      sectionPillPoolRef.current = raw;
      setVisibleState(true);
      setMinimized(false);
      setOpen((prev) => {
        if (!prev) {
          callbacks.onOpen();
          callbacks.onBarEvent?.({ event: "panel_opened" });
        }
        return true;
      });
      const focus = readConversationFocus();
      const qs = pickDiversePageQuestions(
        filterUnaskedPageQuestions(raw, MAX_PAGE_PILLS + 4),
        MAX_PAGE_PILLS,
        { againstLastUser: focus?.lastUser ?? null },
      );
      if (qs.length === 0) {
        sectionPillPoolRef.current = [];
        showPagePills("bar_rest");
        return;
      }
      const turnId = `pq-section-${Date.now()}`;
      const options = qs.map((q) => ({ label: pillLabel(q), sendText: q }));
      pagePillsSourceRef.current = "bar_rest";
      setPagePills({ turnId, source: "bar_rest", options });
      callbacksRef.current.onPageQuestionPillsShown?.(
        qs.map(questionHash),
        "bar_rest",
      );
    };
    _setBarLines = (lines) => setBarLinesState(lines);
    _setPageQuestionPool = (questions) => {
      pageQuestionPoolRef.current = Array.isArray(questions)
        ? questions.filter((q) => typeof q === "string" && q.trim().length > 0)
        : [];
      // New page corpus → leave section-seed mode so SPA nav gets page pills.
      sectionPillPoolRef.current = [];
      // Do NOT clear rankedNextAsks here — reapplyLivingBarFromCache calls this
      // after applyServerBar; wiping would race the server list and force the old
      // diversify path. SPA clears via setRankedNextAsks([]) + lastRankedPagePills.
    };
    /**
     * Shared next-ask list from server bar compose (preferred — already metered
     * server-side) or any external rank. Paints Suggested; voice reads #1.
     * Does not open free TTS or extra rank calls.
     */
    _setRankedNextAsks = (questions) => {
      const clean = (Array.isArray(questions) ? questions : [])
        .map((q) => (typeof q === "string" ? q.replace(/\s+/g, " ").trim() : ""))
        .filter((q) => q.length >= 6)
        .slice(0, MAX_PAGE_PILLS);
      rankedNextAsksRef.current = clean;
      if (clean.length === 0) return;
      // Only paint when panel would show dead-chrome pills (not mid-turn FM).
      if (thinkingRef.current) return;
      if (shouldSuppressPagePills(messagesRef.current)) return;
      paintRankedNextAsks(clean, pagePillsSourceRef.current || "bar_rest");
    };
    _setPageVoiceMeta = (meta) => {
      pageVoiceMetaRef.current = {
        heroMonologue: Array.isArray(meta.heroMonologue)
          ? meta.heroMonologue.filter((s) => typeof s === "string" && s.trim())
          : [],
        inviteLines: Array.isArray(meta.inviteLines)
          ? meta.inviteLines.filter((s) => typeof s === "string" && s.trim())
          : [],
        sectionHeadings: Array.isArray(meta.sectionHeadings)
          ? meta.sectionHeadings.filter((s) => typeof s === "string" && s.trim())
          : [],
      };
    };
    _setRailContinuityLead = (lead) => {
      const t = typeof lead === "string" ? lead.replace(/\s+/g, " ").trim() : "";
      setRailContinuityLeadState(t || null);
    };
    _setSectionBarView = (view) => {
      setSectionBarViewState(view && view.active ? view : null);
      // A REAL view implies the payload landed (batched with the view state).
      // A null push does NOT — the mount-race replay pushes null before any
      // data; the empty-payload case signals through setSectionDataLanded.
      if (view && view.active) setSectionDataLandedState(true);
    };
    _setSectionDataLanded = () => setSectionDataLandedState(true);
    if (_pendingSectionDataLanded) {
      _pendingSectionDataLanded = false;
      setSectionDataLandedState(true);
    }
    // Flush any section chrome that arrived before this effect bound the ref.
    if (_pendingSectionBarView !== undefined) {
      const pending = _pendingSectionBarView;
      _pendingSectionBarView = undefined;
      _setSectionBarView(pending);
    }
    _setLivingBarSkin = (skin) => setLivingBarSkinState(skin);
    _setInlineMode = (m) => setInlineModeState(m);
    if (_pendingInlineMode !== undefined) {
      const pendingInline = _pendingInlineMode;
      _pendingInlineMode = undefined;
      setInlineModeState(pendingInline);
    }
    _setInlineCfgLanded = () => setInlineCfgLandedState(true);
    if (_pendingInlineCfgLanded) {
      _pendingInlineCfgLanded = false;
      setInlineCfgLandedState(true);
    }
    _setInlineCloseAllowed = (allowed) => setInlineCloseAllowedState(allowed);
    if (_pendingInlineCloseAllowed !== undefined) {
      const pending = _pendingInlineCloseAllowed;
      _pendingInlineCloseAllowed = undefined;
      setInlineCloseAllowedState(pending);
    }
    if (_pendingLivingBarSkin !== undefined) {
      const pending = _pendingLivingBarSkin;
      _pendingLivingBarSkin = undefined;
      setLivingBarSkinState(pending);
    }
    _setVoiceMode = (mode) => setVoiceModeState(mode);
    if (_pendingVoiceMode !== undefined) {
      const pending = _pendingVoiceMode;
      _pendingVoiceMode = undefined;
      setVoiceModeState(pending);
    }
    // SPA nav hooks — pause mic during route change; announce after page-voice.
    _pauseVoiceForNavigation = () => {
      if (!voiceConversationRef.current) return;
      clearReListenTimer();
      voiceTalkRef.current?.abort();
      setVoiceTalkState("idle");
      // Protect only SESSION (SB answer / turn continuity) speech across SPA nav.
      // Nav monologues MUST cut so the new page can speak (bf7c7e7e — never on
      // main until now). conversationOwnsVoice() wrongly treated ANY active
      // audio as protected, so engaged_nav kept talking through route changes.
      if (isSessionSpeechActive()) {
        /* keep SB answer playing */
      } else {
        stopAgentSpeech();
      }
      pendingNavPillRef.current = null;
      pendingScrollAnnounceRef.current = null;
      // Keep voiceConversationRef true — session survives navigation.
      if (classicVoiceRef.current) setVoiceHint("Page changing…");
    };
    // Fadi's rule (QA 2026-07-22): an ACTIVE conversation is never interrupted
    // by page narration. Navigating mid-thread keeps the thread — the page may
    // introduce itself only once the conversation has been quiet for a while.
    // Skipping here also skips the mp3 fetch → an unfetched reservation is
    // never generated or billed.
    const ACTIVE_CONVO_QUIET_MS = 60_000;
    /**
     * True when a live CHAT thread should block page monologues.
     * Must NOT include "mic open" — after a monologue we open a 5s listen
     * window; treating that as ownership caused SPA nav voice:play to be
     * dropped (telemetry 8a854121: pages 3–4 minted navigation_voice but
     * zero voice_host_said / no playback).
     */
    const conversationOwnsVoice = (): boolean => {
      if (thinkingRef.current || speakNextAgentReplyRef.current) return true;
      // Only SESSION answer audio owns the voice absolutely — not nav monologues.
      if (isSessionSpeechActive()) return true;
      const msgs = messagesRef.current;
      const last = msgs.length > 0 ? msgs[msgs.length - 1]! : null;
      return !!last && Date.now() - last.createdAt < ACTIVE_CONVO_QUIET_MS;
    };
    // Server-authoritative nav voice: the server decided cold(monologue)/
    // warm(reservation) and gave us the mp3 URL. Play it (the client no longer
    // synthesizes its own nav monologue); chain into click-to-talk when done.
    _playNavVoice = (mp3Url: string) => {
      // Classic AI Voice only — tap-to-talk drops nav monologue frames.
      if (!classicVoiceRef.current) return;
      if (!voiceAssistRef.current) return;
      if (!voiceConversationRef.current) {
        // No voice session yet: browsers forbid unsolicited audio anyway, so
        // STASH the canned monologue for this path — the cold-open tap plays
        // it directly instead of minting a host-line reservation.
        const p =
          typeof window !== "undefined"
            ? window.location.pathname.replace(/\/$/, "") || "/"
            : "/";
        pendingNavVoiceUrlRef.current = { path: p, url: mp3Url };
        return;
      }
      if (isWhisperMuted()) return;
      // Only a live SB answer may block the next page monologue. An open mic
      // (post-host listen) must yield — abort and play the new page.
      if (isSessionSpeechActive()) {
        try {
          console.log("[BlueHat voice] playNavVoice SKIP: session answer playing");
        } catch {
          /* ignore */
        }
        return;
      }
      clearReListenTimer();
      voiceTalkRef.current?.abort();
      voiceTalkRef.current = null;
      setVoiceTalkState("idle");
      stopAgentSpeech();
      const chain = ++voiceChainRef.current;
      setVoiceHint("New page overview…");
      unlockAudioPlayback();
      speakNavVoiceUrl(mp3Url, () => {
        if (chain !== voiceChainRef.current) return;
        if (!voiceConversationRef.current || isWhisperMuted()) return;
        if (thinkingRef.current || speakNextAgentReplyRef.current) return;
        startClickToTalkRef.current({ skipBridge: true });
      }, "nav");
    };
    // Server-assembled host line arrived: play it and run the requesting
    // trigger's stashed continuation (listen window) when it finishes. The
    // onDone carries the site's own chain/mute guards.
    _playHostVoice = (intentId: string, mp3Url: string) => {
      const resolved = _resolveHostIntent(intentId);
      if (!resolved) return; // unknown / already-resolved (timed out) intent
      // Nav monologues must not start over a live session answer — only session.
      if (resolved.role === "nav" && isSessionSpeechActive()) {
        try {
          resolved.onDone();
        } catch {
          /* ignore */
        }
        return;
      }
      // Host nav/session line replaces any open listen mic.
      if (resolved.role === "nav") {
        clearReListenTimer();
        voiceTalkRef.current?.abort();
        voiceTalkRef.current = null;
        setVoiceTalkState("idle");
      }
      unlockAudioPlayback();
      speakNavVoiceUrl(mp3Url, resolved.onDone, resolved.role);
    };
    /**
     * While AI Voice is on: SPA page-voice load + debounced section scroll.
     * - Not engaged: cold page intro on new URL only.
     * - Engaged: scenario 2 continuity — nav (page) + scroll (section match).
     * - Section scroll NEVER cuts mid-sentence: queues until speech/listen
     *   idle (pendingScrollAnnounceRef is that queue — do not "simplify" it
     *   into an immediate announce).
     */
    _announcePageForVoice = async (view) => {
      // Classic AI Voice only — the narration engine is dormant otherwise.
      if (!classicVoiceRef.current) return;
      if (!voiceAssistRef.current || !voiceConversationRef.current) return;
      if (isWhisperMuted()) return;
      // Mid-conversation navigation: the thread owns the voice — no page
      // narration, no nav offer, no hint churn. Pills/bars still update via
      // the server-bar path; only the AUDIO yields to the conversation. BUT
      // the mic must follow the visitor across pages: they may answer the
      // last question on the page they just landed on (QA 2026-07-22: said
      // "paid" right after navigating; the listen had gone silent-armed and
      // nothing was hearing). Re-open ONE quiet listen per path change.
      if (conversationOwnsVoice()) {
        // Mid-chat thread: no page monologue. Still re-arm mic on a new path
        // once the session answer is done (mic must follow the visitor).
        const p =
          typeof window !== "undefined" ? window.location.pathname : "";
        if (
          p &&
          convoListenPathRef.current !== p &&
          !voiceTalkRef.current &&
          !thinkingRef.current &&
          !speakNextAgentReplyRef.current &&
          // NEVER cut a playing SB answer to open the mic.
          !isSessionSpeechActive()
        ) {
          convoListenPathRef.current = p;
          setVoiceHint("Still listening — ask me anything…");
          startClickToTalkRef.current({ skipBridge: true });
        }
        return;
      }
      // Don't interrupt an in-flight Sales Brain turn.
      if (thinkingRef.current || speakNextAgentReplyRef.current) return;
      if (!voiceConversationRef.current) return;

      const hasMessages = messagesRef.current.some(
        (m) =>
          (m.sender === "client" || m.sender === "agent") &&
          m.text.trim().length > 0 &&
          !m.answerChips &&
          !m.chip,
      );
      const focus = readConversationFocus();
      // Engaged = chat history or stored focus (closed panel still continuous).
      const engaged = hasMessages || !!focus;

      const topic = pageTopic();
      const path =
        typeof window !== "undefined"
          ? `${window.location.pathname}${window.location.search}`
          : "";
      // Session path journey: 2+ distinct pages → mini LLM for logical pills.
      // First-land monologue / phase-2 matchesFocus paths unchanged otherwise.
      const journey = recordPathLand(
        typeof window !== "undefined" ? window.location.pathname : path,
      );
      const journeySerious = isPathJourneySerious(journey);
      const pathStack = journeySerious
        ? pathStackForRank(journey)
        : undefined;
      const sectionKey = (view?.currentHeading ?? "").trim().toLowerCase();
      const sectionId =
        typeof view?.sectionIndex === "number"
          ? `i${view.sectionIndex}`
          : sectionKey || "page";
      const announceKey = `${path}::${sectionId}`;
      if (lastNavAnnounceKeyRef.current === announceKey) return;

      const prevKey = lastNavAnnounceKeyRef.current;
      const isNewPage = !prevKey || !prevKey.startsWith(`${path}::`);

      const sectionQs = view?.currentQuestions ?? [];
      const nextQs = view?.nextQuestions ?? [];
      const pagePool = pageQuestionPoolRef.current;
      const sectionInventory = [...sectionQs, ...nextQs];
      const pageInventory = [
        ...sectionInventory,
        ...pagePool,
        ...(view?.openWithQuestions ?? []),
      ];

      // Normalize path for revisit memory (home ↔ pricing must not re-read monologue).
      const pathKey =
        (typeof window !== "undefined"
          ? window.location.pathname
          : path
        ).replace(/\/$/, "") || "/";
      const alreadyIntroduced = spokenPageIntroPathsRef.current.has(pathKey);

      // Shared candidate pool + rank context for mini LLM (inventory only).
      const buildRankCandidates = (
        rankedSec: string[],
        rankedPg: string[],
      ): string[] =>
        // Asked questions are hard-excluded BEFORE the rank call — the LLM
        // must never even see a question the visitor already used (chosen
        // pills stay gone; Fadi's rule, QA 2026-07-22).
        filterUnaskedPageQuestions(
          [...rankedSec, ...rankedPg, ...sectionQs, ...pagePool]
            .map((q) => q.replace(/\s+/g, " ").trim())
            .filter((q, i, a) => q.length >= 6 && a.indexOf(q) === i),
          12,
        );
      const rankContext = {
        ...(focus
          ? {
              lastUser: focus.lastUser,
              lastAgentPreview: focus.lastAgentPreview,
              topicLabel: focus.topicLabel,
              nextAskHints: focus.nextAskHints,
            }
          : {}),
        ...(pathStack && pathStack.length >= PATH_JOURNEY_LLM_THRESHOLD
          ? { pathStack }
          : {}),
      };

      // ── Not engaged: page intro on NEW URL only (scenario 1 land) ────
      if (!engaged) {
        if (!isNewPage) return;
        // Revisit: logical pill + stop-talking cue (no monologue re-read).
        // First land: monologue unchanged. After 2+ pages: mini LLM ranks pills.
        if (alreadyIntroduced) {
          const rankedSection = rankQuestionsByFocus(sectionQs, focus);
          const rankedPage = rankQuestionsByFocus(pagePool, focus);
          // Shared next-asks: prefer server-bar list (already metered) → no extra rank.
          // Else one rank max:3 (same spend-gated endpoint) so Suggested = voice #1.
          const existingShared = rankedNextAsksRef.current;
          let rankedFromApi: string[] | null = null;
          let apiPick: string | null = null;
          let rankSource = "lexical_local";
          const lexicalPick =
            pickNavHostPill(rankedSection, rankedPage) ??
            pickNavHostPill(sectionQs, pagePool);
          // Serious journey + no server-bar list yet: one max:3 rank (inventory only).
          if (journeySerious && existingShared.length === 0) {
            const candidates = buildRankCandidates(rankedSection, rankedPage);
            const apiOrigin = getTtsApiOrigin();
            if (apiOrigin && candidates.length >= 2) {
              try {
                setVoiceHint("Finding a good next question…");
                const ranked = await fetchRankedPageQuestions({
                  apiOrigin,
                  url:
                    typeof window !== "undefined"
                      ? window.location.href
                      : path,
                  candidates,
                  max: MAX_PAGE_PILLS,
                  context: rankContext,
                });
                if (
                  !voiceConversationRef.current ||
                  isWhisperMuted() ||
                  thinkingRef.current ||
                  speakNextAgentReplyRef.current
                ) {
                  return;
                }
                if (ranked?.ranked?.length) {
                  rankedFromApi = ranked.ranked;
                  apiPick = ranked.pick;
                  rankSource = ranked.source;
                } else if (ranked?.pick) {
                  rankedFromApi = [ranked.pick];
                  apiPick = ranked.pick;
                  rankSource = ranked.source;
                }
              } catch {
                /* lexical pill already set */
              }
            }
          }
          const sharedAsks = buildSharedNextAsks({
            existing: existingShared,
            rankedFromApi,
            pick: apiPick ?? lexicalPick,
            lexicalPools: [rankedSection, rankedPage, sectionQs, pagePool],
          });
          const revisitPill = sharedAsks[0] ?? lexicalPick;
          if (sharedAsks.length > 0) {
            paintRankedNextAsks(sharedAsks, "bar_rest");
          }
          lastNavAnnounceKeyRef.current = `${path}::intro`;
          pendingNavPillRef.current = revisitPill;
          clearReListenTimer();
          voiceTalkRef.current?.abort();
          setVoiceTalkState("idle");
          stopAgentSpeech();
          const chain = ++voiceChainRef.current;
          setVoiceHint("Back on this page…");
          emitVoiceEvent({
            event: "voice_host_said",
            kind: "revisit",
            // Text is server-assembled (nav mp3 via _playNavVoice); record the
            // offered pill as the analytics signal.
            said: revisitPill ?? "(server host line)",
            pill: revisitPill ?? undefined,
            rankSource,
          });
          // Speech is server-authoritative: the nav mp3 arrives via _playNavVoice
          // (voice:play). The client no longer synthesizes its own monologue.
          void chain;
          return;
        }

        spokenPageIntroPathsRef.current.add(pathKey);
        lastNavAnnounceKeyRef.current = `${path}::intro`;
        pendingNavPillRef.current = null;
        clearReListenTimer();
        voiceTalkRef.current?.abort();
        setVoiceTalkState("idle");
        stopAgentSpeech();
        const chain = ++voiceChainRef.current;
        setVoiceHint("New page overview…");
        emitVoiceEvent({
          event: "voice_host_said",
          kind: "cold_open",
          // Text is server-assembled (nav mp3 via _playNavVoice).
          said: "(server host line)",
        });
        // Speech is server-authoritative: the nav mp3 arrives via _playNavVoice
        // (voice:play). The client no longer synthesizes its own monologue.
        void chain;
        return;
      }

      // ── Scenario 2: engaged — PAGE navigation only (for now) ───────
      // Scroll continuity is off: intrusive with TTS (stop/start). Text
      // sidebar/hero can own section continuity later without audio thrash.
      if (!isNewPage) {
        // Still mark section keys so we don't thrash if re-enabled later.
        lastNavAnnounceKeyRef.current = announceKey;
        return;
      }

      const matchInventory = pageInventory;
      // Inventory = strong continuity. Page path/title = weaker (no monologue).
      const inventoryMatch =
        !!focus && focusMatchesInventory(matchInventory, focus);
      const pageMatch =
        !!focus &&
        focusMatchesPageContext(focus, {
          pageTopic: topic,
          path,
          inventory: matchInventory,
        });
      const matchesFocus = inventoryMatch || pageMatch;
      // Speak "Related to {topic}" only when THIS page's inventory actually
      // continues the topic (e.g. usage/wallets tokens) — not every page that
      // merely says the site brand. Fixes sticky "Related to how usage wallets
      // works" on every SPA land (telemetry: matchesFocus true + same topic).
      const rawTopic = focus?.topicLabel ?? null;
      const topicOnThisPage = topicMatchesInventory(
        rawTopic,
        matchInventory,
      );
      const continuityTopic =
        topicOnThisPage && isSpeakableContinuityTopic(rawTopic)
          ? rawTopic
          : null;

      // Lexical baseline (always available; used for unrelated + LLM fallback).
      const rankedSection = rankQuestionsByFocus(sectionQs, focus);
      const rankedPage = rankQuestionsByFocus(pagePool, focus);
      // Shared next-asks: prefer server-bar list (already metered) → zero extra rank.
      // Else one rank max:3 so Suggested pills === voice offer (#1 spoken only).
      const existingShared = rankedNextAsksRef.current;
      let rankedFromApi: string[] | null = null;
      let apiPick: string | null = null;
      let rankSource = "lexical_local";
      const lexicalPick =
        pickNavHostPill(rankedSection, rankedPage) ??
        pickNavHostPill(sectionQs, pagePool);

      // Phase 2: related page → mini LLM. Also after 2+ distinct pages
      // (serious journey) even if focus doesn't match this inventory —
      // path stack + thread steer inventory-only pick. First monologue untouched.
      // Skip network when server bar already seeded the shared list (token-safe).
      const useLlmRank =
        existingShared.length === 0 &&
        ((matchesFocus && !!focus) || journeySerious);
      if (useLlmRank) {
        const candidates = buildRankCandidates(rankedSection, rankedPage);
        const apiOrigin = getTtsApiOrigin();
        if (apiOrigin && candidates.length >= 2) {
          try {
            setVoiceHint("Finding a good next question…");
            const ranked = await fetchRankedPageQuestions({
              apiOrigin,
              url:
                typeof window !== "undefined" ? window.location.href : path,
              candidates,
              max: MAX_PAGE_PILLS,
              context: rankContext,
            });
            if (
              !voiceConversationRef.current ||
              isWhisperMuted() ||
              thinkingRef.current ||
              speakNextAgentReplyRef.current
            ) {
              return;
            }
            // Another land may have started while we waited.
            if (lastNavAnnounceKeyRef.current === announceKey) {
              // same key not yet committed — ok
            }
            if (ranked?.ranked?.length) {
              rankedFromApi = ranked.ranked;
              apiPick = ranked.pick;
              rankSource = ranked.source;
            } else if (ranked?.pick) {
              rankedFromApi = [ranked.pick];
              apiPick = ranked.pick;
              rankSource = ranked.source;
            }
          } catch {
            /* lexical pill already set */
          }
        }
      }
      const sharedAsks = buildSharedNextAsks({
        existing: existingShared,
        rankedFromApi,
        pick: apiPick ?? lexicalPick,
        lexicalPools: [rankedSection, rankedPage, sectionQs, pagePool],
      });
      const pill = sharedAsks[0] ?? lexicalPick;
      if (sharedAsks.length > 0) {
        paintRankedNextAsks(sharedAsks, "bar_rest");
      }

      // 1st engaged nav → meta on; 2nd skip; 3rd on; …
      engagedNavCountRef.current += 1;
      const navIndex = engagedNavCountRef.current;
      const includeMeta = shouldIncludeEngagedNavMeta(navIndex);
      // Path monologue only on FIRST land this session — revisits get pill only
      // (home→price→home→price was re-reading the same $799 block every time).
      const engagedPathKey =
        (typeof window !== "undefined"
          ? window.location.pathname
          : path
        ).replace(/\/$/, "") || "/";
      const engagedRevisit =
        spokenPageIntroPathsRef.current.has(engagedPathKey);
      const pageSummary = engagedRevisit
        ? []
        : (pageVoiceMetaRef.current.heroMonologue ?? []);
      if (!engagedRevisit && pageSummary.length > 0) {
        spokenPageIntroPathsRef.current.add(engagedPathKey);
      }
      // Speech is server-assembled: the client offers the ranked on-screen pill
      // as context; the server frames the continuity sentence around it. pageSummary
      // is server-side too (page-voice); the client no longer composes the words.
      void pageSummary;
      void engagedRevisit;
      lastNavAnnounceKeyRef.current = announceKey;
      pendingNavPillRef.current = pill;
      pendingScrollAnnounceRef.current = null;

      clearReListenTimer();
      voiceTalkRef.current?.abort();
      setVoiceTalkState("idle");
      // Page land: replace prior host audio (route change already paused).
      stopAgentSpeech();

      const chain = ++voiceChainRef.current;
      setVoiceHint(
        continuityTopic
          ? "Continuing your conversation…"
          : "Next question…",
      );
      try {
        console.log("[BlueHat voice] scenario2 continuity", {
          mode: "nav",
          inventoryMatch,
          pageMatch,
          matchesFocus,
          journeySerious,
          pathN: journey.paths.length,
          pathStack: pathStack?.slice(-6),
          rawTopic,
          spokenTopic: continuityTopic,
          rankSource,
          pill: pill?.slice(0, 80) ?? null,
          pageTopic: topic,
          path,
          navIndex,
          includeMeta,
        });
      } catch {
        /* ignore */
      }
      emitVoiceEvent({
        event: "voice_host_said",
        kind: "engaged_nav",
        // Text is server-assembled; the client records the offered pill as the
        // signal for lift analysis (it no longer knows the exact spoken words).
        said: pill ?? "(server host line)",
        pill: pill ?? undefined,
        rankSource,
        matchesFocus,
        navIndex,
        includeMeta,
      });
      unlockAudioPlayback();
      const flushPendingScrollAnnounce = () => {
        const pending = pendingScrollAnnounceRef.current;
        if (!pending) return;
        if (isAgentSpeechActive() || voiceTalkRef.current != null) return;
        pendingScrollAnnounceRef.current = null;
        // Defer a tick so speech idle settles before next host line.
        window.setTimeout(() => {
          if (!voiceConversationRef.current || isWhisperMuted()) return;
          if (isAgentSpeechActive() || voiceTalkRef.current != null) return;
          announcePageForVoiceRef.current?.(pending);
        }, 200);
      };

      requestHostVoice({ trigger: "engaged_nav", panelOpen: openRef.current, pill: pill ?? undefined }, () => {
        if (chain !== voiceChainRef.current) return;
        if (!voiceConversationRef.current || isWhisperMuted()) return;
        if (thinkingRef.current || speakNextAgentReplyRef.current) return;

        // ── 10s push-to-talk: yes / question / silence ───────────────
        // Silence: stay voice-on, quiet (browse continues the session).
        const finishNavListen = (modeListen: "timeout" | "no" | "abort") => {
          setVoiceDraft(null);
          if (chain !== voiceChainRef.current) return;
          clearReListenTimer();
          voiceTalkRef.current?.abort();
          voiceTalkRef.current = null;
          setVoiceTalkState("idle");
          const offered = pendingNavPillRef.current;
          pendingNavPillRef.current = null;
          // Offered aloud → mark asked so it drops from shared Suggested (no re-nag).
          // Remaining ranked siblings stay for tap; text/voice still march.
          if (offered && (modeListen === "timeout" || modeListen === "no")) {
            markAsked(offered);
            if (rankedNextAsksRef.current.length > 0) {
              paintRankedNextAsks(rankedNextAsksRef.current, "bar_rest");
            } else if (!shouldSuppressPagePills(messagesRef.current)) {
              showPagePills("bar_rest");
            }
          }
          emitVoiceEvent({
            event: "voice_nav_listen",
            outcome: modeListen,
            pill: offered ?? undefined,
          });
          if (modeListen === "timeout" || modeListen === "no") {
            setVoiceHint(
              modeListen === "no"
                ? "No problem — keep browsing. Tap AI Voice anytime."
                : "Still with you — scroll, pick a question, or ask.",
            );
          }
          flushPendingScrollAnnounce();
        };

        const acceptOfferedPill = () => {
          const q = pendingNavPillRef.current;
          pendingNavPillRef.current = null;
          clearReListenTimer();
          voiceTalkRef.current?.abort();
          setVoiceTalkState("idle");
          if (!q) return;
          emitVoiceEvent({
            event: "voice_nav_listen",
            outcome: "yes",
            pill: q,
          });
          openChat();
          setVoiceHint("Answering…");
          // flushOrSend markAsked — same path as tapping the chip.
          flushOrSendRef.current(q);
        };

        const acceptSpoken = (t: string) => {
          const offered = pendingNavPillRef.current;
          pendingNavPillRef.current = null;
          clearReListenTimer();
          voiceTalkRef.current?.abort();
          setVoiceTalkState("idle");
          // Different spoken ask → still retire the offered pill (was heard aloud).
          if (offered) markAsked(offered);
          emitVoiceEvent({
            event: "voice_nav_listen",
            outcome: "question",
            pill: offered ?? undefined,
          });
          openChat();
          setVoiceHint("Answering…");
          flushOrSendRef.current(t);
        };

        if (!isSttSupported()) {
          setTerminalVoiceHint("Tap a question on screen if you want that answer.");
          return;
        }

        stopAgentSpeech();
        voiceTalkRef.current?.abort();
        // One silent grace re-listen on an empty timeout. Visitors often answer
        // DURING the spoken line (mic not open yet — QA 2026-07-22: 24s nav
        // utterance, answer lost, window timed out untouched). The retry is the
        // human "sorry — say that again?" beat without any TTS: one extra STT
        // window (~fractions of a cent), retried-once guard, never loops.
        let navListenRetried = false;
        const startNavListenWindow = (windowMs: number) => {
          // End-of-speech often never fires for short utterances ("yes" in a
          // noisy room) — the recognizer keeps waiting while the words sit as
          // an interim. The hard timer INHERITS the interim: what the visitor
          // saw typed in the bar is what gets submitted (QA 2026-07-22).
          let lastInterim = "";
          let flushTried = false;
          const processNavFinal = (raw: string): void => {
            // A (possibly late, flushed) final closes this listen window —
            // cancel any pending hard/grace timer so it cannot double-fire.
            if (navListenHardTimerRef.current != null) {
              clearTimeout(navListenHardTimerRef.current);
              navListenHardTimerRef.current = null;
            }
            const t = raw.trim();
            if (!t) return;
            if (isStopTalkingCommand(t)) {
              endVoiceSessionRef.current("AI Voice off.");
              return;
            }
            if (isBookDemoCommand(t)) {
              clearReListenTimer();
              voiceTalkRef.current?.abort();
              voiceTalkRef.current = null;
              setVoiceTalkState("idle");
              setVoiceDraft(null);
              if (launchBookDemoRef.current?.()) {
                setVoiceHint("Opening demo…");
              } else {
                setVoiceHint("Demo isn’t available — ask a question…");
              }
              return;
            }
            if (isNavNoCommand(t)) {
              finishNavListen("no");
              return;
            }
            if (pill && isNavYesCommand(t)) {
              clearReListenTimer();
              acceptOfferedPill();
              return;
            }
            if (isLikelyVoiceGarbageTranscript(t)) {
              setVoiceHint("Didn’t catch that — say yes, or ask a question…");
              return;
            }
            clearReListenTimer();
            setVoiceDraft(null);
            acceptSpoken(t);
          };
          sttTranscribingRef.current = false;
          sttTranscribeWaitRef.current = 0;
          const talk = createSttVoiceTalk({
            onTranscribing: (a) => { sttTranscribingRef.current = a; },
      onLevel: setMicLevel,
            silenceMs: 1600,
            onState: (s) => {
              setVoiceTalkState(s);
              if (s === "listening") {
                setVoiceHint(
                  pill
                    ? "Say yes, tap the question, or ask your own…"
                    : "Ask me, or tap a question on the side…",
                );
              }
            },
            onInterim: (t) => {
              if (!t) return;
              lastInterim = t;
              const short = t.length > 40 ? `${t.slice(0, 38).trim()}…` : t;
              setVoiceHint(`Hearing: “${short}”`);
              setVoiceDraft(t);
            },
            onFinal: (transcript) => {
              if (chain !== voiceChainRef.current) return;
              processNavFinal(transcript);
            },
          });
          voiceTalkRef.current = talk;
          talk.start();

          navListenHardTimerRef.current = setTimeout(function onNavListenTimeout() {
            navListenHardTimerRef.current = null;
            if (chain !== voiceChainRef.current) return;
            // See the push-to-talk timeout: never abort captured-but-untranscribed
            // speech.
            if (sttTranscribingRef.current && sttTranscribeWaitRef.current < STT_TRANSCRIBE_MAX_WAIT_MS) {
              sttTranscribeWaitRef.current += STT_TRANSCRIBE_POLL_MS;
              navListenHardTimerRef.current = setTimeout(onNavListenTimeout, STT_TRANSCRIBE_POLL_MS);
              return;
            }
            // Words were heard but never finalized → submit them (same path a
            // real final takes). The visitor watched them type; honor them.
            // Prefer last interim; also honor whatever is still typed in the
            // bar — whisper interims are never words, so chip answers that
            // never finalized left draft-only and never sent.
            const fromBar = (voiceDraftRef.current ?? "").trim();
            const inherited = (lastInterim.trim() || fromBar).trim();
            if (inherited && !isLikelyVoiceGarbageTranscript(inherited)) {
              voiceTalkRef.current?.abort();
              voiceTalkRef.current = null;
              setVoiceTalkState("idle");
              processNavFinal(inherited);
              return;
            }
            if (fromBar && isChipStyleVoiceAnswer(fromBar)) {
              voiceTalkRef.current?.abort();
              voiceTalkRef.current = null;
              setVoiceTalkState("idle");
              processNavFinal(fromBar);
              return;
            }
            // FLUSH before killing (#397 whisper regression): whisper
            // transcribes ASYNC after speech ends, and abort() here discarded
            // both the in-flight transcription and any buffered utterance — a
            // spoken answer near the window edge vanished silently. stop()
            // flushes: buffered speech transcribes and the late onFinal routes
            // through processNavFinal. One grace re-arm; nothing landing →
            // exactly the old path (retry once, then finish).
            if (!flushTried && voiceTalkRef.current) {
              flushTried = true;
              voiceTalkRef.current.stop();
              navListenHardTimerRef.current = setTimeout(onNavListenTimeout, STT_FLUSH_GRACE_MS);
              return;
            }
            if (!navListenRetried) {
              navListenRetried = true;
              setVoiceHint("Didn’t catch that — I’m listening now…");
              voiceTalkRef.current?.abort();
              // Grace re-listen ~same order as primary window (5s cut).
              startNavListenWindow(4_000);
              return;
            }
            finishNavListen("timeout");
          }, windowMs);
        };
        startNavListenWindow(NAV_HOST_LISTEN_MS);
      });
    };
    announcePageForVoiceRef.current = (v) => _announcePageForVoice?.(v);
    // When speech ends with no listen window (e.g. unsupported mic), flush queue.
    setOnAgentSpeechIdle(() => {
      if (!voiceConversationRef.current) return;
      if (voiceTalkRef.current != null) return;
      const pending = pendingScrollAnnounceRef.current;
      if (!pending) return;
      pendingScrollAnnounceRef.current = null;
      window.setTimeout(() => {
        if (!voiceConversationRef.current || isWhisperMuted()) return;
        if (isAgentSpeechActive() || voiceTalkRef.current != null) return;
        announcePageForVoiceRef.current?.(pending);
      }, 200);
    });
    _setStickyBarVisible = (v) => {
      setStickyBarVisibleState((prev) => {
        if (prev !== v) {
          callbacks.onBarEvent?.({ event: v ? "sticky_bar_shown" : "sticky_bar_hidden" });
        }
        return v;
      });
    };
    _setVisible = (v) => setVisibleState(v);
    _setBanner = (b) => setBannerState(b);
    _setDisplay = (d) => setDisplayState(d);
    _setPrimaryCta = (c) => setPrimaryCtaState(c);
    _refreshTopic = () => setTopicTick((t) => t + 1);
    _setBottomBar = (b) => setBottomBarState(b);
    if (_pendingStickyBarVisible !== undefined) {
      const pendingSticky = _pendingStickyBarVisible;
      _pendingStickyBarVisible = undefined;
      _setStickyBarVisible?.(pendingSticky);
    }
    _setBarSuppressed = (v) => setBarSuppressedState(v);
    _setLowerBarModeAllows = (v) => setLowerBarModeAllowsState(v);
    if (_pendingLowerBarModeAllows !== undefined) {
      setLowerBarModeAllowsState(_pendingLowerBarModeAllows);
      _pendingLowerBarModeAllows = undefined;
    }
    _setBarNudgesOnly = (v) => setBarNudgesOnlyState(v);
    if (_pendingBarNudgesOnly !== undefined) {
      setBarNudgesOnlyState(_pendingBarNudgesOnly);
      _pendingBarNudgesOnly = undefined;
    }
    if (_pendingBarSuppressed !== undefined) {
      const pending = _pendingBarSuppressed;
      _pendingBarSuppressed = undefined;
      setBarSuppressedState(pending);
    }
    return () => {
      _setMessages = null;
      _addMessage = null;
      _updateMessage = null;
      _setThinking = null;
      _addCta = null;
      _addForm = null;
      _addChip = null;
      _addAnswerChips = null;
      _openChat = null;
      _openWithText = null;
      _openWithSectionQuestions = null;
      _setBarLines = null;
      _setPageQuestionPool = null;
      _setRankedNextAsks = null;
      _setPageVoiceMeta = null;
      _setRailContinuityLead = null;
      _setSectionBarView = null;
      _setLivingBarSkin = null;
      _setInlineMode = null;
      _setInlineCfgLanded = null;
      _setInlineCloseAllowed = null;
      _setSectionDataLanded = null;
      _setVoiceMode = null;
      _pauseVoiceForNavigation = null;
      _announcePageForVoice = null;
      _playNavVoice = null;
      _playHostVoice = null;
      _setStickyBarVisible = null;
      _setVisible = null;
      _setBanner = null;
      _setDisplay = null;
      _setPrimaryCta = null;
      _refreshTopic = null;
      _setBottomBar = null;
      _setBarSuppressed = null;
      _setLowerBarModeAllows = null;
      _setBarNudgesOnly = null;
      voiceTalkRef.current?.abort();
      voiceTalkRef.current = null;
    };
    // showPagePills / markAsked / paintRankedNextAsks via refs; re-bind when identities change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPagePills, markAsked, paintRankedNextAsks]);

  // Inline "open" mode: the chat is open by design, so connect the WS as
  // soon as the mode is known — there's no user click to trigger it. In
  // "expand" mode the WS connects only through openChat()'s closed→open
  // transition, exactly like the floating bar (#592).
  useEffect(() => {
    if (!inline) return;
    // "open" is open by configuration — the ONLY inline mode that self-opens.
    // Agent no longer resumes here (the returning-visitor ambush, reversed
    // 2026-08-26): an engaged session shows the continue affordance instead.
    const shouldOpen = inlineMode === "open";
    if (!shouldOpen) return;
    setMinimized(false);
    setOpen(true);
    if (!inlineOpenedRef.current) {
      inlineOpenedRef.current = true;
      callbacks.onOpen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inline, inlineMode]);

  // #592: reflect the inline state onto the light-DOM host so site designers
  // can style both states from page CSS, and apply/clear the unsized-
  // container height assist measured in openChat.
  useEffect(() => {
    const host = _hostEl;
    if (!host) return;
    if (!inline || inlineMode == null) {
      host.removeAttribute("data-bhai-inline-state");
      return;
    }
    const expanded = inlineMode === "open" || open;
    host.setAttribute("data-bhai-inline-state", expanded ? "expanded" : "collapsed");
    // Questions-only containers hug their pills exactly as an expand bar
    // hugs its capsule, so they need the same working height to open into.
    if (inlineMode === "expand" || inlineMode === "nudges") {
      if (open && inlineExpandHeightRef.current) {
        host.style.height = inlineExpandHeightRef.current;
      } else if (!open && host.style.height) {
        host.style.height = "";
      }
    }
  }, [inline, inlineMode, open]);

  // Reopened from a prior page (persisted flag): fire onOpen once so the WS
  // connects and the server replays history via chat:init — the same path a click
  // takes. The panel is already seeded open above, so there's no closed→open flash.
  // Also surface THIS page's question pills under the restored thread.
  useEffect(() => {
    if (reopen) {
      callbacks.onOpen();
      showPagePills("bar_rest");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reopen]);

  // When bar lines update for a new page while the panel is open (SPA or full
  // navigation with chat kept open), refresh pills so they match the page the
  // visitor is on — without wiping answer chips / CTA / rich-lead chrome.
  useEffect(() => {
    if (!open || thinking) return;
    if (shouldSuppressPagePills(messagesRef.current)) return;
    showPagePills(pagePillsSourceRef.current);
  }, [barLines, open, thinking, showPagePills]);

  // Escape closes the stage (side-panel only) — same path as the ✕ / scrim.
  useEffect(() => {
    if ((inline && inlineMode !== "expand" && inlineMode !== "nudges") || !open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") minimize();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inline, inlineMode, open]);

  /**
   * Any session holding a conversation is an ENGAGED one, and engaged sessions
   * come back open. Keyed on messages rather than on the visitor's open
   * intent, because the intent flag is per tab and the conversation is not —
   * and because it catches history the SERVER replayed, so a session that
   * predates this flag records itself the first time it is opened.
   */
  useEffect(() => {
    if (messages.length > 0) markConversationEngaged();
  }, [messages.length]);

  // Persist the open intent for the browsing session (FLOATING only) so a full
  // page navigation re-opens the chat on the destination page.
  //
  // Inline deliberately does not write it. This flag is read by every surface,
  // so an inline container setting it made the floating bottom bar open itself
  // on every other page of the site — the container's state leaking onto pages
  // that never had one. Agent mode's own resume is the sid-scoped engaged flag,
  // which only agent mode reads.
  useEffect(() => {
    if (!inline) setChatOpenFlag(open);
  }, [inline, open]);

  // Mobile behavior while the panel is open:
  // - mark the light-DOM host with .bhai-chat-open (a styling hook for host pages)
  // - lock body scroll on phones so the page can't scroll under the panel
  // - publish the visual viewport to the host so chat.css can make the open panel
  //   TAKE OVER THE WHOLE SCREEN on phones: --bhai-vvt (pan offset) + --bhai-vvh
  //   (visible height, which shrinks as the keyboard opens). The panel is
  //   position:fixed (sized against the LAYOUT viewport, which iOS does NOT shrink
  //   for the keyboard), so these vars pin it to the VISIBLE area — fullscreen,
  //   composer above the keys, nothing behind it.
  useEffect(() => {
    if (inline) return;
    const panelOpen = open && !minimized;
    const host = _hostEl;
    host?.classList.toggle("bhai-chat-open", panelOpen);
    if (!panelOpen) return;

    const vv = window.visualViewport;
    const syncKb = () => {
      if (!vv || !host) return;
      // Keyboard overlap below the visual viewport, and the viewport's pan
      // offset (iOS pans the visual viewport on input focus) — both in layout-
      // viewport px, so the fixed-positioned panel can stay fully on screen.
      const kb = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      host.style.setProperty("--bhai-kb", `${Math.round(kb)}px`);
      host.style.setProperty("--bhai-vvt", `${Math.round(Math.max(0, vv.offsetTop))}px`);
      // Visible viewport height — the open panel is sized to exactly this on
      // phones so it fills the visible area above the keyboard and takes over the
      // screen; it shrinks/grows live as the keyboard opens/closes.
      host.style.setProperty("--bhai-vvh", `${Math.round(vv.height)}px`);
    };
    syncKb();
    vv?.addEventListener("resize", syncKb);
    vv?.addEventListener("scroll", syncKb);

    // Phone scroll lock. iOS Safari IGNORES overflow:hidden while the keyboard
    // is up (it force-pans/scrolls anyway), so use the position:fixed body
    // technique: freeze the page at its current scroll offset, restore on close.
    let unlock: (() => void) | null = null;
    if (window.matchMedia("(max-width: 640px)").matches) {
      const b = document.body.style;
      const prev = {
        position: b.position, top: b.top, left: b.left,
        right: b.right, width: b.width, overflow: b.overflow,
      };
      const scrollY = window.scrollY;
      b.position = "fixed";
      b.top = `-${scrollY}px`;
      b.left = "0";
      b.right = "0";
      b.width = "100%";
      b.overflow = "hidden";
      unlock = () => {
        b.position = prev.position; b.top = prev.top; b.left = prev.left;
        b.right = prev.right; b.width = prev.width; b.overflow = prev.overflow;
        window.scrollTo(0, scrollY);
      };
    }
    return () => {
      vv?.removeEventListener("resize", syncKb);
      vv?.removeEventListener("scroll", syncKb);
      host?.style.removeProperty("--bhai-kb");
      host?.style.removeProperty("--bhai-vvt");
      host?.style.removeProperty("--bhai-vvh");
      unlock?.();
    };
  }, [inline, open, minimized]);

  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      if (next) {
        callbacks.onOpen();
      } else {
        callbacks.onClose();
      }
      return next;
    });
  }, [callbacks]);

  // Typewriter for living bar text.
  useEffect(() => {
    const tw = createTypewriter(() => textRef.current, {
      onRender: (_t, _line, done) => setBarTyping(!done),
      // NO onLineShown reporting. We track what visitors DO, not what we showed
      // them (Brian, 2026-09-28), and an impression on a timer is worth less than
      // nothing: the typewriter cycles for as long as the page stays open, so this
      // posted to /api/events every ~18.5s indefinitely. One session reached
      // 381,097 `bar_line_shown` rows, each recomputing a session score that read
      // the whole accumulated history back — the pod pegged a 1-core limit and a
      // sibling tenant was OOMKilled (2026-09-27). Nothing ever read the event.
      //
      // `bar_line_clicked` still reports, because that is a visitor acting.
    });
    typewriterRef.current = tw;
    tw.setLines(barLinesRef.current);
    return () => {
      tw.stop();
      typewriterRef.current = null;
    };
  }, []);

  useEffect(() => {
    typewriterRef.current?.setLines(barLines);
  }, [barLines]);

  // One mark pulse per session when the bar first appears (tab-visible only).
  useEffect(() => {
    if (typeof document === "undefined") return;
    try {
      if (sessionStorage.getItem(BAR_PULSE_KEY) === "1") return;
    } catch {
      /* ignore */
    }
    const run = () => {
      if (document.visibilityState !== "visible") return;
      setMarkPulse(true);
      try {
        sessionStorage.setItem(BAR_PULSE_KEY, "1");
      } catch {
        /* ignore */
      }
      window.setTimeout(() => setMarkPulse(false), 1200);
    };
    if (document.visibilityState === "visible") run();
    else {
      const onVis = () => {
        if (document.visibilityState === "visible") {
          run();
          document.removeEventListener("visibilitychange", onVis);
        }
      };
      document.addEventListener("visibilitychange", onVis);
      return () => document.removeEventListener("visibilitychange", onVis);
    }
  }, []);

  // Banner / explicit open (never toggles closed — distinct from `toggle`).
  // A CTA click-through is a HANDOFF: the visitor chose the destination.
  // Clear the open-intent flag BEFORE navigation so the chat does not chase
  // them onto it — it reopened over the contact page (bench, 2026-08-13).
  // The panel stays visually open for the ~150ms until the page unloads.
  const handleCtaClick = useCallback(
    (url: string, slug: string, openInNewTab: boolean, source?: "presented" | "panel_header") => {
      setChatOpenFlag(false);
      callbacks.onClickCta(url, slug, openInNewTab, source);
    },
    [callbacks],
  );

  const handleProductClick = useCallback(
    (productId: string, url: string) => {
      // Same open-intent clear as handleCtaClick: without it the chat reopens over
      // the product page the visitor just navigated to.
      setChatOpenFlag(false);
      callbacks.onClickProduct?.(productId, url);
    },
    [callbacks],
  );

  const openChat = useCallback(() => {
    // #592 expand assist: measure the blessed div BEFORE the chat commits —
    // a container hugging the collapsed surface is auto-sized, so the
    // expand supplies a working height; a designer-sized container never
    // gets overridden.
    if (inline) {
      // "Is this container auto-sized?" is answered by comparing the container
      // to the CONTENT it holds. For a bar that is the host's own height — the
      // host hugs. Agent mode fills its container by design (it centres inside
      // it), so the host always equals the container and every container
      // looked auto-sized: a 360px container came back 560. Measure the agent
      // surface itself instead, and a designer's height survives the expand.
      const agentEl = _hostEl?.shadowRoot?.querySelector(".bhai-agent") as HTMLElement | null;
      const contentPx = agentEl ? agentEl.scrollHeight : (_hostEl?.offsetHeight ?? 0);
      inlineExpandHeightRef.current = inlineExpandedHostHeight(
        _hostEl?.parentElement?.clientHeight ?? 0,
        contentPx,
      );
    }
    setMinimized(false);
    setBarDismissed(false);
    try {
      sessionStorage.removeItem(BAR_DISMISS_KEY);
    } catch {
      /* ignore */
    }
    setOpen((prev) => {
      if (!prev) {
        callbacks.onOpen();
        callbacks.onBarEvent?.({ event: "panel_opened" });
      }
      return true;
    });
  }, [callbacks]);

  /**
   * Voice "book demo": speak saved CTA handoff (tenant TTS — LLM once, cached)
   * then execute the same onClickCta as the Book a Demo button.
   */
  const launchBookDemo = useCallback((): boolean => {
    type Target = {
      url: string;
      slug: string;
      openInNewTab: boolean;
      source: "presented" | "panel_header";
    };
    let target: Target | null = null;
    const msgs = messagesRef.current;
    for (let i = msgs.length - 1; i >= 0; i--) {
      const c = msgs[i]!.cta;
      if (c?.url) {
        target = {
          url: c.url,
          slug: c.slug ?? "",
          openInNewTab: c.openInNewTab ?? true,
          source: "presented",
        };
        break;
      }
    }
    if (!target && primaryCta?.url) {
      target = {
        url: primaryCta.url,
        slug: primaryCta.slug,
        openInNewTab: primaryCta.openInNewTab,
        source: "panel_header",
      };
    }
    if (!target) return false;

    openChat();
    const run = () => {
      callbacks.onClickCta(
        target!.url,
        target!.slug,
        target!.openInNewTab,
        target!.source,
      );
    };

    // Classic AI Voice: handoff line first (same ash/tenant TTS), then CTA.
    // Tap-to-talk: the CTA just opens — voice only answers.
    if (classicVoiceRef.current && voiceConversationRef.current && !isWhisperMuted()) {
      setVoiceHint("Opening demo…");
      requestHostVoice({ trigger: "cta_handoff", panelOpen: openRef.current }, () => {
        run();
      });
      return true;
    }
    run();
    return true;
  }, [openChat, callbacks, primaryCta, requestHostVoice]);
  launchBookDemoRef.current = launchBookDemo;

  /**
   * Ask from a surface pill (rail / notes / glass / theater).
   * Same Sales Brain path as typed ask. If Click-to-talk already started a
   * guided session, stop the mic and speak the answer back into the loop.
   *
   * After the answer:
   *  - FM / answer chips / CTA / RLH → no page pills (shouldSuppressPagePills)
   *  - dead chrome → up to 3 unasked from the COMING-UP section only
   *    (not this-section rephrases, not flat page pool first)
   */
  const askFromSurface = useCallback(
    (q: string, surfaceKind?: SurfacePillKind) => {
      const payload = q.trim();
      if (!payload || thinkingRef.current) return;

      // Freeze coming-up section inventory at click time for post-answer refill.
      const view = sectionBarViewRef.current;
      const seedRaw =
        view?.comingUpPool && view.comingUpPool.length > 0
          ? view.comingUpPool
          : (view?.nextQuestions ?? []);
      const seed = seedRaw.filter(
        (x): x is string => typeof x === "string" && x.trim().length > 0,
      );
      // Always set (even empty) so we don't accidentally keep a stale pool
      // from a prior page / CTA open.
      sectionPillPoolRef.current = seed;

      const kind: SurfacePillKind =
        surfaceKind ??
        (view?.currentQuestions?.includes(payload)
          ? "now"
          : view?.nextQuestions?.includes(payload)
            ? "next"
            : "other");
      callbacksRef.current.onBarEvent?.({
        event: "surface_pill_clicked",
        skin: livingBarSkin ?? DEFAULT_LIVING_BAR_SKIN,
        kind,
        question: payload.length > 300 ? payload.slice(0, 300) : payload,
      });

      voiceTalkRef.current?.abort();
      setVoiceTalkState("idle");
      armVoiceForNextAnswer(payload); // classic-only inside
      if (classicVoiceRef.current && voiceConversationRef.current) {
        warmSpeechVoices();
        setVoiceHint("Answering…");
      } else if (voiceConversationRef.current) {
        // Tap-to-talk one-shot: a pill click mid-listen commits a turn like a
        // spoken one — the session ends; the answer arrives as text.
        endVoiceSessionRef.current?.();
      }
      openChat();
      markAsked(payload);
      setPagePills(null);
      setThinkingState(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.setTimeout(() => callbacksRef.current.onSend(payload), 50);
        });
      });
    },
    [voiceAssist, openChat, markAsked, armVoiceForNextAnswer, livingBarSkin],
  );

  /**
   * Push-to-talk mic. Hard window starts when STT reports "listening" (not at
   * start() — whisper cold-start was eating the window so FM answers never
   * registered). Post-answer uses a longer window + faster silence commit.
   */
  const startListeningOnly = useCallback(() => {
    if (!voiceAssist) return;
    if (!isSttSupported()) {
      setVoiceTalkState("unsupported");
      setTerminalVoiceHint("Voice isn’t available here — tap AI Voice or type below.");
      return;
    }
    // Never listen while muted / session ended / answer in flight.
    if (isWhisperMuted() || !voiceConversationRef.current) {
      try { console.log("[BlueHat voice] listen refused: conv/mute", { conv: voiceConversationRef.current, muted: isWhisperMuted() }); } catch { /* ignore */ }
      setVoiceTalkState("idle");
      return;
    }
    if (thinkingRef.current || speakNextAgentReplyRef.current) {
      try { console.log("[BlueHat voice] listen refused: armed/thinking", { armed: speakNextAgentReplyRef.current, thinking: thinkingRef.current }); } catch { /* ignore */ }
      setVoiceHint("Still answering — I’ll listen when this reply is done.");
      return;
    }
    const isPostAnswer = postAnswerListenRef.current;
    postAnswerListenRef.current = false;
    const listenMs = isPostAnswer ? POST_ANSWER_LISTEN_MS : PUSH_TO_TALK_LISTEN_MS;
    const silenceMs = isPostAnswer ? POST_ANSWER_SILENCE_MS : 1800;
    try {
      console.log("[BlueHat voice] mic OPEN", {
        isPostAnswer,
        listenMs,
        silenceMs,
      });
    } catch {
      /* ignore */
    }
    stopAgentSpeech();
    voiceTalkRef.current?.abort();
    clearReListenTimer();
    const listenGen = ++pushToTalkListenGenRef.current;
    // Fresh listen → fresh transcription-wait budget (see the timeout guard).
    sttTranscribingRef.current = false;
    sttTranscribeWaitRef.current = 0;
    // Short utterances ("yes") often never finalize — end-of-speech detection
    // waits forever in ambient noise while the words sit as an interim. The
    // hard timer below inherits this so what the visitor watched typing gets
    // submitted instead of silently dropped (QA 2026-07-22).
    let lastPttInterim = "";
    let pttFlushTried = false;
    let hardTimerArmed = false;
    const listenSecLabel = Math.round(listenMs / 1000);

    const armHardTimer = () => {
      if (hardTimerArmed) return;
      if (listenGen !== pushToTalkListenGenRef.current) return;
      hardTimerArmed = true;
      if (navListenHardTimerRef.current != null) {
        clearTimeout(navListenHardTimerRef.current);
        navListenHardTimerRef.current = null;
      }
      navListenHardTimerRef.current = setTimeout(function onPttListenTimeout() {
      navListenHardTimerRef.current = null;
      if (listenGen !== pushToTalkListenGenRef.current) return;
      if (!voiceConversationRef.current) return;
      // Words already spoken are mid-transcription — deferring costs a
      // moment; aborting deletes them. Bounded so a wedged worker still ends
      // the window.
      if (sttTranscribingRef.current && sttTranscribeWaitRef.current < STT_TRANSCRIBE_MAX_WAIT_MS) {
        sttTranscribeWaitRef.current += STT_TRANSCRIBE_POLL_MS;
        navListenHardTimerRef.current = setTimeout(onPttListenTimeout, STT_TRANSCRIBE_POLL_MS);
        return;
      }

      // Answer in flight — don't touch the mic mid-SB turn.
      if (thinkingRef.current || speakNextAgentReplyRef.current) return;
      // Un-finalized words in the window → submit them exactly as a final
      // would have been (stop-command and garbage still respected).
      // Prefer last interim; also honor whatever is still typed in the bar
      // (whisper interims are never words — chip answers that never
      // finalized left draft-only and never sent).
      const fromBar = (voiceDraftRef.current ?? "").trim();
      const inherited = (lastPttInterim.trim() || fromBar).trim();
      if (inherited && !isLikelyVoiceGarbageTranscript(inherited)) {
        clearReListenTimer();
        voiceTalkRef.current?.abort();
        voiceTalkRef.current = null;
        setVoiceTalkState("idle");
        if (isStopTalkingCommand(inherited)) {
          endVoiceSession("AI Voice off.");
          return;
        }
        setVoiceHint("Answering…");
        openChat();
        flushOrSendRef.current(inherited);
        return;
      }
      // Chip-style bar draft even if garbage heuristics misfire (e.g. ellipsis).
      if (fromBar && isChipStyleVoiceAnswer(fromBar)) {
        clearReListenTimer();
        voiceTalkRef.current?.abort();
        voiceTalkRef.current = null;
        setVoiceTalkState("idle");
        setVoiceHint("Answering…");
        openChat();
        flushOrSendRef.current(fromBar);
        return;
      }
      // FLUSH before going silent-armed (#397 whisper regression): abort()
      // here discarded whisper's in-flight/buffered speech — a 7s spoken
      // answer committing at ~9.8s died at the 10s cutoff. stop() flushes;
      // the late onFinal routes through the normal ladder above. One grace
      // re-arm; still nothing → silent-armed exactly as before.
      if (!pttFlushTried && voiceTalkRef.current) {
        pttFlushTried = true;
        voiceTalkRef.current.stop();
        navListenHardTimerRef.current = setTimeout(onPttListenTimeout, STT_FLUSH_GRACE_MS);
        return;
      }
      clearReListenTimer();
      voiceTalkRef.current?.abort();
      voiceTalkRef.current = null;
      setVoiceTalkState("idle");
      setVoiceDraft(null);
      // Post-answer FM: one free re-open so "I said yes" isn't dead after
      // whisper cold-start ate the first window (no tap required).
      if (isPostAnswer && postAnswerRetryRef.current && voiceConversationRef.current) {
        postAnswerRetryRef.current = false;
        postAnswerListenRef.current = true;
        setVoiceHint("Still listening — say your answer…");
        reListenTimerRef.current = setTimeout(() => {
          reListenTimerRef.current = null;
          if (!voiceConversationRef.current || isWhisperMuted()) return;
          if (thinkingRef.current || speakNextAgentReplyRef.current) return;
          startListeningOnly();
        }, 200);
        return;
      }
      const armedMsg = "AI Voice on — I'll follow along as you browse.";
      setVoiceHint(armedMsg);
      window.setTimeout(() => {
        setVoiceHint((prev) => (prev === armedMsg ? null : prev));
      }, 2600);
    }, listenMs);
    };

    const talk = createSttVoiceTalk({
      onTranscribing: (a) => { sttTranscribingRef.current = a; },
      onLevel: setMicLevel,
      // Post-answer: faster silence so "yes"/"both" commit; cold: longer.
      silenceMs,
      onState: (s) => {
        setVoiceTalkState(s);
        if (s === "listening") {
          const beginWindow = () => {
            if (listenGen !== pushToTalkListenGenRef.current) return;
            // Hard window starts HERE — not at talk.start() — so whisper mic
            // acquire + model warm-up don't consume the listen budget.
            armHardTimer();
            setVoiceHint(
              isPostAnswer
                ? `Your turn — listening… (${listenSecLabel}s)`
                : `Listening… (${listenSecLabel}s)`,
            );
          };
          // COLD START (live bug 2026-08-05, bluehatai.ai): capture begins as
          // soon as the mic + worklet are up, but whisper's 32MB model may
          // still be downloading — and it emits no textual interims, so the
          // visitor sees nothing while speaking. The countdown used to arm
          // here regardless, so a first-use visitor's words were captured,
          // queued behind the download, and then thrown away when the window
          // expired ("Didn't catch that"). Audio is NOT lost while we wait:
          // whisper's transcribe() awaits load() and the endpointer keeps
          // committing utterances. So only the CLOCK waits for readiness.
          if (sttNeedsWarm()) {
            setVoiceHint("Preparing voice… (first time only)");
            void sttReady().then(beginWindow);
          } else {
            beginWindow();
          }
        } else if (s === "idle") {
          // Hard timer owns silence timeout; idle after commit is fine.
        } else if (s === "error") {
          setTerminalVoiceHint("Mic/network issue — tap AI Voice again, or type below.");
        } else if (s === "unsupported") {
          setTerminalVoiceHint("Voice isn’t available here — tap AI Voice or type below.");
        }
      },
      onInterim: (t) => {
        // Short — must fit inside the living bar without covering dual-chrome.
        // Whisper's early partials are junk ("...", "[BLANK_AUDIO]", lone
        // punctuation) — showing them verbatim made the bar read `Hearing:
        // "…"` for the whole utterance and the visitor concluded the mic was
        // deaf (live, 2026-08-05). Junk keeps the honest "Listening…" hint;
        // only real words are worth echoing.
        const clean = (t ?? "").trim();
        const junk = !clean || !/[\p{L}\p{N}]{2,}/u.test(clean) || /^\[.*\]$/.test(clean);
        if (!junk) {
          lastPttInterim = clean;
          const short = clean.length > 42 ? `${clean.slice(0, 40).trim()}…` : clean;
          setVoiceHint(`Hearing: “${short}”`);
          setVoiceDraft(clean);
        }
      },
      onFinal: (transcript) => {
        if (listenGen !== pushToTalkListenGenRef.current) return;
        clearReListenTimer();
        postAnswerRetryRef.current = false;
        // A (possibly late, flushed) final closes this window — cancel any
        // pending hard/grace timer so it cannot double-fire.
        if (navListenHardTimerRef.current != null) {
          clearTimeout(navListenHardTimerRef.current);
          navListenHardTimerRef.current = null;
        }
        const t = transcript.trim();
        if (!t) {
          // Stay ARMED — ending the whole session left no way back in except
          // toggling AI Voice off/on (bench catch). The Speak button re-arms.
          setVoiceTalkState("idle");
          setTerminalVoiceHint("Didn’t catch that — tap Speak to try again.");
          return;
        }
        // CONFIRMATION MOMENT (live catch 2026-08-05): with no usable
        // interims the visitor never saw their words and assumed the mic
        // failed. The final transcript lands visibly in the bar as it is
        // handed off — the sent message then appears in the chat.
        {
          const shortFinal = t.length > 42 ? `${t.slice(0, 40).trim()}…` : t;
          setVoiceHint(`Heard: “${shortFinal}”`);
          setVoiceDraft(t);
        }
        if (isStopTalkingCommand(t)) {
          endVoiceSession("AI Voice off.");
          return;
        }
        // Book demo / open booking CTA (header or presented button) — no SB hop.
        if (isBookDemoCommand(t)) {
          voiceTalkRef.current?.abort();
          voiceTalkRef.current = null;
          setVoiceTalkState("idle");
          setVoiceDraft(null);
          const launched = launchBookDemoRef.current?.();
          if (launched) {
            setVoiceHint("Opening demo…");
          } else {
            setVoiceHint("Demo isn’t available right now — ask me anything.");
            startClickToTalkRef.current({ skipBridge: true });
          }
          return;
        }
        // Drop STT ambient junk — never send to Sales Brain.
        if (isLikelyVoiceGarbageTranscript(t)) {
          try {
            console.log("[BlueHat voice] ignored garbage transcript", {
              preview: t.slice(0, 80),
            });
          } catch {
            /* ignore */
          }
          voiceTalkRef.current?.abort();
          voiceTalkRef.current = null;
          setVoiceTalkState("idle");
          setVoiceHint("Didn’t catch a clear answer — say it again…");
          // FM window: keep listening (don't force a tap after noise).
          if (isPostAnswer && listenGen === pushToTalkListenGenRef.current) {
            postAnswerListenRef.current = true;
            reListenTimerRef.current = setTimeout(() => {
              reListenTimerRef.current = null;
              if (listenGen !== pushToTalkListenGenRef.current) return;
              if (!voiceConversationRef.current || isWhisperMuted()) return;
              if (thinkingRef.current || speakNextAgentReplyRef.current) return;
              startListeningOnly();
            }, 250);
          }
          return;
        }
        // Guide intent: read on-screen pills aloud — no Sales Brain round-trip.
        // CLASSIC-ONLY: tap-to-talk never emits TTS, so "read them" is not a
        // command there — it falls through and goes to the engine as an
        // ordinary question (a silent no-op that re-opens the mic would read
        // as the widget ignoring the visitor).
        if (classicVoiceRef.current && isReadThemCommand(t)) {
          setVoiceHint("Reading the questions on screen…");
          const chain = ++voiceChainRef.current;
          // Server reads the current section's questions (resolved from the
          // latest section_view). The client only signals the intent + transcript.
          requestHostVoice({ trigger: "read_them", transcript: t }, () => {
            if (chain !== voiceChainRef.current) return;
            if (!voiceConversationRef.current || isWhisperMuted()) return;
            if (thinkingRef.current || speakNextAgentReplyRef.current) return;
            clearReListenTimer();
            reListenTimerRef.current = setTimeout(() => {
              reListenTimerRef.current = null;
              if (chain !== voiceChainRef.current) return;
              if (!voiceConversationRef.current || isWhisperMuted()) return;
              setVoiceHint("Your turn — tap one, say one, or ask something else.");
              startClickToTalkRef.current({ skipBridge: true });
            }, 700);
          });
          return;
        }
        setVoiceDraft(null);
        voiceTalkRef.current = null;
        setVoiceTalkState("idle");
        if (classicVoiceRef.current) {
          setVoiceHint(`Got it: “${t}” — answering…`);
          armVoiceForNextAnswer(t);
          warmSpeechVoices();
        } else {
          // Tap-to-talk ONE-SHOT: the utterance is committed, the mic's job
          // is done. End the session so the mic reads idle (a live-looking
          // mic with nothing listening turns the next tap into "off" — the
          // double-tap trap). The answer lands as text like any typed turn.
          endVoiceSessionRef.current?.();
        }
        openChat();
        markAsked(t);
        setPagePills(null);
        setThinkingState(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.setTimeout(() => callbacksRef.current.onSend(t), 50);
          });
        });
      },
    });
    voiceTalkRef.current = talk;
    talk.start();

    // Safety: if STT never reaches "listening" (mic/model hang), still arm
    // the hard timer so we don't leave a zombie session.
    window.setTimeout(() => {
      if (listenGen !== pushToTalkListenGenRef.current) return;
      if (!hardTimerArmed) armHardTimer();
    }, 4_000);
  }, [
    voiceAssist,
    openChat,
    markAsked,
    armVoiceForNextAnswer,
    clearReListenTimer,
    endVoiceSession,
  ]);

  const startClickToTalk = useCallback(
    (opts?: { skipBridge?: boolean }) => {
      if (!voiceAssist) return;
      // Explicit user gesture re-arms voice after "stop talking".
      if (isWhisperMuted()) {
        setWhisperMuted(false);
        setVoiceHint("Voice on — listening…");
      }
      if (!isSttSupported()) {
        setVoiceTalkState("unsupported");
        setVoiceHint("Voice isn’t available here — tap a question or type below.");
        return;
      }
      const wasOff = !voiceConversationRef.current;
      voiceConversationRef.current = true;
      setVoiceSessionOn(true);
      if (wasOff) {
        emitVoiceEvent({ event: "voice_session_started" });
      }
      // TTS warms are CLASSIC-ONLY — tap-to-talk never plays audio, so
      // warming synth voices (and the OpenAI TTS preflight POST) for it is
      // pure waste. STT warms below run for both: both modes listen.
      if (classicVoiceRef.current) {
        warmSpeechVoices();
        unlockAudioPlayback();
        warmOpenAiTts();
      }
      // Whisper STT: preload runtime+model now (31MB, once per origin) so the
      // first listen isn't cold; visible one-time hint while it downloads.
      warmStt((p) => {
        // Coarse steps only — every state update re-renders this whole tree,
        // and a fast model fetch emits progress continuously.
        if (p.phase === "fetching_model" && p.totalBytes) {
          const pct = Math.floor(((p.receivedBytes / p.totalBytes) * 100) / 10) * 10;
          if (pct > 0 && pct < 100 && pct !== sttWarmPctRef.current) {
            sttWarmPctRef.current = pct;
            setVoiceHint(`Preparing voice… ${pct}% (first time only)`);
          }
        }
      });

      const view = sectionBarViewRef.current;
      /** After monologue / bridge / invite TTS: extended listen (not bare 5s). */
      const beginMic = () => {
        if (!voiceConversationRef.current || isWhisperMuted()) return;
        postAnswerListenRef.current = true;
        postAnswerRetryRef.current = true;
        startListeningOnly();
      };

      // Follow-up re-listen after monologue / next-steps / FM — mic only.
      if (opts?.skipBridge) {
        // openMic already set flags; cold paths that call skipBridge alone
        // still need the extended window (monologue → "ask me anything").
        if (!postAnswerListenRef.current) {
          postAnswerListenRef.current = true;
          postAnswerRetryRef.current = true;
        }
        beginMic();
        return;
      }

      const panelOpen = open;
      const hasConversation = messagesRef.current.some(
        (m) =>
          (m.sender === "client" || m.sender === "agent") &&
          m.text.trim().length > 0 &&
          !m.answerChips &&
          !m.chip,
      );

      voiceBridgeSpokenRef.current = true;
      voiceTalkRef.current?.abort();

      // Question in flight — CLASSIC waits so the answer can be spoken (the
      // hint promises exactly that). Tap-to-talk has no answer speech, so
      // there is nothing to wait for or collide with: fall through to the
      // tap gate below and just listen.
      if (classicVoiceRef.current && thinkingRef.current && (panelOpen || hasConversation)) {
        const lastClient = [...messagesRef.current]
          .reverse()
          .find((m) => m.sender === "client" && m.text.trim());
        armVoiceForNextAnswer(lastClient?.text);
        setVoiceHint("Waiting for the answer… then I’ll speak it.");
        return;
      }

      stopAgentSpeech();

      // Tap-to-talk (the new voice-communication feature): straight to
      // listening — no monologue, no host lines. Classic AI Voice continues
      // below with THE SAME OLD flow, fully intact.
      if (!classicVoiceRef.current) {
        setVoiceHint("Listening…");
        beginMic();
        return;
      }

      // ── Page monologue intro (path-specific from page-voice) ─────────
      // Always prefer this when we have lines for THIS path — even if chat
      // history exists. Old gate (!hasConversation only) skipped monologue
      // forever after one ask (telemetry: voice on/off with no cold_open).
      const pathNow =
        typeof window !== "undefined"
          ? window.location.pathname.replace(/\/$/, "") || "/"
          : "/";
      const meta = pageVoiceMetaRef.current;
      const hasMono = (meta.heroMonologue?.length ?? 0) > 0;
      const alreadyIntro = spokenPageIntroPathsRef.current.has(pathNow);

      // ── Canned monologue stashed from the pageview (unengaged session) ──
      // Play the pre-generated mp3 DIRECTLY: no reservation mint, no worker
      // LLM, no synth wait — instant first-tap audio. Engaged sessions never
      // receive monologue URLs (the server mints for them), so this branch
      // is exactly the "monologue until engaged" behavior.
      const stashedNav = pendingNavVoiceUrlRef.current;
      const stashedNavUrl = stashedNav && stashedNav.path === pathNow ? stashedNav.url : null;
      if (!alreadyIntro && stashedNavUrl) {
        pendingNavVoiceUrlRef.current = null;
        spokenPageIntroPathsRef.current.add(pathNow);
        coldOpenSpokenRef.current = true;
        lastNavAnnounceKeyRef.current = `${pathNow}::intro`;
        setVoiceHint("Page overview — then 10s to speak…");
        unlockAudioPlayback();
        emitVoiceEvent({
          event: "voice_host_said",
          kind: "cold_open",
          said: "(canned monologue)",
        });
        speakNavVoiceUrl(stashedNavUrl, () => {
          if (!voiceConversationRef.current || isWhisperMuted()) return;
          beginMic();
        }, "nav");
        return;
      }

      if (!alreadyIntro && (hasMono || (meta.inviteLines?.length ?? 0) > 0)) {
        spokenPageIntroPathsRef.current.add(pathNow);
        coldOpenSpokenRef.current = true;
        lastNavAnnounceKeyRef.current = `${pathNow}::intro`;
        setVoiceHint("Page overview — then 10s to speak…");
        unlockAudioPlayback();
        emitVoiceEvent({
          event: "voice_host_said",
          kind: "cold_open",
          said: "(server host line)",
        });
        // Server assembles the page intro (monologue/invites) — client signals
        // the cold-open trigger only.
        requestHostVoice({ trigger: "cold_open" }, () => {
          if (!voiceConversationRef.current || isWhisperMuted()) return;
          beginMic();
        });
        return;
      }

      // ── Panel OPEN (eyes-on): short pick-or-ask — pills are visible ──
      if (panelOpen) {
        setVoiceHint("Pick your next question, or just ask.");
        requestHostVoice({ trigger: "panel_invite", panelOpen: true }, () => {
          if (!voiceConversationRef.current || isWhisperMuted()) return;
          beginMic();
        });
        return;
      }

      // Already intro'd this path: listen only if no conversation; else nav host.
      if (!hasConversation) {
        setVoiceHint("Listening… (10s)");
        beginMic();
        return;
      }

      // ── Scenario 2 (engaged, panel closed): session-aware pill offer ─
      // Do NOT open the widget until they say yes / ask.
      const hasPill = !!pickNavHostPill(
        view?.currentQuestions ?? [],
        view?.openWithQuestions ?? view?.nextQuestions ?? [],
      );
      if (hasPill && _announcePageForVoice) {
        lastNavAnnounceKeyRef.current = null;
        _announcePageForVoice(view);
        return;
      }

      // Fallback when no grounded pills yet (page-voice still loading).
      setVoiceHint("Got it — pick a question, or ask me anything.");
      emitVoiceEvent({
        event: "voice_host_said",
        kind: "bridge",
        said: "(server host line)",
      });
      requestHostVoice({ trigger: "bridge", panelOpen: false }, () => {
        if (!voiceConversationRef.current || isWhisperMuted()) return;
        beginMic();
      });
    },
    [
      voiceAssist,
      startListeningOnly,
      open,
      armVoiceForNextAnswer,
      emitVoiceEvent,
      requestHostVoice,
    ],
  );

  startClickToTalkRef.current = startClickToTalk;

  /**
   * Primary UI control: turn AI Voice on (cold/engaged host) or fully off.
   * Session-level — kills mic, TTS, nav host, and re-listen (not just the mic window).
   */
  const toggleAiVoice = useCallback(() => {
    if (voiceSessionOn || voiceConversationRef.current) {
      endVoiceSession("AI Voice off.");
      return;
    }
    startClickToTalk();
  }, [voiceSessionOn, endVoiceSession, startClickToTalk]);

  // Expose for hero placement + external host controls (snippet light DOM).
  useEffect(() => {
    _toggleAiVoice = () => toggleAiVoice();
    return () => {
      if (_toggleAiVoice) _toggleAiVoice = null;
    };
  }, [toggleAiVoice]);

  // Warm OpenAI TTS when CLASSIC voice turns on so first click isn't cold.
  // Tap-to-talk never plays audio — no warm, no preflight POST.
  useEffect(() => {
    if (!classicVoice) return;
    warmOpenAiTts();
  }, [classicVoice]);

  // Keep hero (light DOM) AI Voice control in sync with session green/On.
  useEffect(() => {
    if (!voiceAssist) return;
    setAiVoiceSessionUi(voiceSessionOn);
  }, [voiceAssist, voiceSessionOn]);

  /**
   * After chat:init / history reload, mark the latest agent as already spoken
   * so we never TTS-replay old turns after a hard refresh.
   */
  useEffect(() => {
    if (!voiceAssist) return;
    // Classic: only mark when no live session (a live session's answers are
    // handled by the speak effect). Tap-to-talk: NOTHING speaks, so mark
    // every answer as it lands — a later mode flip to classic must not
    // TTS-replay a turn the visitor already read as text.
    if (classicVoice && voiceConversationRef.current) return;
    if (speakNextAgentReplyRef.current) return;
    const pair = findLatestTurnPair(messages);
    if (pair) {
      lastSpokenAgentIdRef.current = pair.agent.id;
    }
  }, [messages, voiceAssist, classicVoice, findLatestTurnPair]);

  /**
   * Speak Sales Brain text for the visitor turn while AI Voice is on.
   * Source of truth = same agent.message.text as the chat bubble (TTS of SB).
   * Prefer armed user key (late-voice fix); if a send path forgot to arm
   * (e.g. openWithText before the arm fix), still speak the latest unspoken
   * complete pair once Thinking is done.
   */
  useEffect(() => {
    // CLASSIC-ONLY, and this gate carries the whole voice-out contract: this
    // effect has a missed-arm FALLBACK below (voice on + new SB answer, no
    // arm ⇒ speak the latest pair), so gating armVoiceForNextAnswer alone
    // does NOT silence tap-to-talk — the fallback would speak anyway. Tap
    // mode is voice in / text out; the answer renders in the chat, period.
    if (!classicVoice) {
      speakNextAgentReplyRef.current = false;
      pendingVoiceUserKeyRef.current = null;
      return;
    }
    if (isWhisperMuted() || !voiceConversationRef.current) {
      speakNextAgentReplyRef.current = false;
      pendingVoiceUserKeyRef.current = null;
      return;
    }

    // Don't TTS a partial stream — wait until Thinking clears.
    if (thinkingRef.current || thinking) return;

    const pendingKey = pendingVoiceUserKeyRef.current;
    const armed = speakNextAgentReplyRef.current === true;

    // Strict pairing when armed; else latest complete pair (missed-arm fallback).
    let pair =
      armed && pendingKey && pendingKey !== "__pending__"
        ? findTurnPairForUserKey(messages, pendingKey)
        : null;
    if (!pair && armed && pendingKey === "__pending__") {
      pair = findLatestTurnPair(messages);
    }
    if (!pair && !armed) {
      // Voice on + new SB answer arrived without arm (hero/bar openWithText).
      pair = findLatestTurnPair(messages);
    }
    if (!pair) return;

    if (armed && pendingKey && pendingKey !== "__pending__") {
      const clientKey = normalizeQuestion(pair.client.text);
      if (clientKey !== pendingKey) return;
    }

    // Already spoke this reply — clear arm when idle.
    if (lastSpokenAgentIdRef.current === pair.agent.id) {
      if (!thinkingRef.current) {
        speakNextAgentReplyRef.current = false;
        pendingVoiceUserKeyRef.current = null;
      }
      return;
    }

    // Safety: never speak if a newer client message is already on screen than
    // the one we armed for (pill clicked while previous answer was mid-TTS).
    const latestClient = [...messages].reverse().find((m) => m.sender === "client");
    if (
      armed &&
      latestClient &&
      pendingKey &&
      pendingKey !== "__pending__" &&
      normalizeQuestion(latestClient.text) !== pendingKey
    ) {
      // A newer ask won; wait for its arm + agent (armVoice already bumped chain).
      return;
    }

    clearReListenTimer();
    if (armWatchdogRef.current != null) {
      window.clearTimeout(armWatchdogRef.current);
      armWatchdogRef.current = null;
    }
    const chain = ++voiceChainRef.current;
    lastSpokenAgentIdRef.current = pair.agent.id;
    speakNextAgentReplyRef.current = false;
    const armedUser = pair.client.text;
    pendingVoiceUserKeyRef.current = null;
    const sbText = pair.agent.text;
    // Prefetch the *spoken* extractive form (first sentence) immediately so
    // OpenAI TTS is in flight the moment text hits the bubble — no compress hop.
    const spoken = textForSpeech(sbText);
    if (spoken) prefetchSpeakText(spoken);
    try {
      console.log("[BlueHat voice] SB text ready → TTS (extractive_fast)", {
        user: armedUser.slice(0, 80),
        agentId: pair.agent.id.slice(0, 8),
        sbChars: sbText.length,
        spokenChars: spoken.length,
        preview: spoken.slice(0, 120),
      });
    } catch {
      /* ignore */
    }
    if (spoken) {
      emitVoiceEvent({
        event: "voice_host_said",
        kind: "sb_answer",
        said: spoken.slice(0, 500),
      });
    }
    const onAnswerSpoken = () => {
      if (chain !== voiceChainRef.current) {
        try { console.log("[BlueHat voice] re-listen DEAD: chain moved", { chain, now: voiceChainRef.current }); } catch { /* ignore */ }
        return;
      }
      if (!voiceConversationRef.current || isWhisperMuted() || !classicVoiceRef.current) {
        try { console.log("[BlueHat voice] re-listen DEAD: conv/mute", { conv: voiceConversationRef.current, muted: isWhisperMuted() }); } catch { /* ignore */ }
        setVoiceHint(null);
        return;
      }
      // If the visitor already sent another turn, do not re-open the mic.
      if (speakNextAgentReplyRef.current || thinkingRef.current) {
        try { console.log("[BlueHat voice] re-listen DEAD: armed/thinking", { armed: speakNextAgentReplyRef.current, thinking: thinkingRef.current }); } catch { /* ignore */ }
        return;
      }
      try { console.log("[BlueHat voice] answer spoken → follow-up listen"); } catch { /* ignore */ }
      clearReListenTimer();
      // After answer: follow-up hint + push-to-talk listen IMMEDIATELY.
      // Every ms here is dead air a fast "yes" falls into (bench 2026-07-23:
      // the answer landed ~300ms after the reply — mic was still 1s away
      // behind 280ms + 400ms stacked timers → engine never heard it, zero
      // logs). setTimeout(0) only to unwind the audio 'ended' stack.
      reListenTimerRef.current = setTimeout(() => {
        reListenTimerRef.current = null;
        speakFollowUpThenListen(chain);
      }, 0);
    };
    // Server-authoritative answer speech: play the server's mp3 URL when present
    // (no client text→synth POST); fall back to browser TTS of the answer inside
    // speakAgentVoiceUrl. Absent (voice off / mint failed) → the legacy path.
    if (pair.agent.voiceUrl) {
      speakAgentVoiceUrl(pair.agent.voiceUrl, sbText, onAnswerSpoken);
    } else {
      speakAgentText(sbText, onAnswerSpoken);
    }
    setVoiceHint("Answering…");
    openChat();
    if (thinkingRef.current) {
      setThinkingState(false);
    }
  }, [
    messages,
    thinking,
    classicVoice,
    openChat,
    findLatestTurnPair,
    findTurnPairForUserKey,
    clearReListenTimer,
    speakFollowUpThenListen,
  ]);

  // Panel close: stop mic / nav-listen only. If AI Voice session is still on,
  // do NOT cut answer TTS mid-sentence — they closed the chat, not the voice.
  // Only "stop talking" / mute / endVoiceSession hard-kills speech.
  useEffect(() => {
    if (!open) {
      clearReListenTimer();
      voiceTalkRef.current?.abort();
      voiceTalkRef.current = null;
      setVoiceTalkState("idle");
      pendingNavPillRef.current = null;
      const voiceOn =
        voiceConversationRef.current &&
        voiceAssistRef.current &&
        !isWhisperMuted();
      if (voiceOn) {
        // Keep TTS playing; bump listen chain only (not speech generation).
        // Do not call stopAgentSpeech() here.
        setVoiceHint(
          isAgentSpeechActive()
            ? "Chat closed — still speaking…"
            : "Chat closed — I’ll still guide as you browse.",
        );
      } else {
        // No active voice session — safe to silence any stray host audio.
        voiceChainRef.current += 1;
        stopAgentSpeech();
      }
    }
  }, [open, clearReListenTimer]);

  const expandBar = useCallback(
    (e?: ReactMouseEvent) => {
      e?.stopPropagation();
      callbacks.onBarEvent?.({ event: "bar_expanded" });
      openChat();
      // Expand without submitting — same discoverability as resting click.
      showPagePills("bar_rest");
      // Focus is handled by ChatWindow mount (composer autofocus path).
    },
    [callbacks, openChat, showPagePills],
  );

  const dismissBar = useCallback(
    (e?: ReactMouseEvent) => {
      e?.stopPropagation();
      setBarDismissed(true);
      try {
        sessionStorage.setItem(BAR_DISMISS_KEY, "1");
      } catch {
        /* ignore */
      }
      callbacks.onBarEvent?.({ event: "bar_dismissed" });
    },
    [callbacks],
  );

  /** Inline close (tenant opted in): hide the in-page unit for the session.
   *  Same telemetry event as the floating dismiss — it is the same intent. */
  const dismissInline = useCallback(
    (e?: ReactMouseEvent) => {
      e?.stopPropagation();
      setInlineDismissed(true);
      try {
        sessionStorage.setItem(INLINE_DISMISS_KEY, "1");
      } catch {
        /* ignore — in-memory state still hides it for this page */
      }
      callbacks.onBarEvent?.({ event: "bar_dismissed" });
    },
    [callbacks],
  );

  const onBarClick = useCallback(() => {
    if (barDismissed) {
      // Pill restore
      setBarDismissed(false);
      try {
        sessionStorage.removeItem(BAR_DISMISS_KEY);
      } catch {
        /* ignore */
      }
      return;
    }
    // First click: open the panel + load THIS page's question pills.
    // Do NOT auto-submit the typewriter line — rolling text reads as animation
    // and accidental clicks sent answers the visitor did not mean. They pick a
    // pill or type. Session continues across pages; pills always match the page
    // they are on (barLines), history comes back via chat:init.
    const line = typewriterRef.current?.currentFullLine();
    const kind = (line?.kind ?? "fallback") as BarLineKind;
    callbacks.onBarEvent?.({ event: "bar_line_clicked", kind, submitted: false });
    openChat();
    showPagePills("bar_rest");
  }, [barDismissed, callbacks, openChat, showPagePills]);

  // Side-panel close → collapse to the centered text bar (not a full teardown).
  // Clicking that bar re-opens the same chat.
  const minimize = useCallback(() => {
    setOpen(false);
    setMinimized(true);
    callbacks.onClose();
  }, [callbacks]);

  const dismissBanner = useCallback(() => {
    setBannerDismissed(true);
    try {
      sessionStorage.setItem(BANNER_DISMISS_KEY, "1");
    } catch {
      // storage blocked (private mode / sandbox) — in-memory dismiss still holds.
    }
  }, []);

  const handleSend = useCallback(
    (text: string) => {
      // Typing (or any send) retires answer chips; suggested bucket returns after answer.
      if (thinkingRef.current) return;
      const payload = text.trim();
      if (!payload) return;
      // Same as voice: "book the demo" executes CTA, does not re-ask SB.
      if (
        voiceAssist &&
        voiceConversationRef.current &&
        isBookDemoCommand(payload)
      ) {
        if (launchBookDemoRef.current?.()) {
          setVoiceHint("Opening demo…");
          return;
        }
      }
      setMessages((prev) => prev.filter((m) => !m.answerChips));
      markAsked(payload);
      setPagePills(null);
      setThinkingState(true);
      // Typed ask during a voice session must arm TTS for this turn too.
      armVoiceForNextAnswer(payload);
      if (voiceAssist && voiceConversationRef.current) {
        setVoiceHint("Answering…");
      }
      callbacks.onSend(payload);
    },
    [callbacks, markAsked, armVoiceForNextAnswer, voiceAssist],
  );

  const handleSubmitLead = useCallback(
    (values: Record<string, string>) => {
      callbacks.onSubmitLead(values);
    },
    [callbacks],
  );

  // Chip accept: retire the chip, post the opt-in event, and send the accept
  // text through the normal chat path (it renders + reaches the worker exactly
  // like a typed "yes").
  const handleChipClick = useCallback(
    (sendText: string) => {
      setMessages((prev) => prev.filter((m) => !m.chip && !m.answerChips));
      setThinkingState(true);
      armVoiceForNextAnswer(sendText);
      if (voiceAssist && voiceConversationRef.current) {
        setVoiceHint("Answering…");
      }
      callbacks.onChipClick(sendText);
      callbacks.onSend(sendText);
    },
    [callbacks, armVoiceForNextAnswer, voiceAssist],
  );

  // Answer-choice chip: hide the group, emit analytics, send as visitor text.
  // Does NOT fire rich_lead_opt_in (that path is handleChipClick only).
  const handleAnswerChipClick = useCallback(
    (label: string, sendText: string, turnId: string) => {
      setMessages((prev) => prev.filter((m) => !m.answerChips));
      callbacks.onAnswerChipClick?.(label, turnId);
      flushOrSend(sendText);
    },
    [callbacks, flushOrSend],
  );

  // Embedding page-question pill — mark session-asked, answer only (no dual chrome).
  const handlePagePillClick = useCallback(
    (sendText: string) => {
      if (thinkingRef.current) return;
      const source = pagePillsSourceRef.current;
      callbacks.onPageQuestionPillClicked?.(questionHash(sendText), sendText, source);
      // flushOrSend marks asked (session-wide), hides bucket, sends.
      flushOrSend(sendText);
    },
    [callbacks, flushOrSend],
  );

  // UI-only label for section surface kickers (all skins). No send/rank effect.
  const sectionSurfaceLead =
    sectionBarView && !barDismissed
      ? surfaceLeadLabel({
          continuityLead: railContinuityLead,
          currentHeading: sectionBarView.currentHeading,
          nextHeading: sectionBarView.nextHeading,
          currentQuestions: sectionBarView.currentQuestions,
          nextQuestions: sectionBarView.nextQuestions,
          pagePool: pageQuestionPoolRef.current,
          monologue: pageVoiceMetaRef.current.heroMonologue,
          inviteLines: pageVoiceMetaRef.current.inviteLines,
          sectionHeadings: pageVoiceMetaRef.current.sectionHeadings,
        })
      : "";

  // Phone: 1 this-section + 2 coming-up surface pills (all skins).
  // Clinical Glass dual level: rank picks WHO, length places WHERE.
  // nudgeFloatCount() is module state set alongside the pool BEFORE the view
  // is pushed, so the sectionBarView dep re-runs this memo with a consistent
  // pair (the count never changes without a new view following it).
  const cglassLevels = useMemo(
    () =>
      (isGlassCapsuleSkin(livingBarSkin) || isAiStyledSkin(livingBarSkin)) && sectionBarView
        ? partitionDualLevels(
            [...sectionBarView.currentQuestions, ...sectionBarView.nextQuestions],
            // A phone count above the desktop count must have chips to paint.
            mobileSurface ? (nudgeMobileCount() ?? nudgeFloatCount()) : nudgeFloatCount(),
          )
        : { stack: [] as string[] },
    [livingBarSkin, sectionBarView, mobileSurface],
  );
  // The floating chips actually rendered. Phones show at most TWO (Fadi live
  // QA 2026-08-05: one reads as a leftover, two is the product) — the customer
  // count can only lower that, never raise it; 0 means none on any surface.
  // Phones draw from EITHER level (row backfills) because the capsule row is
  // desktop-only — without the backfill a row-only partition left phones with
  // an empty bar (live, ndx 2026-08-04).
  /**
   * Site agent's chips. Not the capsule partition — this skin has one row, not
   * a stack and a row — so it takes the page's questions in order and caps
   * them at the tenant's floating count. Empty pool = no chip row at all,
   * which is the barren state working rather than a hole to fill.
   */
  const agentChips = useMemo(() => {
    if (!inline || inlineMode !== "agent" || !agentChipsAllowed || !sectionBarView) {
      return [] as string[];
    }
    const cap = mobileSurface ? phoneChipBudget(2) : nudgeFloatCount();
    return [...sectionBarView.currentQuestions, ...sectionBarView.nextQuestions]
      .map((q) => q.trim())
      .filter(Boolean)
      .slice(0, cap);
  }, [inline, inlineMode, agentChipsAllowed, sectionBarView, mobileSurface]);

  /**
   * The rotation. Two phases so the outgoing chip leaves before the incoming
   * one arrives: "in" holds for its own reading time, then "out" plays the
   * exit and the index advances behind it.
   *
   * Nothing rotates when there is only one question — a chip sliding out and
   * back in with the same words reads as a glitch, not a suggestion.
   */
  const [agentChipIdx, setAgentChipIdx] = useState(0);
  const [agentChipPhase, setAgentChipPhase] = useState<"in" | "out">("in");
  const agentChip = agentChips.length > 0 ? agentChips[agentChipIdx % agentChips.length]! : null;

  useEffect(() => {
    if (!agentChip || agentChips.length < 2) return;
    const hold = window.setTimeout(() => {
      setAgentChipPhase("out");
      // Advance only after the exit has played, so the two never overlap.
      const swap = window.setTimeout(() => {
        setAgentChipIdx((i) => (i + 1) % agentChips.length);
        setAgentChipPhase("in");
      }, AGENT_CHIP_EXIT_MS);
      agentChipSwapRef.current = swap;
    }, agentChipDwellMs(agentChip));
    return () => {
      window.clearTimeout(hold);
      if (agentChipSwapRef.current) window.clearTimeout(agentChipSwapRef.current);
    };
  }, [agentChip, agentChips.length]);

  const cglassStackChips = useMemo(
    () =>
      mobileSurface
        ? cglassLevels.stack.slice(0, phoneChipBudget(2))
        : cglassLevels.stack,
    [cglassLevels, mobileSurface],
  );
  const { current: surfaceCurrentQs, next: surfaceNextQs } = sectionBarView
    ? surfacePaintQuestions(sectionBarView, mobileSurface)
    : { current: [] as string[], next: [] as string[] };

  // #592: inline surfaces map viewport-pinned side skins to in-flow
  // siblings; floating is identity, so this changes nothing there.
  const surfaceSkin = inline ? inlineSurfaceSkin(livingBarSkin) : livingBarSkin;
  /** Site agent is a MODE, not a skin: it has no bar form, so nothing about it
   *  is reachable from the Lower bar picker. */
  const agentMode = inline && inlineMode === "agent";
  /** Questions only — inline `nudges` mode. Also a MODE, not a skin: its look
   *  comes from nudgesSurfaceLook, whatever skin the lower bar wears. */
  const nudgesMode = inline && inlineMode === "nudges";

  /**
   * The questions a questions-only surface shows: this rotation window, in
   * order. Inline shows the whole window (it is in-flow and wraps); the
   * floating bar honours the tenant's floating count and the phone budget,
   * exactly as the pills above the full bar do. Empty = nothing paints.
   */
  const nudgesChips = useMemo(() => {
    if (!sectionBarView) return [] as string[];
    const cap = inline
      ? NUDGES_INLINE_MAX
      : mobileSurface
        ? phoneChipBudget(2)
        : nudgeFloatCount();
    return [...sectionBarView.currentQuestions, ...sectionBarView.nextQuestions]
      .map((q) => q.trim())
      .filter(Boolean)
      .slice(0, cap);
  }, [inline, sectionBarView, mobileSurface]);

  /**
   * The questions-only surface: the page's question pills and nothing else.
   * One render for both placements — the inline `nudges` container and the
   * lower bar's questions-only form — so they cannot drift apart.
   *
   * It wears the CAPSULE pills (`.bhai-cglass-chip`) inside the wrap the full
   * bar uses, so every pill rule already in chat.css applies: the AI style's
   * generated colors, or a glass-capsule palette. A pill click is the full
   * bar's pill click (`askFromSurface`): the chat opens with the question
   * asked — in place for a container, as the panel for the bar.
   */
  const nudgesSurface = () => {
    if (nudgesChips.length === 0) return null;
    const look = nudgesSurfaceLook(surfaceSkin);
    const placement = inline ? "inline" : "bar";
    const canClose = inline ? inlineCloseAllowed : true;
    return (
      <div
        className={`bhai-collapsed-wrap bhai-nudges bhai-nudges--${placement}${
          look.ai ? " bhai-collapsed-wrap--ai" : ""
        }${look.palette ? ` bhai-pal--${look.palette}` : ""}${
          mobileSurface ? " bhai-collapsed-wrap--mobile-surface" : ""
        }`}
      >
        <div className="bhai-cglass-stack bhai-nudges-list" role="group" aria-label="Questions about this page">
          {nudgesChips.map((q) => (
            <button
              key={q}
              type="button"
              part="chip"
              className="bhai-cglass-chip"
              title={q}
              onClick={(e) => {
                e.stopPropagation();
                askFromSurface(q);
              }}
            >
              {q}
            </button>
          ))}
          {canClose && (
            <button
              type="button"
              className="bhai-nudges-close"
              aria-label="Close"
              onClick={inline ? dismissInline : dismissBar}
            >
              ×
            </button>
          )}
        </div>
      </div>
    );
  };

  // Collapsed skinned surface — the rail stage + .bhai-collapsed-wrap that
  // every skin family renders. Extracted as a function so the inline
  // "expand" mode (#592) renders the SAME surface in-flow inside the
  // blessed div; floating interpolates it below inside the unchanged
  // six-term gate. Lazy (called at render) so no work happens when gated off.
  /**
   * Site agent's collapsed surface. Its own return, not a branch inside the
   * capsule tree, because the whole design is what is ABSENT: no wrap, no
   * capsule, no icon, no call-to-action, no voice invite. A visitor should see
   * a field and a few questions and nothing to read past.
   *
   * The input is deliberately NOT the shared `.bhai-inline-ask`: that one
   * expands on focus, which is right for a bar someone taps and wrong for a
   * field that looks like search. Here the visitor types in place and ENTER
   * commits — `askFromSurface` opens the conversation with the question
   * already asked, and the composer takes the field's job at the foot of the
   * container, so the field reads as having dropped to the bottom.
   */
  /** The chip slot holds the rotating question nudges, ALWAYS. From 2026-08-26
   *  to 2026-10-13 a returning engaged visitor saw a resume chip here instead
   *  (Fadi, #624) — a thread they could already reach by typing in the field
   *  below, which replays the transcript. Brian: the slot is for nudges; the
   *  affordance went. Inline still never auto-opens. */
  const agentSurface = () => (
    <div className="bhai-agent" role="search">
      {inlineCloseAllowed && (
        <button type="button" className="bhai-agent-close" aria-label="Close" onClick={dismissInline}>
          ×
        </button>
      )}
      {/* ONE question at a time. A row of them is a menu — competition for the
          field, which is the one thing this mode exists to make obvious. One
          rotating suggestion reads as the assistant offering an example. */}
      {agentChip && (
        <div className="bhai-agent-chips" role="group" aria-label="Question about this page">
          <button
            type="button"
            className={`bhai-agent-chip bhai-agent-chip--${agentChipPhase}`}
            title={agentChip}
            onClick={(e) => {
              e.stopPropagation();
              askFromSurface(agentChip);
            }}
          >
            {agentChip}
          </button>
        </div>
      )}
      {/* THE SAME COMPOSER the expanded conversation uses — same class, same
          icon, same send button — because the whole effect depends on it. A
          visitor types here, presses Enter, and the conversation opens with
          this field at its foot; if the field changed appearance across that
          transition it would read as two boxes rather than one that moved. */}
      <form
        className="chat-input bhai-agent-field"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const q = agentDraft.trim();
          if (!q) return;
          setAgentDraft("");
          askFromSurface(q);
        }}
      >
        <ConstellationIcon />
        <input
          type="text"
          aria-label="Ask a question about this page"
          // Dimmed by the composer's own placeholder rule, never absent: an
          // empty box with no hint reads as broken rather than minimal.
          placeholder={fittedAskAboutCopy(
            bottomBar?.copy.placeholder || "Ask me anything",
            hostWidth,
          )}
          value={agentDraft}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => setAgentDraft(e.target.value)}
        />
        <button type="submit" aria-label="Send">
          <SendIcon />
        </button>
      </form>
    </div>
  );

  /**
   * The inline MODE, on the light-DOM host. A designer hook alongside
   * data-bhai-inline-state, and the signal chat.css needs to tell "hug your
   * content" (a bar) from "fill the container the page gave you" (agent, which
   * CENTRES inside it — vertical centring is meaningless in a box that
   * shrink-wraps).
   */
  useEffect(() => {
    const host = _hostEl;
    if (!host) return;
    if (inline && inlineMode) host.setAttribute("data-bhai-inline-mode", inlineMode);
    else host.removeAttribute("data-bhai-inline-mode");
  }, [inline, inlineMode]);

  /** Blessed-div width, for placeholder fitting (agent field + capsule input).
   *  Observed rather than read once: a rail resizes with the page's own layout
   *  without any window resize. null (observer not landed / no support) keeps
   *  the full copy — today's behavior. Inline only; the floating bar's copy
   *  wraps in a span and needs no fitting. */
  const [hostWidth, setHostWidth] = useState<number | null>(null);
  useEffect(() => {
    const host = _hostEl;
    if (!inline || !host || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === "number") setHostWidth(w);
    });
    ro.observe(host);
    return () => ro.disconnect();
  }, [inline]);

  const collapsedSurface = () => (
        <>
        {/* Side rail / theater rail must sit outside .bhai-collapsed-wrap —
            that wrap uses transform, which would trap position:fixed. */}
        {!barDismissed &&
          sectionBarView &&
          surfaceSkin != null &&
          isSideLivingBarSkin(surfaceSkin) && (
          <div
            className={[
              "bhai-rail-stage",
              surfaceSkin === "side_theater" ? "bhai-rail-stage--theater" : "",
              mobileSurface ? "bhai-rail-stage--compact" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            role="group"
            aria-label={
              surfaceSkin === "side_theater"
                ? "Section questions on the theater side rail"
                : "Section questions on the side rail"
            }
          >
            <div
              className={[
                "bhai-rail-panel",
                surfaceSkin === "side_theater" ? "bhai-rail-panel--theater" : "",
                mobileSurface ? "bhai-rail-panel--compact" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {!mobileSurface && (
                <div
                  className={
                    sectionSurfaceLead.toLowerCase().startsWith("related to")
                      ? "bhai-rail-kicker bhai-rail-kicker--continuity"
                      : "bhai-rail-kicker"
                  }
                >
                  {sectionSurfaceLead || "On this page"}
                </div>
              )}
              {surfaceCurrentQs.length > 0 && (
                <div className="bhai-rail-block">
                  <div className="bhai-rail-pills">
                    {surfaceCurrentQs.map((q, i) => (
                      <button
                        key={`rail-cur-${i}`}
                        type="button"
                        className="bhai-rail-pill bhai-rail-pill--now"
                        onClick={(e) => {
                          e.stopPropagation();
                          askFromSurface(q, "now");
                        }}
                      >
                        <span className="bhai-rail-pill__q">{q}</span>
                        <span className="bhai-rail-pill__go">
                          {mobileSurface ? "→" : "Answer now"}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {surfaceNextQs.length > 0 && (
                <div className="bhai-rail-block">
                  <div className="bhai-rail-pills">
                    {surfaceNextQs.map((q, i) => (
                      <button
                        key={`rail-next-${i}`}
                        type="button"
                        className="bhai-rail-pill bhai-rail-pill--next"
                        onClick={(e) => {
                          e.stopPropagation();
                          askFromSurface(q, "next");
                        }}
                      >
                        <span className="bhai-rail-pill__q">{q}</span>
                        <span className="bhai-rail-pill__go">
                          {mobileSurface ? "→" : "Answer now"}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        <div
          className={`bhai-collapsed-wrap${barDismissed ? " bhai-pill-wrap" : ""}${
            // Skin classes derive from the SKIN ALONE (preload-known) — never
            // from sectionBarView, which is page CONTENT arriving later on the
            // page-voice line. Gating the look on content is what painted the
            // stock capsule first and redressed it a beat later (the 2026-08-17
            // flicker). Chips/stack/kicker still gate on sectionBarView below;
            // a sectionless page keeps the dressed capsule with no chips.
            // ai_styled is deliberately chrome-LESS: the generated theme is the
            // whole look, so stamping a skin's section classes over it would
            // undo the design the tenant just approved.
            surfaceSkin != null &&
            !isSideLivingBarSkin(surfaceSkin) &&
            !isAiStyledSkin(surfaceSkin)
              ? ` bhai-collapsed-wrap--section bhai-collapsed-wrap--${livingBarSkinMod(surfaceSkin)}${
                  glassCapsulePalette(surfaceSkin)
                    ? ` bhai-pal--${glassCapsulePalette(surfaceSkin)}`
                    : ""
                }`
              : isAiStyledSkin(surfaceSkin)
                ? " bhai-collapsed-wrap--ai"
                : ""
          }${mobileSurface ? " bhai-collapsed-wrap--mobile-surface" : ""}`}
        >
          {/* Section skins — suppressed while in-page stage owns section UI. */}
          {/* Clinical Glass: floating chip stack — one chip per line so long
              corpus questions render WHOLE; left-aligned to the capsule's
              edge; no section labels (shown, never narrated). The shortest
              of the top-ranked set float here; the capsule below stays slim
              (the in-bar row is retired — law v5, 2026-08-25). */}
          {!barDismissed &&
            sectionBarView &&
            // The stack is the PHONE surface, not a desktop luxury: it wraps,
            // so any question renders. The capsule row is `nowrap` — at ~340px
            // a typical 80-char question cannot seat, and gating the stack on
            // "are there row CANDIDATES" (rather than what actually seats)
            // left phones with an empty bar (live, ndx 2026-08-04).
            // ai_styled wears the SAME question stack (Fadi 2026-08-25):
            // pills are CONTENT, not chrome. The chrome-less rule keeps the
            // capsule drawn by the generated theme; the chips get an --ai
            // scope so CSS themes them from the design's own variables.
            (isGlassCapsuleSkin(surfaceSkin) || isAiStyledSkin(surfaceSkin)) &&
            // cglassStackChips already applies the customer floating count —
            // 0 chips (count 0, or nothing to show) renders no container on
            // any surface.
            cglassStackChips.length > 0 && (
            <div
              className="bhai-cglass-stack"
              role="group"
              aria-label="Questions about this page"
            >
              {cglassStackChips.map((q) => (
                <button
                  key={q}
                  type="button"
                  part="chip"
                  className="bhai-cglass-chip"
                  title={q}
                  onClick={(e) => {
                    e.stopPropagation();
                    askFromSurface(q);
                  }}
                >
                  {q}
                </button>
              ))}
            </div>
          )}
          {/* Tap-to-talk listening status — far right, DIRECTLY above the
              capsule (live catches 2026-08-05, twice: position:fixed anchored
              it to the viewport edge mid-screen; then as the wrap's FIRST
              child it sat above the floating question stack — a hundred-plus
              pixels up the page when the stack was populated. "Above the
              widget" means hugging the CAPSULE: after the stack, before the
              bar.) */}
          {!classicVoice && voiceAssist && voiceSessionOn && voiceTalkState === "listening" && (
            <div className="bhai-listen-chip" role="status" aria-live="polite">
              <span className="bhai-listen-dot" aria-hidden="true" />
              {voiceHint && voiceHint.toLowerCase().startsWith("listening")
                ? voiceHint
                : "Listening…"}
            </div>
          )}
          {!barDismissed &&
            sectionBarView &&
            surfaceSkin === "sticky_notes" && (
            <div
              className="bhai-sticky-notes-stage"
              role="group"
              aria-label="Section questions as sticky notes"
            >
              <SurfaceLeadKicker
                lead={sectionSurfaceLead}
                skin={surfaceSkin}
              />
              <div className="bhai-sticky-notes">
                {surfaceCurrentQs.map((q, i) => (
                  <button
                    key={`sticky-now-${i}`}
                    type="button"
                    className="bhai-sticky-note bhai-sticky-note--now"
                    style={stickyNoteVars(i)}
                    onClick={(e) => {
                      e.stopPropagation();
                      askFromSurface(q);
                    }}
                  >
                    <span className="bhai-sticky-note__q">{q}</span>
                    <span className="bhai-sticky-note__go">
                      Answer now <span aria-hidden="true">→</span>
                    </span>
                    <span className="bhai-sticky-note__tail" aria-hidden="true" />
                  </button>
                ))}
                {surfaceNextQs.map((q, i) => (
                  <button
                    key={`sticky-next-${i}`}
                    type="button"
                    className="bhai-sticky-note bhai-sticky-note--next"
                    style={stickyNoteVars(i + surfaceCurrentQs.length)}
                    onClick={(e) => {
                      e.stopPropagation();
                      askFromSurface(q);
                    }}
                  >
                    <span className="bhai-sticky-note__q">{q}</span>
                    <span className="bhai-sticky-note__go">
                      Answer now <span aria-hidden="true">→</span>
                    </span>
                    <span className="bhai-sticky-note__tail" aria-hidden="true" />
                  </button>
                ))}
              </div>
            </div>
          )}
          {!barDismissed &&
            sectionBarView &&
            surfaceSkin === "glass" && (
            <div
              className="bhai-glass-stage"
              role="group"
              aria-label="Section questions as glass thought cards"
            >
              <SurfaceLeadKicker
                lead={sectionSurfaceLead}
                skin={surfaceSkin}
              />
              <div className="bhai-glass-cards">
                {surfaceCurrentQs.map((q, i) => (
                  <button
                    key={`glass-now-${i}`}
                    type="button"
                    className="bhai-glass-card bhai-glass-card--now"
                    style={glassCardVars(i, "now")}
                    onClick={(e) => {
                      e.stopPropagation();
                      askFromSurface(q, "now");
                    }}
                  >
                    <span className="bhai-glass-card__glow" aria-hidden="true" />
                    <span className="bhai-glass-card__q">{q}</span>
                    <span className="bhai-glass-card__go">
                      Answer now <span aria-hidden="true">→</span>
                    </span>
                  </button>
                ))}
                {surfaceNextQs.map((q, i) => (
                  <button
                    key={`glass-next-${i}`}
                    type="button"
                    className="bhai-glass-card bhai-glass-card--next"
                    style={glassCardVars(i + surfaceCurrentQs.length, "next")}
                    onClick={(e) => {
                      e.stopPropagation();
                      askFromSurface(q, "next");
                    }}
                  >
                    <span className="bhai-glass-card__glow" aria-hidden="true" />
                    <span className="bhai-glass-card__q">{q}</span>
                    <span className="bhai-glass-card__go">
                      Answer now <span aria-hidden="true">→</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {!barDismissed &&
            sectionBarView &&
            surfaceSkin === "constellation" && (
            <div
              className="bhai-constellation-stage"
              role="group"
              aria-label="Section questions as constellation"
            >
              {/* LLM lead — same continuity line as glass/radar/theater. */}
              <SurfaceLeadKicker
                lead={sectionSurfaceLead}
                skin={surfaceSkin}
              />
              <div className="bhai-constellation-field" aria-hidden="true" />
              <div className="bhai-constellation-nodes">
                {surfaceCurrentQs.map((q, i) => (
                  <button
                    key={`star-now-${i}`}
                    type="button"
                    className="bhai-star-node bhai-star-node--now"
                    style={constellationVars(i, "now")}
                    onClick={(e) => {
                      e.stopPropagation();
                      askFromSurface(q);
                    }}
                  >
                    <span className="bhai-star-node__dot" aria-hidden="true" />
                    <span className="bhai-star-node__q">{q}</span>
                    <span className="bhai-star-node__go">
                      Ask <span aria-hidden="true">→</span>
                    </span>
                  </button>
                ))}
                {surfaceNextQs.map((q, i) => (
                  <button
                    key={`star-next-${i}`}
                    type="button"
                    className="bhai-star-node bhai-star-node--next"
                    style={constellationVars(i + surfaceCurrentQs.length, "next")}
                    onClick={(e) => {
                      e.stopPropagation();
                      askFromSurface(q);
                    }}
                  >
                    <span className="bhai-star-node__dot" aria-hidden="true" />
                    <span className="bhai-star-node__q">{q}</span>
                    <span className="bhai-star-node__go">
                      Ask <span aria-hidden="true">→</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {!barDismissed &&
            sectionBarView &&
            surfaceSkin === "radar" && (
            <div
              className="bhai-radar-stage"
              role="group"
              aria-label="Section questions as radar blips"
            >
              <SurfaceLeadKicker
                lead={sectionSurfaceLead}
                skin={surfaceSkin}
              />
              <div className="bhai-radar-scope" aria-hidden="true">
                <span className="bhai-radar-ring bhai-radar-ring--1" />
                <span className="bhai-radar-ring bhai-radar-ring--2" />
                <span className="bhai-radar-ring bhai-radar-ring--3" />
                <span className="bhai-radar-sweep" />
              </div>
              <div className="bhai-radar-blips">
                {surfaceCurrentQs.map((q, i) => (
                  <button
                    key={`radar-now-${i}`}
                    type="button"
                    className="bhai-radar-blip bhai-radar-blip--now"
                    style={radarBlipVars(i)}
                    onClick={(e) => {
                      e.stopPropagation();
                      askFromSurface(q);
                    }}
                  >
                    <span className="bhai-radar-blip__ping" aria-hidden="true" />
                    <span className="bhai-radar-blip__q">{q}</span>
                    <span className="bhai-radar-blip__go">
                      Answer now <span aria-hidden="true">→</span>
                    </span>
                  </button>
                ))}
                {surfaceNextQs.map((q, i) => (
                  <button
                    key={`radar-next-${i}`}
                    type="button"
                    className="bhai-radar-blip bhai-radar-blip--next"
                    style={radarBlipVars(i + surfaceCurrentQs.length)}
                    onClick={(e) => {
                      e.stopPropagation();
                      askFromSurface(q);
                    }}
                  >
                    <span className="bhai-radar-blip__ping" aria-hidden="true" />
                    <span className="bhai-radar-blip__q">{q}</span>
                    <span className="bhai-radar-blip__go">
                      Answer now <span aria-hidden="true">→</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {!barDismissed &&
            sectionBarView &&
            surfaceSkin === "command_line" && (
            <div
              key={`cli-${sectionBarView.sectionIndex}-${sectionBarView.currentHeading}`}
              className="bhai-cli-stage"
              role="group"
              aria-label="Section questions"
            >
              <SurfaceLeadKicker
                lead={sectionSurfaceLead}
                skin={surfaceSkin}
              />
              <div className="bhai-cli-chrome" aria-hidden="true">
                <span className="bhai-cli-dot bhai-cli-dot--r" />
                <span className="bhai-cli-dot bhai-cli-dot--y" />
                <span className="bhai-cli-dot bhai-cli-dot--g" />
              </div>
              <div className="bhai-cli-lines">
                {surfaceCurrentQs.map((q, i) => (
                  <button
                    key={`cur-${q}`}
                    type="button"
                    className="bhai-cli-line bhai-cli-line--now"
                    style={cliLineVars(i)}
                    onClick={(e) => {
                      e.stopPropagation();
                      askFromSurface(q);
                    }}
                  >
                    <span className="bhai-cli-prompt" aria-hidden="true">
                      ›
                    </span>
                    <span className="bhai-cli-q">{q}</span>
                    <span className="bhai-cli-caret" aria-hidden="true" />
                    <span className="bhai-cli-run">Answer now</span>
                  </button>
                ))}
                {surfaceNextQs.map((q, i) => (
                  <button
                    key={`next-${q}`}
                    type="button"
                    className="bhai-cli-line bhai-cli-line--next"
                    style={cliLineVars(i + surfaceCurrentQs.length)}
                    onClick={(e) => {
                      e.stopPropagation();
                      askFromSurface(q);
                    }}
                  >
                    <span className="bhai-cli-prompt bhai-cli-prompt--dim" aria-hidden="true">
                      ›
                    </span>
                    <span className="bhai-cli-q">{q}</span>
                    <span className="bhai-cli-run bhai-cli-run--next">Answer now</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {!barDismissed &&
            sectionBarView &&
            surfaceSkin === "split_theater" && (
            <div
              className="bhai-theater-stage"
              role="group"
              aria-label="Section questions in Now and Ahead stages"
            >
              {/* Original two-panel design — height only is compact. */}
              <div className="bhai-theater-panel bhai-theater-panel--now">
                {displaySectionHeading(sectionBarView.currentHeading) ? (
                  <div className="bhai-theater-panel-head">
                    <span className="bhai-theater-heading">
                      {displaySectionHeading(sectionBarView.currentHeading)}
                    </span>
                  </div>
                ) : null}
                <div className="bhai-theater-qs">
                  {surfaceCurrentQs.map((q, i) => (
                    <button
                      key={`theater-now-${i}`}
                      type="button"
                      className="bhai-theater-q bhai-theater-q--now"
                      onClick={(e) => {
                        e.stopPropagation();
                        askFromSurface(q);
                      }}
                    >
                      <span className="bhai-theater-q__text">{q}</span>
                      <span className="bhai-theater-q__go">Answer now</span>
                    </button>
                  ))}
                  {surfaceCurrentQs.length === 0 ? (
                    <p className="bhai-theater-empty">No questions left here</p>
                  ) : null}
                </div>
              </div>
              <div className="bhai-theater-seam" aria-hidden="true" />
              <div className="bhai-theater-panel bhai-theater-panel--ahead">
                {displaySectionHeading(sectionBarView.nextHeading) ? (
                  <div className="bhai-theater-panel-head">
                    <span className="bhai-theater-heading">
                      {displaySectionHeading(sectionBarView.nextHeading)}
                    </span>
                  </div>
                ) : null}
                <div className="bhai-theater-qs">
                  {surfaceNextQs.map((q, i) => (
                    <button
                      key={`theater-next-${i}`}
                      type="button"
                      className="bhai-theater-q bhai-theater-q--ahead"
                      onClick={(e) => {
                        e.stopPropagation();
                        askFromSurface(q);
                      }}
                    >
                      <span className="bhai-theater-q__text">{q}</span>
                      <span className="bhai-theater-q__go">Answer now</span>
                    </button>
                  ))}
                  {surfaceNextQs.length === 0 ? (
                    <p className="bhai-theater-empty">You’re near the end</p>
                  ) : null}
                </div>
              </div>
            </div>
          )}
          {!barDismissed &&
            sectionBarView &&
            surfaceSkin === "on_this_page" && (
            <div
              className="bhai-otp-stage"
              role="group"
              aria-label="On this page — this section and coming up questions"
            >
              <div className="bhai-otp-panel">
                <div
                  className={
                    sectionSurfaceLead.toLowerCase().startsWith("related to")
                      ? "bhai-otp-kicker bhai-otp-kicker--continuity"
                      : "bhai-otp-kicker"
                  }
                >
                  {sectionSurfaceLead || "On this page"}
                </div>
                {surfaceCurrentQs.length > 0 && (
                  <div className="bhai-otp-block">
                    <div className="bhai-otp-pills">
                      {surfaceCurrentQs.map((q) => (
                        <button
                          key={`cur-${q}`}
                          type="button"
                          className="bhai-otp-pill bhai-otp-pill--now"
                          onClick={(e) => {
                            e.stopPropagation();
                            askFromSurface(q);
                          }}
                        >
                          <span className="bhai-otp-pill__q">{q}</span>
                          <span className="bhai-otp-pill__go">Answer now</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {surfaceNextQs.length > 0 && (
                  <div className="bhai-otp-block">
                    <div className="bhai-otp-pills">
                      {surfaceNextQs.map((q) => (
                        <button
                          key={`next-${q}`}
                          type="button"
                          className="bhai-otp-pill bhai-otp-pill--next"
                          onClick={(e) => {
                            e.stopPropagation();
                            askFromSurface(q);
                          }}
                        >
                          <span className="bhai-otp-pill__q">{q}</span>
                          <span className="bhai-otp-pill__go">Answer now</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <div
            part="bar"
            className={`bhai-collapsed${barDismissed ? " bhai-pill" : ""}${
              // Same rule as the wrap: skin dresses the capsule from frame 1;
              // sectionBarView adds only the bare --section (side skins keep
              // their content-gated path — their chrome IS the rail).
              surfaceSkin != null &&
              !isSideLivingBarSkin(surfaceSkin) &&
              !isAiStyledSkin(surfaceSkin)
                ? ` bhai-collapsed--section bhai-collapsed--${livingBarSkinMod(surfaceSkin)}${
                    glassCapsulePalette(surfaceSkin)
                      ? ` bhai-pal--${glassCapsulePalette(surfaceSkin)}`
                      : ""
                  }`
                : // ai_styled takes NEITHER branch: no section chrome. It gets
                  // its own marker instead, so the capsule can be drawn from
                  // the generated theme (radius/surface/shadow) rather than a
                  // skin's fixed geometry.
                  isAiStyledSkin(surfaceSkin)
                  ? " bhai-collapsed--ai"
                  : sectionBarView && surfaceSkin != null
                    ? ` bhai-collapsed--section bhai-collapsed--${livingBarSkinMod(surfaceSkin)}`
                    : sectionBarView
                      ? " bhai-collapsed--section"
                      : ""
            }${voiceSessionOn ? " bhai-collapsed--voice-on" : ""}${
              voiceTalkState === "listening" ? " bhai-collapsed--listening" : ""
            }`}
            role="button"
            tabIndex={0}
            aria-label={barDismissed ? "Show chat bar" : "Open chat"}
            onClick={
              sectionBarView && !barDismissed
                ? () => {
                    const qs = sectionBarView.openWithQuestions;
                    if (qs.length > 0) {
                      _openWithSectionQuestions?.(qs);
                    } else {
                      onBarClick();
                    }
                  }
                : onBarClick
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onBarClick();
              }
            }}
          >
            <span className={`bhai-collapsed-icon${markPulse ? " bhai-mark-pulse" : ""}`}>
              <ConstellationIcon />
            </span>
            {/* Compact voice status lives INSIDE the bar so it never covers
                OTP / dual-chrome Answer now pills above the living bar. */}
            {!barDismissed &&
              voiceAssist &&
              voiceHint &&
              voiceSessionOn && (
              <span className="bhai-voice-hint bhai-voice-hint--bar" role="status">
                {voiceHint}
              </span>
            )}
            {!barDismissed &&
              sectionBarView &&
              surfaceSkin != null &&
                !(voiceAssist && voiceSessionOn && voiceHint) && (
              <div className="bhai-collapsed-section-body">
                {isGlassCapsuleSkin(surfaceSkin) && (
                  <span className="bhai-cglass-kicker">
                    {`AI · ${
                      displaySectionHeading(sectionBarView.currentHeading) ||
                      "This page"
                    }`}
                    <span className="bhai-cglass-kicker-priv"> · Private session</span>
                  </span>
                )}
                <span
                  className={`bhai-collapsed-section-lead bhai-collapsed-section-lead--${livingBarSkinMod(surfaceSkin)}`}
                >
                  {isGlassCapsuleSkin(surfaceSkin)
                    ? // Clinical Glass speaks in one short invitation — the
                      // section is already named in the kicker, and the
                      // "X · next: Y" inventory line reads as chrome. LLM
                      // continuity still wins when it's real — but the
                      // lexical inventory template counts as chrome even
                      // when it arrives pre-built in the continuity slot.
                      (railContinuityLead ?? "").trim().length >= 12 &&
                      !/·\s*next:/i.test(railContinuityLead ?? "")
                      ? livingBarLead(surfaceSkin, {
                          hasNext: sectionBarView.nextQuestions.length > 0,
                          currentHeading: sectionBarView.currentHeading,
                          nextHeading: sectionBarView.nextHeading,
                          inventory: [
                            ...sectionBarView.currentQuestions,
                            ...sectionBarView.nextQuestions,
                          ],
                          continuityLead: railContinuityLead,
                        })
                      : askAboutCopy(
                          bottomBar?.copy.placeholder || "Ask me anything",
                        )
                    : livingBarLead(surfaceSkin, {
                        hasNext: sectionBarView.nextQuestions.length > 0,
                        currentHeading: sectionBarView.currentHeading,
                        nextHeading: sectionBarView.nextHeading,
                        inventory: [
                          ...sectionBarView.currentQuestions,
                          ...sectionBarView.nextQuestions,
                        ],
                        continuityLead: railContinuityLead,
                      })}
                </span>
              </div>
            )}
                        {inline && (
              /* #592: the inline collapsed bar is a REAL text field — focus
                 or typing expands the conversation in place, carrying any
                 pre-expansion keystrokes into the composer. */
              <input
                className="bhai-inline-ask"
                type="text"
                aria-label="Ask a question"
                placeholder={
                  (railContinuityLead &&
                    railContinuityLead.trim().length >= 12 &&
                    // A continuity lead that cannot seat whole falls through
                    // to the ask-about copy — placeholders never cut mid-word.
                    placeholderFits(railContinuityLead, hostWidth) &&
                    railContinuityLead) ||
                  fittedAskAboutCopy(
                    bottomBar?.copy.placeholder || "Ask me anything",
                    hostWidth,
                  )
                }
                onClick={(e) => e.stopPropagation()}
                onFocus={() => {
                  setInlineDraft("");
                  openChat();
                }}
                onChange={(e) => {
                  setInlineDraft(e.target.value);
                  openChat();
                }}
              />
            )}
            {!inline &&
              !barDismissed &&
              (!sectionBarView) &&
              !(voiceAssist && voiceSessionOn && voiceHint) && (
              <span
                ref={textRef}
                className={`bhai-collapsed-text${barTyping ? " bhai-typing" : ""}`}
              >
                {(railContinuityLead &&
                  railContinuityLead.trim().length >= 12 &&
                  railContinuityLead) ||
                  askAboutCopy(bottomBar?.copy.placeholder || "Ask me anything")}
              </span>
            )}
            {/* Voice control: invite wraps the button so we don't say "AI Voice" twice. */}
            {!barDismissed && classicVoice && (
              <span
                className={`bhai-collapsed-voice-row${
                  voiceSessionOn ? " bhai-collapsed-voice-row--on" : ""
                }`}
                onClick={(e) => e.stopPropagation()}
              >
                {!voiceSessionOn && (
                  <span className="bhai-collapsed-voice-invite-pre">
                    I can talk too —
                  </span>
                )}
                <button
                  type="button"
                  className={`bhai-voice-talk${
                    voiceSessionOn ? " bhai-voice-talk--session" : ""
                  }${
                    voiceSessionOn && voiceTalkState === "listening"
                      ? " bhai-voice-talk--listening"
                      : ""
                  }`}
                  aria-label={
                    voiceSessionOn
                      ? "AI Voice is on. Tap to turn off"
                      : "Turn AI Voice on"
                  }
                  aria-pressed={voiceSessionOn}
                  title={
                    voiceSessionOn
                      ? "AI Voice is on — tap to turn off"
                      : "Turn AI Voice on"
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleAiVoice();
                  }}
                >
                  {voiceSessionOn ? "AI Voice On" : "AI Voice"}
                </button>
                {!voiceSessionOn && (
                  <span className="bhai-collapsed-voice-invite-post">
                    let’s chat
                  </span>
                )}
              </span>
            )}
            {/* Bar mic — icon only; the invite copy that advertised the AI's
                own voice is retired with the monologues. */}
            {!barDismissed && !classicVoice && voiceAssist && (
              <span
                className={`bhai-collapsed-voice-row${
                  voiceSessionOn ? " bhai-collapsed-voice-row--on" : ""
                }`}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className={`bhai-voice-talk bhai-voice-mic${
                    voiceSessionOn ? " bhai-voice-talk--session" : ""
                  }${
                    voiceSessionOn && voiceTalkState === "listening"
                      ? " bhai-voice-talk--listening"
                      : ""
                  }`}
                  aria-label={
                    voiceSessionOn
                      ? "Voice is on. Tap to turn off"
                      : "Ask by voice"
                  }
                  aria-pressed={voiceSessionOn}
                  title={
                    voiceSessionOn
                      ? "Voice is on — tap to turn off"
                      : "Ask by voice — tap, then speak"
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleAiVoice();
                  }}
                >
                  <MicIcon />
                </button>
              </span>
            )}
            {!barDismissed && isGlassCapsuleSkin(surfaceSkin) && (
              <span className="bhai-cglass-anon" aria-label="Anonymous session">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="5" y="10" width="14" height="10" rx="2.5" stroke="currentColor" strokeWidth="2" />
                  <path d="M8 10V7a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="2" />
                </svg>
                Anonymous
              </span>
            )}
            {!barDismissed && (
              <button
                type="button"
                className={`bhai-collapsed-expand${
                  sectionBarView
                    ? " bhai-collapsed-expand--cta"
                    : ""
                }`}
                aria-label={
                  sectionBarView && surfaceSkin != null
                    ? livingBarCtaLabel(
                        surfaceSkin,
                        sectionBarView.ctaLabel || "Expand chat",
                      )
                    : sectionBarView
                      ? sectionBarView.ctaLabel || "Expand chat"
                      : "Expand chat"
                }
                onClick={(e) => {
                  e.stopPropagation();
                  if (
                    sectionBarView &&
                            sectionBarView.openWithQuestions?.length
                  ) {
                    _openWithSectionQuestions?.(sectionBarView.openWithQuestions);
                  } else {
                    expandBar(e);
                  }
                }}
              >
                {sectionBarView ? (
                  <>
                    <span className="bhai-collapsed-cta-label">
                      {surfaceSkin != null
                        ? livingBarCtaLabel(surfaceSkin, sectionBarView.ctaLabel)
                        : sectionBarView.ctaLabel || "Answer me now"}
                    </span>
                    {/* Phones (≤640px) paint this instead of the label — the
                        text button is what forced the bar onto two rows
                        (2026-10-09). The stylesheet decides which one shows;
                        the handler and aria-label above are shared. */}
                    <span className="bhai-collapsed-cta-icon" aria-hidden="true">
                      <SendIcon />
                    </span>
                  </>
                ) : (
                  <SendIcon />
                )}
              </button>
            )}
            {!barDismissed && (!inline || inlineCloseAllowed) && (
              <button
                type="button"
                className="bhai-collapsed-close"
                aria-label={inline ? "Close" : "Dismiss chat bar"}
                onClick={inline ? dismissInline : dismissBar}
              >
                ×
              </button>
            )}
          </div>
        </div>
        </>
  );

  // Side-panel is always present: its collapsed bar is the sole persistent
  // launcher, so it must render from first paint (fixes the "first click does
  // nothing" — the tree + open handler are live immediately) and must stay put if
  // a later chatEnabled=false arrives. Only classic/inline modes hide on !visible.
  if (!visible && display !== "side_panel") return <style>{styles}</style>;

  if (inline) {
    // #592: what the blessed div paints. Fail-closed blank until the mode is
    // known (mirrors #589); "expand" shows the SAME skinned surface floating
    // uses, in-flow, honoring the scope/design bar verdicts; the chat renders
    // once open. inlineRenderPhase is pure — pinned by inline-mode tests.
    const phase = inlineRenderPhase(
      inlineMode,
      open,
      // Deliberately WITHOUT lowerBarModeAllows — see its declaration. The
      // container is what `unless_inline` defers to; it cannot be the thing
      // that mode switches off.
      (bottomBar?.enabled ?? true) && !barSuppressed,
      // Config landed (look known) AND page-voice resolved (row layout
      // final) — the first paint is the final look by construction.
      surfaceReady,
    );
    if (phase === "blank") return <style>{styles}</style>;
    // Closed by the visitor via the tenant-enabled X: nothing in the container
    // for the rest of the session. Collapsed phase only — a chat the visitor
    // opened is never yanked away.
    if (phase === "collapsed" && inlineDismissed) return <style>{styles}</style>;
    if (phase === "collapsed") {
      return (
        <>
          <style>{styles}</style>
          {agentMode ? agentSurface() : nudgesMode ? nudgesSurface() : collapsedSurface()}
        </>
      );
    }
    // #592: the expanded inline window carries the SKIN's look — stamped as
    // classes so chat.css themes it per family (mod + palette helpers only;
    // the family test bans member equality here).
    const skwPalette = glassCapsulePalette(surfaceSkin);
    // Agent mode wears NO skin. It is not a bar, so the living-bar skin the
    // tenant picked for their lower bar has nothing to say about it — and
    // letting it through meant a navy skin painted a navy panel on a white
    // page, on every page, whatever the site looked like. Its panel is the
    // plain themed one, which is what --bhjs-surface / the host's own CSS
    // then get to decide.
    // Questions only (nudges mode) is the same: its pills already left the
    // bar's skin for the AI look, so its conversation does too.
    const skinnedPanel = !agentMode && !nudgesMode;
    const skwClass = [
      "chat-window--inline",
      skinnedPanel && surfaceSkin != null && !isAiStyledSkin(surfaceSkin)
        ? `bhai-skwin--${livingBarSkinMod(surfaceSkin)}`
        : "",
      skinnedPanel && skwPalette ? `bhai-pal--${skwPalette}` : "",
    ]
      .filter(Boolean)
      .join(" ");
    return (
      <>
        <style>{styles}</style>
        <ChatWindow
          messages={messages}
          thinking={thinking}
          onSend={handleSend}
          onSubmitLead={handleSubmitLead}
          onClickCta={handleCtaClick}
          onClickProduct={handleProductClick}
          onClickChip={handleChipClick}
          onClickAnswerChip={handleAnswerChipClick}
          pagePills={pagePills}
          onClickPagePill={handlePagePillClick}
          // Expand AND agent close back to their collapsed surface. Agent
          // spent 0.9.0-0.9.2 with no X, justified by "an engaged session
          // reopens on the next load anyway" — true under the auto-resume
          // that the ambush reversal removed, after which a visitor was
          // simply TRAPPED in an open rail chat (Lynda's site, 2026-09-01).
          // Closing now has a real resting state: the bare field with the
          // continue chip, transcript intact behind it. Only mode "open"
          // keeps no close — its chat is the configured state; closing
          // would leave a blank container.
          onClose={inlineMode === "expand" || inlineMode === "agent" || inlineMode === "nudges" ? minimize : undefined}
          initialDraft={inlineDraft}
          autoFocusComposer={inlineMode === "expand"}
          windowClassName={skwClass}
          primaryCta={primaryCta}
          voiceAssist={voiceAssist}
          voiceSessionOn={voiceSessionOn}
          voiceTalkState={voiceTalkState}
          voiceHint={voiceHint}
          voiceDraft={voiceDraft}
          onEditVoiceDraft={() => setVoiceDraft(null)}
          onToggleVoice={toggleAiVoice}
          onSpeak={() => {
            stopAgentSpeech();
            startClickToTalkRef.current({ skipBridge: true });
          }}
          onStopListening={() => voiceTalkRef.current?.stop()}
          classicVoice={classicVoice}
        />
      </>
    );
  }

  // The per-page banner div (when present) makes `banner` non-null. While the
  // banner is the launcher it "blocks the circle" (the addendum's banner-first
  // rule); the circle returns as the close control once chat is open, or as a
  // fallback launcher if the visitor dismisses the banner.
  const sidePanel = display === "side_panel";

  // The collapsed bar is the ONLY persistent launcher in side-panel mode, so
  // "the widget is gone" is almost always one of these six terms being false.
  // Inferring which from the outside is guesswork — this reports it. Behind
  // bh_debug, so customer consoles stay quiet.
  useEffect(() => {
    const terms = {
      sidePanel,
      minimized,
      notOpen: !open,
      bottomBarEnabled: bottomBar?.enabled ?? true,
      notSuppressed: !barSuppressed,
      lowerBarModeAllows,
      stickyBarVisible,
    };
    const blocking = Object.entries(terms)
      .filter(([, v]) => !v)
      .map(([k]) => k);
    // Lead with the VERDICT and the blocking term as a string: an object dump
    // gets truncated by the console at ~5 keys, which is exactly how the
    // deciding term stayed hidden while this was being debugged.
    debugLog(
      `[BlueHat] bar gate: ${blocking.length === 0 ? "RENDERS" : "HIDDEN — blocked by " + blocking.join(", ")}`,
      terms,
    );
  }, [sidePanel, minimized, open, bottomBar, barSuppressed, lowerBarModeAllows, stickyBarVisible]);

  const bannerActive = !!banner;
  const showBanner = bannerActive && !open && !bannerDismissed && !sidePanel;
  // Classic bubble: one circle (launcher when closed, close when open).
  // Side panel: closing minimizes to a persistent "I'm still here" pill (NOT a
  // chatbot blob); the close control while open is a small circle that drops
  // back to that pill. Either entry point re-opens the same chat.
  const showCircle = !sidePanel && (!bannerActive || open || bannerDismissed);

  return (
    <>
      <style>{styles}</style>
      {showBanner && banner && (
        <Banner config={banner} onOpen={openChat} onDismiss={dismissBanner} />
      )}
      {/* Center stage: the site dims and blurs behind the open panel. Clicking
          the scrim collapses back to the bar — same as the in-panel close. */}
      {sidePanel && open && <div className="chat-scrim" onClick={minimize} aria-hidden="true" />}
      {open && (
        <ChatWindow
          messages={messages}
          thinking={thinking}
          onSend={handleSend}
          onSubmitLead={handleSubmitLead}
          onClickChip={handleChipClick}
          onClickAnswerChip={handleAnswerChipClick}
          pagePills={pagePills}
          onClickPagePill={handlePagePillClick}
          onClose={sidePanel ? minimize : undefined}
          onClickCta={handleCtaClick}
          onClickProduct={handleProductClick}
          primaryCta={primaryCta}
          voiceAssist={voiceAssist}
          voiceSessionOn={voiceSessionOn}
          voiceTalkState={voiceTalkState}
          voiceHint={voiceHint}
          voiceDraft={voiceDraft}
          onEditVoiceDraft={() => setVoiceDraft(null)}
          onToggleVoice={toggleAiVoice}
          onSpeak={() => {
            stopAgentSpeech();
            startClickToTalkRef.current({ skipBridge: true });
          }}
          onStopListening={() => voiceTalkRef.current?.stop()}
          classicVoice={classicVoice}
        />
      )}
      {/* Fleet-wide AI Voice fixture: fixed top-right when panel is CLOSED.
          While the chat window is open, controls live in the panel header
          (right cluster) so visitors can type without losing stop/listening. */}
      {classicVoice && !open && (
        <div
          className={[
            "bhai-voice-session-fab",
            sectionBarView &&
            livingBarSkin != null &&
            isSideLivingBarSkin(livingBarSkin)
              ? "bhai-voice-session-fab--above-rail"
              : "",
            voiceSessionOn ? "bhai-voice-session-fab--on" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          role="region"
          aria-label="AI Voice"
        >
          {voiceHint && voiceSessionOn && (
            <div
              className={`bhai-voice-hint bhai-voice-hint--fab${
                voiceSessionOn ? "" : " bhai-voice-hint--fab-idle"
              }`}
              role="status"
            >
              {voiceHint}
            </div>
          )}
          <div className="bhai-voice-fab-row">
          {voiceSessionOn && (
            <button
              type="button"
              className={`bhai-voice-talk bhai-voice-speak${
                voiceTalkState === "listening" ? " bhai-voice-speak--listening" : ""
              }`}
              aria-label={
                voiceTalkState === "listening" ? "Stop listening" : "Speak — start listening"
              }
              title={voiceTalkState === "listening" ? "Stop listening" : "Speak"}
              onClick={(e) => {
                e.stopPropagation();
                if (voiceTalkState === "listening") {
                  // Push-to-talk finish: commit anything already said.
                  voiceTalkRef.current?.stop();
                } else {
                  stopAgentSpeech();
                  startClickToTalkRef.current({ skipBridge: true });
                }
              }}
            >
              {voiceTalkState === "listening" ? "Stop" : "Speak"}
            </button>
          )}
          <button
            type="button"
            className={`bhai-voice-talk${
              voiceSessionOn ? " bhai-voice-talk--session" : ""
            }${
              voiceSessionOn && voiceTalkState === "listening"
                ? " bhai-voice-talk--listening"
                : ""
            }`}
            aria-label={
              voiceSessionOn
                ? "AI Voice is on. Tap to turn off"
                : "Turn AI Voice on"
            }
            aria-pressed={voiceSessionOn}
            title={
              voiceSessionOn
                ? "AI Voice is on — tap to turn off"
                : "Turn AI Voice on"
            }
            onClick={(e) => {
              e.stopPropagation();
              toggleAiVoice();
            }}
          >
            {voiceSessionOn ? "AI Voice On" : "AI Voice"}
          </button>
          </div>
        </div>
      )}
      {/* Living bar — hidden under the top hero/line; scroll-dock shows it
          once that launcher leaves the viewport (and hides again at top).
          Nav-only sites show immediately. X dismisses for the session. */}
      {sidePanel &&
        minimized &&
        !open &&
        (bottomBar?.enabled ?? true) &&
        !barSuppressed &&
        lowerBarModeAllows &&
        stickyBarVisible &&
        surfaceReady &&
        // Questions only: the pills alone. Once the visitor closes them, the
        // full bar's own dismissed form (the small restore pill) takes over,
        // so the way back is the one every bar already has.
        (barNudgesOnly && !barDismissed ? nudgesSurface() : collapsedSurface())}
      {showCircle && (
        <button
          className="bubble"
          aria-label={open ? "Close chat" : "Open chat"}
          data-y="bottom"
          data-x="right"
          onClick={toggle}
        >
          {open ? <CloseIcon /> : <ChatIcon />}
        </button>
      )}
    </>
  );
}

export function createBubble(
  callbacks: BubbleCallbacks,
  options: MountOptions = { mode: "floating" },
  initialBanner: BannerConfig | null = null,
  initialVisible = false,
): BubbleHandle {
  const host = document.createElement("div");
  // A stable hook for the site's own CSS (`.bhai-widget::part(panel)` etc. —
  // the list is HOST_CSS_* in widget-design/host-css.ts).
  host.className = "bhai-widget";
  host.dataset.mode = options.mode;
  // Bubble is ripped out globally — every widget is side-panel, from creation.
  host.dataset.display = "side_panel";
  _hostEl = host;
  const shadow = host.attachShadow({ mode: "open" });

  const mountPoint = document.createElement("div");
  mountPoint.className = "mount";
  shadow.appendChild(mountPoint);

  if (options.mode === "inline") {
    options.target.appendChild(host);
  } else {
    document.body.appendChild(host);
  }

  const root = createRoot(mountPoint);
  root.render(
    <Bubble
      callbacks={callbacks}
      mode={options.mode}
      initialBanner={initialBanner}
      initialVisible={initialVisible}
      initialInlineMode={options.mode === "inline" ? options.inlineMode ?? null : null}
      initialLivingBarSkin={options.mode === "inline" ? options.initialLivingBarSkin ?? null : null}
      initialConfigLanded={options.mode === "inline" ? options.initialConfigLanded ?? false : false}
      initialInlineClose={options.mode === "inline" ? options.initialInlineClose ?? false : false}
      agentChipsAllowed={options.mode === "inline" ? options.chipsAllowed ?? true : true}
    />,
  );

  return {
    host,
    shadow,
    setMessages: (msgs) => _setMessages?.(msgs),
    addMessage: (msg) => _addMessage?.(msg),
    updateMessage: (msg) => _updateMessage?.(msg),
    setThinking: (t) => _setThinking?.(t),
    addCta: (url, label, slug, openInNewTab) => _addCta?.(url, label, slug, openInNewTab),
    addForm: (form) => _addForm?.(form),
    addChip: (label, sendText) => _addChip?.(label, sendText),
    addProducts: (products) => _addProducts?.(products),
    // Returns whether the chips actually attached (false = discarded: a newer
    // visitor message is in flight, or every option was already asked). The
    // snippet keys the forced open + answer_chips_shown on this.
    addAnswerChips: (chips, turnId, kind) =>
      _addAnswerChips?.(chips, turnId, kind) ?? false,
    open: () => _openChat?.(),
    openWithText: (text?: string) => _openWithText?.(text),
    openWithSectionQuestions: (questions: string[]) => _openWithSectionQuestions?.(questions),
    setBarLines: (lines) => _setBarLines?.(lines),
    setPageQuestionPool: (questions) => _setPageQuestionPool?.(questions),
    setRankedNextAsks: (questions) => _setRankedNextAsks?.(questions),
    setPageVoiceMeta: (meta) => _setPageVoiceMeta?.(meta),
    setRailContinuityLead: (lead) => _setRailContinuityLead?.(lead),
    setDockContinuityLead: (lead) => _setRailContinuityLead?.(lead),
    setSectionBarView: (view) => {
      if (_setSectionBarView) {
        _pendingSectionBarView = undefined;
        _setSectionBarView(view);
      } else {
        // React effect not bound yet — keep last write for flush on bind.
        _pendingSectionBarView = view;
      }
    },
    setLivingBarSkin: (skin) => {
      if (_setLivingBarSkin) {
        _pendingLivingBarSkin = undefined;
        _setLivingBarSkin(skin);
      } else {
        _pendingLivingBarSkin = skin;
      }
    },
    setInlineMode: (mode) => {
      if (_setInlineMode) {
        _pendingInlineMode = undefined;
        _setInlineMode(mode);
      } else {
        // Pre-bind: hold it — losing this write strands an attribute-less
        // inline mount in the fail-closed blank (#592).
        _pendingInlineMode = mode;
      }
    },
    setInlineCloseAllowed: (allowed) => {
      if (_setInlineCloseAllowed) {
        _pendingInlineCloseAllowed = undefined;
        _setInlineCloseAllowed(allowed);
      } else {
        _pendingInlineCloseAllowed = allowed;
      }
    },
    setInlineConfigLanded: () => {
      if (_setInlineCfgLanded) {
        _pendingInlineCfgLanded = false;
        _setInlineCfgLanded();
      } else {
        // Pre-bind: losing this write strands the collapsed surface blank
        // until the 2s forced fallback — buffer it like setInlineMode.
        _pendingInlineCfgLanded = true;
      }
    },
    setSectionDataLanded: () => {
      if (_setSectionDataLanded) {
        _pendingSectionDataLanded = false;
        _setSectionDataLanded();
      } else {
        _pendingSectionDataLanded = true;
      }
    },
    setVoiceMode: (mode) => {
      if (_setVoiceMode) {
        _pendingVoiceMode = undefined;
        _setVoiceMode(mode);
      } else {
        _pendingVoiceMode = mode;
      }
    },
    toggleAiVoice: () => _toggleAiVoice?.(),
    pauseVoiceForNavigation: () => _pauseVoiceForNavigation?.(),
    announcePageForVoice: (view) => _announcePageForVoice?.(view),
    playNavVoice: (mp3Url) => _playNavVoice?.(mp3Url),
    playHostVoice: (intentId, mp3Url) => _playHostVoice?.(intentId, mp3Url),
    setStickyBarVisible: (v) => {
      if (_setStickyBarVisible) {
        _pendingStickyBarVisible = undefined;
        _setStickyBarVisible(v);
      } else {
        // Pre-bind: hold it. See _pendingStickyBarVisible — losing this call
        // leaves the widget with no launcher at all.
        _pendingStickyBarVisible = v;
      }
    },
    setVisible: (v) => _setVisible?.(v),
    setBanner: (b) => _setBanner?.(b),
    setDisplay: (_display) => {
      // No bubble anywhere — force side_panel regardless of what config sends.
      host.dataset.display = "side_panel";
      _setDisplay?.("side_panel");
    },
    refreshTopic: () => _refreshTopic?.(),
    setBottomBar: (b) => _setBottomBar?.(b),
    setBarSuppressed: (v) => {
      if (_setBarSuppressed) {
        _pendingBarSuppressed = undefined;
        _setBarSuppressed(v);
      } else {
        _pendingBarSuppressed = v;
      }
    },
    setLowerBarModeAllows: (v: boolean) => {
      if (_setLowerBarModeAllows) {
        _pendingLowerBarModeAllows = undefined;
        _setLowerBarModeAllows(v);
      } else {
        _pendingLowerBarModeAllows = v;
      }
    },
    setBarNudgesOnly: (v: boolean) => {
      if (_setBarNudgesOnly) {
        _pendingBarNudgesOnly = undefined;
        _setBarNudgesOnly(v);
      } else {
        _pendingBarNudgesOnly = v;
      }
    },
    setPrimaryCta: (c) => _setPrimaryCta?.(c),
    unmount: () => {
      root.unmount();
      host.remove();
      if (_hostEl === host) _hostEl = null;
      // Effect cleanups in the React tree already null out the module-level
      // refs; this is a belt-and-suspenders in case render never committed.
      _setMessages = null;
      _addMessage = null;
      _updateMessage = null;
      _setThinking = null;
      _openChat = null;
      _openWithText = null;
      _openWithSectionQuestions = null;
      _setBarLines = null;
      _setPageQuestionPool = null;
      _setRankedNextAsks = null;
      _setPageVoiceMeta = null;
      _setRailContinuityLead = null;
      _setSectionBarView = null;
      _setLivingBarSkin = null;
      _setInlineMode = null;
      _setStickyBarVisible = null;
      _setVisible = null;
      _setBanner = null;
    },
  };
}
